import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AlertCircle, ArrowRight, CheckCircle, Clock, ArrowRightLeft, Settings, Activity, Zap, ExternalLink, RefreshCw, Shield, HelpCircle } from 'lucide-react';
import bridgeService from '../utils/bridgeService';
import config from '../config/bridgeConfig';
import { getApiUrl, getNetworkConfig } from '../config/mainnetConfig';
import { CONTRACT_ADDRESSES } from '../config/contracts';
import { useWagmiWallet } from '../hooks/useWagmiWallet';
import { useChainId, useWalletClient, usePublicClient, useWriteContract } from 'wagmi';
import { switchChain } from '@wagmi/core';
import { config as wagmiConfig } from '../config/wagmi';
import { createPublicClient, http } from 'viem';
import { ethers } from 'ethers';
import AdminPanel from './AdminPanel';
import WagmiWalletStatus from './WagmiWalletStatus';
import { checkBridgeAvailabilityWithDirection } from '../utils/reserveChecker';
import { trackBridgeTransaction, trackError, trackPageView } from '../config/posthog';

const ZKBridgeApp = () => {
  // Wagmi wallet hook
  const {
    isConnected,
    account: wagmiAccount,
    currentNetwork,
    isCorrectNetwork,
    disconnect,
    isAutoConnecting,
    switchToNetwork
  } = useWagmiWallet();

  
  // Debug environment detection - moved to useEffect to prevent infinite loops
  useEffect(() => {
    console.log('🌍 ZKBridgeApp: Environment detected: mainnet');
    console.log('🌍 ZKBridgeApp: API URL:', getApiUrl());
    console.log('🌍 ZKBridgeApp: Network Config:', getNetworkConfig());
    
    // Track page view
    trackPageView('bridge_home', {
      environment: process.env.REACT_APP_ENVIRONMENT || 'development'
    });
  }, []); // Only run once on mount
  // Get chainId, provider, and signer from Wagmi
  const chainId = useChainId();
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const { writeContractAsync, error: writeContractError, isPending: isWriteContractPending } = useWriteContract();
  
  // Create ethers provider and signer from Wagmi
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  
  useEffect(() => {
    if (publicClient) {
      try {
        const newProvider = new ethers.BrowserProvider(publicClient);
        setProvider(newProvider);
      } catch (error) {
        console.error('Failed to create provider:', error);
        setProvider(null);
      }
    } else {
      setProvider(null);
    }
  }, [publicClient]);
  
  useEffect(() => {
    if (walletClient) {
      try {
        const newProvider = new ethers.BrowserProvider(walletClient);
        const newSigner = newProvider.getSigner();
        setSigner(newSigner);
      } catch (error) {
        console.error('Failed to create signer:', error);
        setSigner(null);
      }
    } else {
      setSigner(null);
    }
  }, [walletClient]);
  const [sourceChain, setSourceChain] = useState('bscMainnet');
  const [destChain, setDestChain] = useState('xphereMainnet');
  const [token, setToken] = useState('');
  const [amount, setAmount] = useState('');
  const [recipient, setRecipient] = useState('');
  const [bridgeRequests, setBridgeRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    // Restore active tab from localStorage, default to 'bridge'
    const savedTab = localStorage.getItem('activeTab');
    return savedTab || 'bridge';
  });
  const [success, setSuccess] = useState(null);
  const [tokenBalances, setTokenBalances] = useState({});
  const [amountError, setAmountError] = useState('');
  
  // Bridge status tracking state
  const [bridgeStatuses, setBridgeStatuses] = useState({});
  const [allBridgeHistory, setAllBridgeHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  
  // Analytics filter state
  const [analyticsFilter, setAnalyticsFilter] = useState({});
  
  // Pagination state for analytics
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 50,
    totalCount: 0
  });
  
  // Bridge configuration state
  const [bridgeConfig, setBridgeConfig] = useState({
    fee: '0.5%',
    minAmount: '0.001',
    maxAmount: '100',
    dailyLimit: '1000',
    dailyUsed: '0',
    remainingDaily: '0',
    processingTime: '~30-60 seconds'
  });

  // Admin status state
  const [isAdmin, setIsAdmin] = useState(false);
  const [isNetworkSwitching, setIsNetworkSwitching] = useState(false);
  
  // Smart refresh preference
  const [smartRefreshEnabled, setSmartRefreshEnabled] = useState(() => {
    const saved = localStorage.getItem('smartRefreshEnabled');
    return saved !== null ? JSON.parse(saved) : true; // Default to true
  });
  
  // Analytics metrics state
  const [analyticsMetrics, setAnalyticsMetrics] = useState({
    totalUSDTLocked: '0',
    totalUSDXLocked: '0',
    rwaNovaBscLocked: '0',
    rwaNovaXphereLocked: '0',
    totalUSDTReleased: '0',
    totalUSDXMinted: '0',
    totalFeesCollected: '0',
    xphereBridgeFees: '0',
    bscBridgeFees: '0',
    rwaNovaBscFees: '0',
    rwaNovaXphereFees: '0',
    totalUSDTReserved: '0',
    totalUSDXReserved: '0',
    rwaNovaBscReserved: '0',
    rwaNovaXphereReserved: '0',
    isLoading: false
  });

  // Actual analytics counts state (for accurate dashboard metrics)
  const [actualCounts, setActualCounts] = useState({
    total: 0,
    completed: 0,
    active: 0,
    totalVolumeUSD: 0,
    successRate: 0,
    tokenStats: {
      usdt: { total: 0, completed: 0, volume: 0, transactions: [] },
      usdx: { total: 0, completed: 0, volume: 0, transactions: [] },
      rwaNova: { total: 0, completed: 0, volume: 0, transactions: [] }
    }
  });

  // Confirmation dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmCheckboxChecked, setConfirmCheckboxChecked] = useState(false);
  
  // Toast notification state for active bridge requests
  const [activeToasts, setActiveToasts] = useState([]);
  

  // Bridge availability state
  const [bridgeAvailability, setBridgeAvailability] = useState({
    isChecking: false,
    isAvailable: true,
    reason: '',
    details: null,
    lastChecked: null
  });

  // Wagmi hooks for chain switching - now handled through useWagmiWallet hook



  // Use centralized contract addresses
  const CONTRACT_ADDRESSES_MAPPED = useMemo(() => {
    return {
      '56': { // BSC Mainnet
        source: CONTRACT_ADDRESSES[56].sourceBridge,
        destination: CONTRACT_ADDRESSES[56].destBridge,
        usdt: CONTRACT_ADDRESSES[56].usdt,
        rwanova: CONTRACT_ADDRESSES[56].rwaNova
      },
      '20250217': { // Xphere Mainnet
        source: CONTRACT_ADDRESSES[20250217].sourceBridge,
        destination: CONTRACT_ADDRESSES[20250217].destBridge,
        usdx: CONTRACT_ADDRESSES[20250217].usdx,
        rwanova: CONTRACT_ADDRESSES[20250217].rwaNova
      }
    };
  }, []);

  // Contract ABIs matching your deployed ENHANCED contracts
  const SOURCE_ABI = useMemo(() => [
    // Core bridge functions
    {
      "inputs": [
        {"internalType": "address", "name": "token", "type": "address"},
        {"internalType": "uint256", "name": "amount", "type": "uint256"},
        {"internalType": "uint256", "name": "destinationChainId", "type": "uint256"},
        {"internalType": "address", "name": "destinationAddress", "type": "address"}
      ],
      "name": "initiateBridge",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "uint256", "name": "requestId", "type": "uint256"}],
      "name": "getBridgeRequest",
      "outputs": [{
        "components": [
          {"internalType": "address", "name": "user", "type": "address"},
          {"internalType": "address", "name": "token", "type": "address"},
          {"internalType": "uint256", "name": "amount", "type": "uint256"},
          {"internalType": "uint256", "name": "destinationChainId", "type": "uint256"},
          {"internalType": "address", "name": "destinationAddress", "type": "address"},
          {"internalType": "uint256", "name": "timestamp", "type": "uint256"},
          {"internalType": "bool", "name": "processed", "type": "bool"},
          {"internalType": "bytes32", "name": "txHash", "type": "bytes32"}
        ],
        "internalType": "struct BridgeRequest",
        "name": "",
        "type": "tuple"
      }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "requestCounter",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    
    // Token management
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "getTokenInfo",
      "outputs": [{
        "components": [
          {"internalType": "bool", "name": "supported", "type": "bool"},
          {"internalType": "uint8", "name": "decimals", "type": "uint8"},
          {"internalType": "uint256", "name": "minAmount", "type": "uint256"},
          {"internalType": "uint256", "name": "maxAmount", "type": "uint256"},
          {"internalType": "uint256", "name": "dailyLimit", "type": "uint256"},
          {"internalType": "uint256", "name": "dailyUsage", "type": "uint256"},
          {"internalType": "uint256", "name": "lastResetDay", "type": "uint256"}
        ],
        "internalType": "struct TokenInfo",
        "name": "",
        "type": "tuple"
      }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "bridgeFee",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "defaultMinAmount",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "defaultMaxAmount",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "address", "name": "token", "type": "address"},
        {"internalType": "uint8", "name": "decimals", "type": "uint8"}
      ],
      "name": "addSupportedToken",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "removeSupportedToken",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    
    // Chain management
    {
      "inputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "name": "supportedChains",
      "outputs": [{"internalType": "bool", "name": "", "type": "bool"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "uint256", "name": "chainId", "type": "uint256"}],
      "name": "addSupportedChain",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "uint256", "name": "chainId", "type": "uint256"}],
      "name": "removeSupportedChain",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    
    // Enhanced contract functions
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "getRemainingDailyLimit",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "getFormattedAmounts",
      "outputs": [
        {"internalType": "string", "name": "minAmountFormatted", "type": "string"},
        {"internalType": "string", "name": "maxAmountFormatted", "type": "string"},
        {"internalType": "string", "name": "dailyLimitFormatted", "type": "string"},
        {"internalType": "string", "name": "dailyUsageFormatted", "type": "string"}
      ],
      "stateMutability": "view",
      "type": "function"
    },
    
    // Fee and limits management
    {
      "inputs": [],
      "name": "bridgeFee",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "uint256", "name": "newFee", "type": "uint256"}],
      "name": "setBridgeFee",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "address", "name": "token", "type": "address"},
        {"internalType": "uint256", "name": "minAmount", "type": "uint256"},
        {"internalType": "uint256", "name": "maxAmount", "type": "uint256"},
        {"internalType": "uint256", "name": "dailyLimit", "type": "uint256"}
      ],
      "name": "setTokenLimits",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "uint256", "name": "minAmount", "type": "uint256"},
        {"internalType": "uint256", "name": "maxAmount", "type": "uint256"},
        {"internalType": "uint256", "name": "dailyLimit", "type": "uint256"}
      ],
      "name": "setDefaultLimits",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    
    // Admin functions
    {
      "inputs": [],
      "name": "owner",
      "outputs": [{"internalType": "address", "name": "", "type": "address"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "address", "name": "relayer", "type": "address"},
        {"internalType": "bool", "name": "authorized", "type": "bool"}
      ],
      "name": "setRelayerAuthorization",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "newCollector", "type": "address"}],
      "name": "setFeeCollector",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "pause",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "unpause",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    
    // Events
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "uint256", "name": "requestId", "type": "uint256"},
        {"indexed": true, "internalType": "address", "name": "user", "type": "address"},
        {"indexed": true, "internalType": "address", "name": "token", "type": "address"},
        {"indexed": false, "internalType": "uint256", "name": "amount", "type": "uint256"},
        {"indexed": false, "internalType": "uint256", "name": "destinationChainId", "type": "uint256"},
        {"indexed": false, "internalType": "address", "name": "destinationAddress", "type": "address"},
        {"indexed": false, "internalType": "bytes32", "name": "commitment", "type": "bytes32"}
      ],
      "name": "BridgeRequest",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "bytes32", "name": "newRoot", "type": "bytes32"},
        {"indexed": false, "internalType": "uint256", "name": "blockNumber", "type": "uint256"}
      ],
      "name": "MerkleRootUpdated",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": false, "internalType": "uint256", "name": "minAmount", "type": "uint256"},
        {"indexed": false, "internalType": "uint256", "name": "maxAmount", "type": "uint256"},
        {"indexed": false, "internalType": "uint256", "name": "dailyLimit", "type": "uint256"}
      ],
      "name": "BridgeLimitsUpdated",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "token", "type": "address"},
        {"indexed": false, "internalType": "uint8", "name": "decimals", "type": "uint8"}
      ],
      "name": "TokenDecimalUpdated",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "token", "type": "address"},
        {"indexed": false, "internalType": "uint8", "name": "decimals", "type": "uint8"}
      ],
      "name": "SupportedTokenAdded",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "token", "type": "address"}
      ],
      "name": "SupportedTokenRemoved",
      "type": "event"
    }
  ], []);

  // Destination contract ABI for enhanced contracts
  const DESTINATION_ABI = useMemo(() => [
    // Core bridge functions
    {
      "inputs": [
        {
          "components": [
            {"internalType": "uint256[2]", "name": "pA", "type": "uint256[2]"},
            {"internalType": "uint256[2][2]", "name": "pB", "type": "uint256[2][2]"},
            {"internalType": "uint256[2]", "name": "pC", "type": "uint256[2]"},
            {"internalType": "uint256[1]", "name": "pubSignals", "type": "uint256[1]"}
          ],
          "internalType": "struct Proof",
          "name": "proof",
          "type": "tuple"
        },
        {
          "components": [
            {"internalType": "bytes32", "name": "nullifierHash", "type": "bytes32"},
            {"internalType": "address", "name": "recipient", "type": "address"},
            {"internalType": "uint256", "name": "amount", "type": "uint256"},
            {"internalType": "uint256", "name": "sourceChainId", "type": "uint256"},
            {"internalType": "address", "name": "token", "type": "address"}
          ],
          "internalType": "struct BridgeData",
          "name": "bridgeData",
          "type": "tuple"
        }
      ],
      "name": "processBridgeTransaction",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    
    // Token management
    {
      "inputs": [{"internalType": "address", "name": "", "type": "address"}],
      "name": "tokenMappings",
      "outputs": [
        {"internalType": "address", "name": "destToken", "type": "address"},
        {"internalType": "uint8", "name": "decimals", "type": "uint8"},
        {"internalType": "bool", "name": "active", "type": "bool"}
      ],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "", "type": "address"}],
      "name": "mintableTokens",
      "outputs": [{"internalType": "bool", "name": "", "type": "bool"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "", "type": "address"}],
      "name": "tokenReserves",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    
    // Chain support
    {
      "inputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "name": "supportedSourceChains",
      "outputs": [{"internalType": "bool", "name": "", "type": "bool"}],
      "stateMutability": "view",
      "type": "function"
    },
    
    // Rate limiting
    {
      "inputs": [
        {"internalType": "address", "name": "", "type": "address"},
        {"internalType": "uint256", "name": "", "type": "uint256"}
      ],
      "name": "dailyMinted",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "", "type": "address"}],
      "name": "dailyMintLimit",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "defaultDailyMintLimit",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    
    // View functions
    {
      "inputs": [{"internalType": "address", "name": "sourceToken", "type": "address"}],
      "name": "getTokenMapping",
      "outputs": [{
        "components": [
          {"internalType": "address", "name": "destToken", "type": "address"},
          {"internalType": "uint8", "name": "decimals", "type": "uint8"},
          {"internalType": "bool", "name": "active", "type": "bool"},
          {"internalType": "bool", "name": "isCrossChain", "type": "bool"}
        ],
        "internalType": "struct TokenMapping",
        "name": "",
        "type": "tuple"
      }],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "getTokenReserve",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "address", "name": "token", "type": "address"},
        {"internalType": "uint256", "name": "day", "type": "uint256"}
      ],
      "name": "getDailyMinted",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "getCurrentDailyMinted",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "getRemainingDailyMintLimit",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "isMintable",
      "outputs": [{"internalType": "bool", "name": "", "type": "bool"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [{"internalType": "address", "name": "token", "type": "address"}],
      "name": "getFormattedAmounts",
      "outputs": [
        {"internalType": "string", "name": "reserveFormatted", "type": "string"},
        {"internalType": "string", "name": "dailyLimitFormatted", "type": "string"},
        {"internalType": "string", "name": "dailyMintedFormatted", "type": "string"}
      ],
      "stateMutability": "view",
      "type": "function"
    },
    
    // Events
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "recipient", "type": "address"},
        {"indexed": true, "internalType": "address", "name": "token", "type": "address"},
        {"indexed": false, "internalType": "uint256", "name": "amount", "type": "uint256"},
        {"indexed": true, "internalType": "bytes32", "name": "nullifierHash", "type": "bytes32"},
        {"indexed": false, "internalType": "uint256", "name": "sourceChainId", "type": "uint256"}
      ],
      "name": "TokensMinted",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "recipient", "type": "address"},
        {"indexed": true, "internalType": "address", "name": "token", "type": "address"},
        {"indexed": false, "internalType": "uint256", "name": "amount", "type": "uint256"},
        {"indexed": true, "internalType": "bytes32", "name": "nullifierHash", "type": "bytes32"},
        {"indexed": false, "internalType": "uint256", "name": "sourceChainId", "type": "uint256"}
      ],
      "name": "TokensReleased",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "newVerifier", "type": "address"}
      ],
      "name": "VerifierUpdated",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "sourceToken", "type": "address"},
        {"indexed": false, "internalType": "address", "name": "destToken", "type": "address"},
        {"indexed": false, "internalType": "uint8", "name": "decimals", "type": "uint8"}
      ],
      "name": "TokenMappingUpdated",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "token", "type": "address"},
        {"indexed": false, "internalType": "uint256", "name": "amount", "type": "uint256"}
      ],
      "name": "TokenReservesAdded",
      "type": "event"
    },
    {
      "anonymous": false,
      "inputs": [
        {"indexed": true, "internalType": "address", "name": "token", "type": "address"},
        {"indexed": false, "internalType": "uint256", "name": "newLimit", "type": "uint256"}
      ],
      "name": "DailyMintLimitUpdated",
      "type": "event"
    }
  ], []);

  const ERC20_ABI = useMemo(() => [
    {
      "inputs": [{"internalType": "address", "name": "owner", "type": "address"}],
      "name": "balanceOf",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "address", "name": "spender", "type": "address"},
        {"internalType": "uint256", "name": "amount", "type": "uint256"}
      ],
      "name": "approve",
      "outputs": [{"internalType": "bool", "name": "", "type": "bool"}],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "address", "name": "owner", "type": "address"},
        {"internalType": "address", "name": "spender", "type": "address"}
      ],
      "name": "allowance",
      "outputs": [{"internalType": "uint256", "name": "", "type": "uint256"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "symbol",
      "outputs": [{"internalType": "string", "name": "", "type": "string"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "decimals",
      "outputs": [{"internalType": "uint8", "name": "", "type": "uint8"}],
      "stateMutability": "view",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "address", "name": "to", "type": "address"},
        {"internalType": "uint256", "name": "amount", "type": "uint256"}
      ],
      "name": "transfer",
      "outputs": [{"internalType": "bool", "name": "", "type": "bool"}],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [
        {"internalType": "address", "name": "to", "type": "address"},
        {"internalType": "uint256", "name": "amount", "type": "uint256"}
      ],
      "name": "mint",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    }
  ], []);


  // Mainnet-only chains - no environment switching needed

  // Fetch bridge configuration when chain changes (moved after function definition)

  // Mainnet-only chain configurations
  const supportedChains = useMemo(() => {
    const networkConfig = getNetworkConfig();
      return {
        'bscMainnet': { 
          name: 'BSC Mainnet', 
          id: 56, 
          color: 'bg-yellow-500',
          rpcUrl: networkConfig.bscMainnet?.rpcUrl || 'https://bsc-dataseed.binance.org',
          explorer: 'https://bscscan.com',
          currency: 'BNB'
        },
        'xphereMainnet': { 
          id: 20250217, 
          name: 'Xphere Mainnet',
          color: 'bg-red-500',
          explorer: 'https://xp.tamsa.io',
        rpcUrl: networkConfig.xphereMainnet?.rpcUrl || 'https://xp-mainnet.rpc.xplorium.xyz'
      }
    };
  }, []);

  // Get chain key by chain ID
  const getChainKeyById = useCallback((chainId) => {
    if (!chainId) return null;
    return Object.keys(supportedChains).find(key => BigInt(supportedChains[key].id) === BigInt(chainId));
  }, [supportedChains]);

  // Display message function for non-bridge notifications (like network errors)
  const showMessage = useCallback((type, title, message, duration = 8000) => {
    // Log the message for debugging - DO NOT add to activeToasts to prevent premature toasts
    console.log(`⚠️ ${type.toUpperCase()}: ${title} - ${message}`);
    
    // Update success message for user feedback without creating toasts
    if (type === 'error') {
      setSuccess(`Error: ${message}`);
      // Auto-clear error display after delay
      setTimeout(() => setSuccess(null), Math.min(duration, 10000));
    } else {
      // For info messages show a loading route temporarily.
      setSuccess(message);
      setTimeout(() => setSuccess(null), Math.min(duration, 8000));
    }
  }, []);

  // Toast notification functions for active bridge requests
  const addToast = useCallback((requestId, requestData) => {
    console.log('🔔 Creating toast with data:', { requestId, requestData });
    
    // Only process bridge requests (not switch nor any non-bridge calls).
    if (!requestId || !requestData || !requestData.sourceNetwork) {
      console.log('🔔 Skipping non-bridge toast - bridge data missing:', requestId);
      return;
    }
    
    const toast = {
      id: requestId,
      requestId,
      status: 'pending',
      sourceNetwork: requestData.sourceNetwork,
      destNetwork: requestData.destNetwork,
      amount: requestData.amount,
      tokenType: requestData.tokenType, // Store token type (usdt/usdx)
      tokenAddress: requestData.tokenAddress, // Store actual contract address
      createdAt: new Date(),
      timestamp: Date.now()
    };
    
    console.log('🔔 Toast object created:', toast);
    setActiveToasts(prev => [...prev, toast]);
  }, []);

  // Function to automatically switch to the correct network
  const switchToCorrectNetwork = useCallback(async (targetChainId) => {
    try {
      console.log(`🔄 Attempting to switch to chain ID: ${targetChainId}`);
      
      // Convert chainId to network key
      const networkKey = getChainKeyById(targetChainId);
      if (!networkKey) {
        throw new Error(`Network not supported for chain ID ${targetChainId}`);
      }
      
      // Use the proper switchToNetwork from useWagmiWallet hook
      await switchToNetwork(networkKey);
      console.log(`✅ Successfully switched to ${networkKey} (Chain ID: ${targetChainId})`);
      return true;
    } catch (error) {
      console.error(`❌ Failed to switch to chain ID ${targetChainId}:`, error);
      
      // Do not show error toast here - this is just an automatic switch attempt
      // The caller will handle showing appropriate messages
      return false;
    }
  }, [switchToNetwork, getChainKeyById]);

  const updateToast = useCallback((requestId, status) => {
    setActiveToasts(prev => prev.map(toast => 
      toast.id === requestId 
        ? { ...toast, status }
        : toast
    ));
  }, []);

  const removeToast = useCallback((requestId) => {
    setActiveToasts(prev => prev.filter(toast => toast.id !== requestId));
  }, []);

  const clearCompletedToasts = useCallback(() => {
    setActiveToasts(prev => prev.filter(toast => 
      toast.status !== 'completed' && toast.status !== 'failed' && toast.status !== 'skipped'
    ));
  }, []);

  const clearAllToasts = useCallback(() => {
    setActiveToasts([]);
  }, []);

  // Removed handleNetworkSwitch - now using Wagmi switchToNetwork

  // Get available destination chains (excluding current source chain)
  const getAvailableDestinationChains = useCallback((sourceChainKey) => {
    return Object.keys(supportedChains).filter(key => key !== sourceChainKey);
  }, [supportedChains]);

  // Get token for specific chain
  

  
  const getChainToken = useCallback((chainKey) => {
    switch (chainKey) {
      case 'bscMainnet':
        return 'usdt';
      case 'xphereMainnet':
        return 'usdx';
      default:
        return null;
    }
  }, []);

  // Updated token system for USDT/USDX/RWA Nova with chain restrictions
  const getSupportedTokens = useCallback((chainId) => {
    if (!chainId) return [];
    
    const chainKey = getChainKeyById(chainId);
    if (!chainKey) return [];

    const contracts = CONTRACT_ADDRESSES[chainId];
    if (!contracts) return [];

    const tokens = [];
    
    // Chain-specific token restrictions
    if (chainKey === 'bscMainnet' && contracts.usdt) {
      tokens.push({ 
        symbol: 'usdt', 
        name: process.env.REACT_APP_USDT_NAME || 'Tether USD', 
        type: 'stablecoin',
        address: contracts.usdt,
        decimals: parseInt(process.env.REACT_APP_USDT_DECIMALS || '6') // Will be updated dynamically
      });
    }
    
    if (chainKey === 'xphereMainnet' && contracts.usdx) {
      tokens.push({ 
        symbol: 'usdx', 
        name: process.env.REACT_APP_USDX_NAME || 'USDX', 
        type: 'stablecoin',
        address: contracts.usdx,
        decimals: parseInt(process.env.REACT_APP_USDX_DECIMALS || '6') // Will be updated dynamically
      });
    }
    
    // RWA Nova - available on both chains
    if (contracts.rwaNova) {
      tokens.push({ 
        symbol: 'rwaNova', 
        name: process.env.REACT_APP_RWA_NOVA_NAME || 'RWA Nova', 
        type: 'token',
        address: contracts.rwaNova,
        decimals: parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18') // Will be updated dynamically
      });
    }
    
    return tokens;
  }, [getChainKeyById]);

  const supportedTokens = useMemo(() => {
    if (!chainId) return [];
    return getSupportedTokens(chainId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chainId]);



  const handleDisconnectWallet = useCallback(() => {
    disconnect();
  }, [disconnect]);



  useEffect(() => {
    // Load bridge requests from localStorage
    const savedRequests = localStorage.getItem('zkBridgeRequests');
    if (savedRequests) {
      setBridgeRequests(JSON.parse(savedRequests));
    }
  }, []);

  useEffect(() => {
    // Save bridge requests to localStorage
    if (bridgeRequests.length > 0) {
      localStorage.setItem('zkBridgeRequests', JSON.stringify(bridgeRequests));
    }
  }, [bridgeRequests]);



  // Set default token when chain changes (only if no token selected or current token not available)
  useEffect(() => {
    if (chainId && supportedTokens.length > 0) {
      // Check if current token is still available
      const currentTokenAvailable = supportedTokens.find(t => t.symbol === token);
      if (!token || !currentTokenAvailable) {
        // Set the first available token as default
        setToken(supportedTokens[0].symbol);
      }
    } else if (chainId && supportedTokens.length === 0) {
      setToken('');
    }
  }, [chainId, supportedTokens]); // eslint-disable-line react-hooks/exhaustive-deps

  // Note: Removed aggressive auto-switching of bridge configuration
  // Users should manually select their desired source/destination chains


  const switchToChain = useCallback(async (chainKey) => {
    if (!window.ethereum) return;
    
    const chain = supportedChains[chainKey];
    if (!chain) return;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${BigInt(chain.id).toString(16)}` }],
      });
    } catch (switchError) {
      // Chain not added to MetaMask
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [{
              chainId: `0x${BigInt(chain.id).toString(16)}`,
              chainName: chain.name,
              rpcUrls: [chain.rpcUrl],
              nativeCurrency: {
                name: chain.currency,
                symbol: chain.currency,
                decimals: 18,
              },
              blockExplorerUrls: [chain.explorer],
            }],
          });
        } catch (addError) {
          console.error('Failed to add chain:', addError);
        }
      }
    }
  }, [supportedChains]);

  // Removed unused ethers.js contract functions - now using Wagmi hooks directly

  // Helper function to get token reserve (mainnet-only)
  const getTokenReserve = useCallback(async (contract, tokenAddress) => {
    console.log(`🔍 Getting token reserve for ${tokenAddress} on mainnet`);
    
            try {
            // Mainnet contracts use getTokenReserve (singular) - ZKBridgeDestinationEnhancedFixed.sol
            console.log('🔄 Trying mainnet function: getTokenReserve');
            const result = await contract.getTokenReserve(tokenAddress);
            console.log('✅ Mainnet getTokenReserve successful:', result.toString());
            return result;
        } catch (error) {
      console.log(`❌ Primary function failed for mainnet:`, error.message);
            throw new Error(`getTokenReserve failed: ${error.message}`);
          }
  }, []);

  // Helper function to get daily limit (mainnet-only)
  const getDailyLimit = useCallback(async (contract, tokenAddress) => {
    console.log(`🔍 Getting daily limit for ${tokenAddress} on mainnet`);
    
    try {
      // Mainnet contracts have getRemainingDailyMintLimit
      console.log('🔄 Trying mainnet function: getRemainingDailyMintLimit');
      const result = await contract.getRemainingDailyMintLimit(tokenAddress);
      console.log('✅ Mainnet getRemainingDailyMintLimit successful:', result.toString());
      return result;
    } catch (error) {
      console.log(`❌ Daily limit function failed for mainnet:`, error.message);
      // Return 0 if function fails
      return ethers.parseUnits('0', 6);
    }
  }, []);

  const getDestinationContractReadOnly = useCallback((destChainId) => {
    
    console.log(`🔧 Creating destination contract for chain: ${destChainId} (type: ${typeof destChainId})`);
    
    const address = CONTRACT_ADDRESSES_MAPPED[destChainId]?.destination;
    if (!address) throw new Error(`Destination contract not deployed on chain ${destChainId}`);
    
    console.log(`   Contract address: ${address}`);
    
    // Create a provider specific to the destination chain
    let destProvider;
    // Convert to string for comparison to handle both number and string inputs
    const chainIdStr = destChainId.toString();
    console.log(`   Chain ID string: ${chainIdStr}`);
    
    if (chainIdStr === '56') {
      // BSC Mainnet
      const rpcUrl = process.env.REACT_APP_BSC_MAINNET_RPC_URL || 'https://bsc-dataseed.binance.org';
      console.log(`   Using BSC Mainnet provider: ${rpcUrl}`);
      destProvider = new ethers.JsonRpcProvider(rpcUrl);
    } else if (chainIdStr === '20250217') {
      // Xphere Mainnet
      const rpcUrl = process.env.REACT_APP_XPHERE_MAINNET_RPC_URL || 'https://xp-mainnet.rpc.xplorium.xyz';
      console.log(`   Using Xphere Mainnet provider: ${rpcUrl}`);
      destProvider = new ethers.JsonRpcProvider(rpcUrl);
    } else {
      throw new Error(`Unsupported destination chain: ${destChainId}`);
    }
    
    console.log(`   Provider created successfully`);
    return new ethers.Contract(address, DESTINATION_ABI, destProvider);
  }, [CONTRACT_ADDRESSES_MAPPED, DESTINATION_ABI]);





  const loadTokenBalances = useCallback(async () => {
    if (!wagmiAccount || !publicClient || !chainId) return;

    try {
      const balances = {};
      const contracts = CONTRACT_ADDRESSES[chainId];
      
      if (contracts) {
        // Load USDT balance
        if (contracts.usdt) {
          const balance = await publicClient.readContract({
            address: contracts.usdt,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [wagmiAccount],
          });
          
          // Get decimals from enhanced contract if available
          try {
            const sourceContractAddress = CONTRACT_ADDRESSES[chainId]?.sourceBridge;
            if (sourceContractAddress) {
              const tokenInfo = await publicClient.readContract({
                address: sourceContractAddress,
                abi: SOURCE_ABI,
                functionName: 'getTokenInfo',
                args: [contracts.usdt],
              });
              
              // Parse the tokenInfo response - it can be an object or array
              let tokenDecimals;
              if (Array.isArray(tokenInfo)) {
                const [, decimals] = tokenInfo;
                tokenDecimals = decimals;
              } else if (tokenInfo && typeof tokenInfo === 'object') {
                tokenDecimals = tokenInfo.decimals;
              } else {
                // Try to get decimals from token contract as fallback
                try {
                  const tokenContract = new ethers.Contract(contracts.usdt, ERC20_ABI, provider);
                  tokenDecimals = await tokenContract.decimals();
                } catch {
                  tokenDecimals = 6; // final fallback
                }
              }
              balances.usdt = ethers.formatUnits(balance, tokenDecimals);
            } else {
              // Try to get decimals from token contract as fallback
              try {
                const tokenContract = new ethers.Contract(contracts.usdt, ERC20_ABI, provider);
                const tokenDecimals = await tokenContract.decimals();
                balances.usdt = ethers.formatUnits(balance, tokenDecimals);
              } catch {
                // Final fallback to environment variable
                balances.usdt = ethers.formatUnits(balance, parseInt(process.env.REACT_APP_USDT_DECIMALS));
              }
            }
          } catch {
            // Try to get decimals from token contract as fallback
            try {
              const tokenContract = new ethers.Contract(contracts.usdt, ERC20_ABI, provider);
              const tokenDecimals = await tokenContract.decimals();
              balances.usdt = ethers.formatUnits(balance, tokenDecimals);
            } catch {
              // Final fallback to environment variable
              balances.usdt = ethers.formatUnits(balance, parseInt(process.env.REACT_APP_USDT_DECIMALS));
            }
          }
        }

        // Load USDX balance
        if (contracts.usdx) {
          const balance = await publicClient.readContract({
            address: contracts.usdx,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [wagmiAccount],
          });
          
          // Get decimals from enhanced contract if available
          try {
            const sourceContractAddress = CONTRACT_ADDRESSES[chainId]?.sourceBridge;
            if (sourceContractAddress) {
              const tokenInfo = await publicClient.readContract({
                address: sourceContractAddress,
                abi: SOURCE_ABI,
                functionName: 'getTokenInfo',
                args: [contracts.usdx],
              });
              
              // Parse the tokenInfo response - it can be an object or array
              let tokenDecimals;
              if (Array.isArray(tokenInfo)) {
                const [, decimals] = tokenInfo;
                tokenDecimals = decimals;
              } else if (tokenInfo && typeof tokenInfo === 'object') {
                tokenDecimals = tokenInfo.decimals;
              } else {
                // Try to get decimals from token contract as fallback
                try {
                  const tokenContract = new ethers.Contract(contracts.usdx, ERC20_ABI, provider);
                  tokenDecimals = await tokenContract.decimals();
                } catch {
                  tokenDecimals = 6; // final fallback
                }
              }
              balances.usdx = ethers.formatUnits(balance, tokenDecimals);
            } else {
              // Try to get decimals from token contract as fallback
              try {
                const tokenContract = new ethers.Contract(contracts.usdx, ERC20_ABI, provider);
                const tokenDecimals = await tokenContract.decimals();
                balances.usdx = ethers.formatUnits(balance, tokenDecimals);
              } catch {
                // Final fallback to environment variable
                balances.usdx = ethers.formatUnits(balance, parseInt(process.env.REACT_APP_USDX_DECIMALS));
              }
            }
          } catch {
            // Try to get decimals from token contract as fallback
            try {
              const tokenContract = new ethers.Contract(contracts.usdx, ERC20_ABI, provider);
              const tokenDecimals = await tokenContract.decimals();
              balances.usdx = ethers.formatUnits(balance, tokenDecimals);
            } catch {
              // Final fallback to environment variable
              balances.usdx = ethers.formatUnits(balance, parseInt(process.env.REACT_APP_USDX_DECIMALS));
            }
          }
        }

        // Load RWA Nova balance
        if (contracts.rwaNova) {
          const balance = await publicClient.readContract({
            address: contracts.rwaNova,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [wagmiAccount],
          });
          
          // Get decimals from enhanced contract if available
          try {
            const sourceContractAddress = CONTRACT_ADDRESSES[chainId]?.sourceBridge;
            if (sourceContractAddress) {
              const tokenInfo = await publicClient.readContract({
                address: sourceContractAddress,
                abi: SOURCE_ABI,
                functionName: 'getTokenInfo',
                args: [contracts.rwaNova],
              });
              
              // Parse the tokenInfo response - it can be an object or array
              let tokenDecimals;
              if (Array.isArray(tokenInfo)) {
                const [, decimals] = tokenInfo;
                tokenDecimals = decimals;
              } else if (tokenInfo && typeof tokenInfo === 'object') {
                tokenDecimals = tokenInfo.decimals;
              } else {
                // Try to get decimals from token contract as fallback
                try {
                  const tokenContract = new ethers.Contract(contracts.rwaNova, ERC20_ABI, provider);
                  tokenDecimals = await tokenContract.decimals();
                } catch {
                  tokenDecimals = 18; // final fallback for RWA Nova
                }
              }
              balances.rwaNova = ethers.formatUnits(balance, tokenDecimals);
            } else {
              // Try to get decimals from token contract as fallback
              try {
                const tokenContract = new ethers.Contract(contracts.rwaNova, ERC20_ABI, provider);
                const tokenDecimals = await tokenContract.decimals();
                balances.rwaNova = ethers.formatUnits(balance, tokenDecimals);
              } catch {
                // Final fallback to environment variable
                balances.rwaNova = ethers.formatUnits(balance, parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18'));
              }
            }
          } catch {
            // Try to get decimals from token contract as fallback
            try {
              const tokenContract = new ethers.Contract(contracts.rwaNova, ERC20_ABI, provider);
              const tokenDecimals = await tokenContract.decimals();
              balances.rwaNova = ethers.formatUnits(balance, tokenDecimals);
            } catch {
              // Final fallback to environment variable
              balances.rwaNova = ethers.formatUnits(balance, parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18'));
            }
          }
        }
      }
      
      setTokenBalances(balances);
    } catch (error) {
      console.error('Error loading token balances:', error);
    }
  }, [wagmiAccount, publicClient, chainId, ERC20_ABI, SOURCE_ABI, provider]);

  // Load token balances when wagmiAccount changes
  useEffect(() => {
    if (wagmiAccount && chainId) {
      loadTokenBalances();
    }
  }, [wagmiAccount, chainId, loadTokenBalances]);

  // Automatically set source chain based on connected network
  useEffect(() => {
    if (isConnected && chainId && supportedChains) {
      // Find the chain key that matches the current chain ID
      const matchingChainKey = Object.keys(supportedChains).find(key => 
        BigInt(supportedChains[key].id) === BigInt(chainId)
      );
      
      if (matchingChainKey && matchingChainKey !== sourceChain) {
        console.log(`🔄 Auto-setting source chain to ${matchingChainKey} based on connected network (Chain ID: ${chainId})`);
        setSourceChain(matchingChainKey);
        
        // If destination is the same as new source, change it to the other available chain
        if (matchingChainKey === destChain) {
          const availableDestinations = getAvailableDestinationChains(matchingChainKey);
          if (availableDestinations.length > 0) {
            setDestChain(availableDestinations[0]);
          }
        }
      }
    }
  }, [isConnected, chainId, supportedChains, sourceChain, destChain, getAvailableDestinationChains]);


  const checkTokenApproval = useCallback(async (tokenAddress, spenderAddress, amount) => {
    // Get token info from enhanced contract to determine decimals
    const sourceContractAddress = CONTRACT_ADDRESSES[chainId]?.sourceBridge;
    if (!sourceContractAddress) throw new Error(`Source contract not deployed on chain ${chainId}`);
    
    const tokenInfo = await publicClient.readContract({
      address: sourceContractAddress,
      abi: SOURCE_ABI,
      functionName: 'getTokenInfo',
      args: [tokenAddress],
    });
    
    // Parse the tokenInfo response - it can be an object or array
    let tokenDecimals;
    if (Array.isArray(tokenInfo)) {
      const [, decimals] = tokenInfo;
      tokenDecimals = decimals;
    } else if (tokenInfo && typeof tokenInfo === 'object') {
      tokenDecimals = tokenInfo.decimals;
    } else {
      // Try to get decimals from token contract as fallback
      try {
        const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
        tokenDecimals = await tokenContract.decimals();
      } catch {
        tokenDecimals = 6; // final fallback
      }
    }
    
    const requiredAmount = ethers.parseUnits(amount, tokenDecimals);
    
    // Use publicClient to read allowance
    const allowance = await publicClient.readContract({
      address: tokenAddress,
      abi: ERC20_ABI,
      functionName: 'allowance',
      args: [wagmiAccount, spenderAddress],
    });
    
    return BigInt(allowance) >= BigInt(requiredAmount);
  }, [publicClient, wagmiAccount, chainId, SOURCE_ABI, ERC20_ABI, provider]);

  // Helper function to map network name to chain ID
  const getChainIdFromNetwork = useCallback((networkName) => {
    if (!networkName) return null;
    
    switch (networkName.toLowerCase()) {
      case 'bscmainnet':
        return 56; // BSC Mainnet
      case 'xpheremainnet':
        return 20250217; // Xphere Mainnet
      default:
        return null;
    }
  }, []);

  const approveToken = useCallback(async (tokenAddress, spenderAddress, amount) => {
    // Verify we're on the correct chain for approval - real-time check
    const requiredChainId = getChainIdFromNetwork(sourceChain);
    
    // Real-time verification right before transaction
    let realCurrentChain;
    try {
      if (provider && provider.getNetwork) {
        const network = await provider.getNetwork();
        realCurrentChain = Number(network.chainId);
      } else {
        // Fallback to use window.ethereum.eth_chainId itself instead
        if (window.ethereum) {
          const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
          realCurrentChain = parseInt(chainIdHex, 16);
        } else {
          realCurrentChain = chainId;
        }
      }
      console.log(`🔍 Real-time approval chain check: current ${realCurrentChain}, required ${requiredChainId}`);
      if (realCurrentChain && requiredChainId && BigInt(realCurrentChain) !== BigInt(requiredChainId)) {
        const networkName = supportedChains[sourceChain]?.name || sourceChain;
        throw new Error(`Cannot approve tokens on chain ${realCurrentChain}. Please switch to ${networkName} for approval.`);
      }
    } catch (providerCheckError) {
      throw new Error(`Approval chain verification failed: ${providerCheckError.message}`);
    }
    
    // Get token info from enhanced contract to determine decimals
    const sourceContractAddress = CONTRACT_ADDRESSES[realCurrentChain]?.sourceBridge;
    if (!sourceContractAddress) throw new Error(`Source contract not deployed on chain ${realCurrentChain}`);
    
    const tokenInfo = await publicClient.readContract({
      address: sourceContractAddress,
      abi: SOURCE_ABI,
      functionName: 'getTokenInfo',
      args: [tokenAddress],
    });
    
    // Parse the tokenInfo response - it can be an object or array
    let tokenDecimals;
    if (Array.isArray(tokenInfo)) {
      const [, decimals] = tokenInfo;
      tokenDecimals = decimals;
    } else if (tokenInfo && typeof tokenInfo === 'object') {
      tokenDecimals = tokenInfo.decimals;
    } else {
      // Try to get decimals from token contract as fallback
      try {
        const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
        tokenDecimals = await tokenContract.decimals();
      } catch {
        tokenDecimals = 6; // final fallback
      }
    }
    
    const approveAmount = ethers.parseUnits(amount, tokenDecimals);
    
    console.log(`Approving ${amount} tokens (${tokenDecimals} decimals) for bridge contract...`);
    
    try {
      console.log('🔍 Approval parameters:', {
        tokenAddress,
        spenderAddress,
        approveAmount: approveAmount.toString(),
        abi: ERC20_ABI.length
      });

      // Check for writeContract errors before attempting transaction, but skip cached user rejection errors
      if (writeContractError) {
        // Check if this is a cached user rejection that we should ignore for retry attempts
        const isUserRejection = writeContractError.message && (
          writeContractError.message.includes('User rejected the request') ||
          writeContractError.message.includes('User denied transaction signature') ||
          writeContractError.code === 'ACTION_REJECTED' ||
          writeContractError.name === 'UserRejectedRequestError'
        );
        
        if (isUserRejection) {
          console.log('⏩ Ignoring cached user rejection error, proceeding with fresh approval attempt');
        } else {
          console.warn('Persistent approval call writeContractError detected, likely due to previous cancelled request:', writeContractError);
          throw new Error(`WriteContract error: ${writeContractError.message}`);
        }
      }

      if (isWriteContractPending) {
        console.warn('Another transaction is pending (isWriteContractPending=true), please wait');
        throw new Error('Another transaction is pending, please wait');
      }

      // Force wallet network context refresh for approval using Wagmi
      try {
        // Use Wagmi's switchChain action for proper network switching 
        await switchChain(wagmiConfig, { chainId: realCurrentChain });
        
        // Wait a moment for the network switch to fully activate the provider
        await new Promise(resolve => setTimeout(resolve, 1000));
        console.log('🔄 Forced wallet to network context for approval via Wagmi', realCurrentChain);
      } catch (switchError) {
        // This is expected if already on the correct network
        console.warn('Approval network switch already succeeded or not needed:', switchError.message);
      }

      // Use Wagmi's writeContractAsync for approval
      const hash = await writeContractAsync({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'approve',
        args: [spenderAddress, approveAmount],
      });
      
      console.log('Approval transaction hash:', hash);
      
      if (!hash) {
        throw new Error('Transaction hash is undefined - transaction may have been rejected or failed');
      }
      
      // Wait for transaction confirmation
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      console.log('Token approval confirmed:', receipt);
      
    } catch (error) {
      console.error('Approval failed:', error);
        throw error;
      }
  }, [writeContractAsync, writeContractError, isWriteContractPending, publicClient, chainId, SOURCE_ABI, ERC20_ABI, provider, sourceChain, supportedChains, getChainIdFromNetwork]);

  // Check destination contract status for enhanced contracts
  const checkDestinationContractStatus = useCallback(async (sourceChainId, tokenAddress) => {
    try {
      // For cross-chain bridges, we need to check the source chain's destination contract
      // to get the correct token mapping, then check the actual destination chain for reserves
      
      // First, get the token mapping from the source chain's destination contract
      const sourceDestContract = getDestinationContractReadOnly(sourceChainId);
      if (!sourceDestContract) {
        throw new Error('Source chain destination contract not available');
      }

      console.log(`🔍 Checking source chain destination contract on chain ${sourceChainId} for token mapping`);
      
      // Get token mapping from source chain's destination contract
      const tokenMapping = await sourceDestContract.getTokenMapping(tokenAddress);
      const destTokenAddress = tokenMapping[0];
      const isMappingActive = tokenMapping[2];
      const isCrossChain = tokenMapping[3];
      
      console.log(`✅ Token mapping: ${tokenAddress} -> ${destTokenAddress} (active: ${isMappingActive}, crossChain: ${isCrossChain})`);
      
      if (!isMappingActive) {
        throw new Error(`Token mapping not configured on source chain destination contract`);
      }

      // Now check the actual destination chain for reserves
      // We need to determine the destination chain ID from the bridge direction
      const destChainId = sourceChainId === 56 ? 20250217 : 56; // BSC <-> Xphere
      
      const destContract = getDestinationContractReadOnly(destChainId);
      if (!destContract) {
        throw new Error('Destination contract not available');
      }

      console.log(`🔍 Checking destination contract on chain ${destChainId} for reserves`);
      
      // Test basic contract accessibility
      try {
        const contractAddress = await destContract.getAddress();
        console.log(`✅ Destination contract accessible at: ${contractAddress}`);
        console.log(`   Using provider for chain ${destChainId}`);
      } catch (accessError) {
        console.warn(`⚠️ Could not access destination contract: ${accessError.message}`);
        throw new Error(`Destination contract not accessible: ${accessError.message}`);
      }

      // Check if the source chain is supported by destination contract
      try {
        const isSourceChainSupported = await destContract.supportedSourceChains(sourceChainId);
        console.log(`✅ Source chain ${sourceChainId} supported: ${isSourceChainSupported}`);
        
        if (!isSourceChainSupported) {
          throw new Error(`Source chain ${sourceChainId} not supported by destination contract`);
        }
      } catch (chainError) {
        console.warn(`⚠️ Could not check source chain support: ${chainError.message}`);
        // Continue with other checks if this fails
      }

      // Check if destination token is mintable or has sufficient reserves
      try {
        console.log(`🔍 Checking destination token: ${destTokenAddress} on chain ${destChainId}`);
        
        const isMintable = await destContract.mintableTokens(destTokenAddress);
        console.log(`✅ Destination token mintable: ${isMintable}`);
        
        if (!isMintable) {
          // Only check reserves for non-mintable tokens
          const reserves = await getTokenReserve(destContract, destTokenAddress);
          console.log(`✅ Destination token reserves: ${reserves.toString()}`);
          
          if (reserves === 0n) {
            throw new Error(`Destination token has no reserves on destination chain`);
          }
        } else {
          // For mintable tokens, reserves are not needed
          console.log(`✅ Destination token is mintable - no reserves required`);
        }
        
        // Try to get remaining daily limit
        try {
          const remainingLimit = await getDailyLimit(destContract, destTokenAddress);
          console.log(`✅ Daily limit: ${remainingLimit.toString()}`);
        } catch (limitError) {
          console.warn(`⚠️ Could not check daily limit: ${limitError.message}`);
        }
        
      } catch (reserveError) {
        console.warn(`⚠️ Could not check destination token reserves: ${reserveError.message}`);
        // Continue with other checks if this fails
      }

      return {
        destChainId,
        sourceChainId,
        tokenAddress,
        destTokenAddress,
        status: 'available',
        details: {
          contractAccessible: true,
          sourceChainSupported: true,
          tokenMappingActive: true,
          isCrossChain
        }
      };
    } catch (error) {
      console.warn('Destination contract check failed:', error.message);
      // Don't throw error, just return status
      return {
        destChainId: sourceChainId === 56 ? 20250217 : 56,
        sourceChainId,
        tokenAddress,
        status: 'error',
        error: error.message
      };
    }
  }, [getDestinationContractReadOnly, getTokenReserve, getDailyLimit]);

  // Check bridge availability with comprehensive reserve checking
  const checkBridgeAvailabilityStatus = useCallback(async () => {
    if (!amount || !sourceChain || !destChain || !token) {
      setBridgeAvailability({
        isChecking: false,
        isAvailable: true,
        reason: '',
        details: null,
        lastChecked: null
      });
      return;
    }

    setBridgeAvailability(prev => ({ ...prev, isChecking: true }));

    try {
      const sourceChainInfo = supportedChains[sourceChain];
      const destChainInfo = supportedChains[destChain];
      
      if (!sourceChainInfo || !destChainInfo) {
        throw new Error('Invalid chain configuration');
      }

      const sourceContractAddress = CONTRACT_ADDRESSES_MAPPED[sourceChainInfo.id]?.source;
      const destContractAddress = CONTRACT_ADDRESSES_MAPPED[destChainInfo.id]?.destination;
      // Get token address from supportedTokens first, then fallback to CONTRACT_ADDRESSES_MAPPED
      const selectedTokenInfo = supportedTokens.find(t => t.symbol === token);
      const tokenAddress = selectedTokenInfo?.address || CONTRACT_ADDRESSES_MAPPED[sourceChainInfo.id]?.[token.toLowerCase()];

      if (!sourceContractAddress || !destContractAddress || !tokenAddress) {
        throw new Error('Contract addresses not configured');
      }

      const availability = await checkBridgeAvailabilityWithDirection({
        sourceChainId: sourceChainInfo.id,
        destChainId: destChainInfo.id,
        sourceTokenAddress: tokenAddress,
        amount: amount,
        tokenSymbol: token // Pass the actual token symbol (e.g., 'RWANOVA', 'USDX', 'USDT')
      });

      setBridgeAvailability({
        isChecking: false,
        isAvailable: availability.available,
        reason: availability.reason,
        details: availability.details,
        lastChecked: new Date()
      });

      console.log('🔍 Bridge availability check result:', availability);

    } catch (error) {
      console.error('❌ Error checking bridge availability:', error);
      setBridgeAvailability({
        isChecking: false,
        isAvailable: false,
        reason: `Error checking availability: ${error.message}`,
        details: { error: error.message },
        lastChecked: new Date()
      });
    }
  }, [amount, sourceChain, destChain, token, supportedChains, CONTRACT_ADDRESSES_MAPPED]);

  // Check bridge availability when relevant parameters change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      checkBridgeAvailabilityStatus();
    }, 500); // Debounce the check

    return () => clearTimeout(timeoutId);
  }, [amount, sourceChain, destChain, token, checkBridgeAvailabilityStatus]);

  // Bridge status tracking functions - moved here to fix initialization order
  const loadAllBridgeHistory = useCallback(async (customFilters = null, page = 1, pageSize = 20) => {
    setIsLoadingHistory(true);
    try {
      // Get bridge requests with current filters and pagination
      const offset = (page - 1) * pageSize;
      const filterOptions = { 
        limit: pageSize,
        offset: offset
      };
      const filtersToUse = customFilters || analyticsFilter || {};
      
      if (filtersToUse.status) filterOptions.status = filtersToUse.status;
      if (filtersToUse.network) filterOptions.network = filtersToUse.network;
      
      const history = await bridgeService.getBridgeRequests(filterOptions);
      let requests = history.requests || [];
      
      // Debug: Log the full API response to understand the structure
      console.log('🔍 API Response Debug:', {
        fullResponse: history,
        requestsLength: requests.length,
        totalRequests: history.totalRequests,
        returnedRequests: history.returnedRequests,
        limit: history.limit,
        offset: history.offset
      });
      
      // Apply user address filter on the frontend if specified
      if (filtersToUse.userAddress) {
        const searchAddress = filtersToUse.userAddress.toLowerCase();
        requests = requests.filter(request => 
          (request.userAddress && request.userAddress.toLowerCase().includes(searchAddress)) ||
          (request.recipientAddress && request.recipientAddress.toLowerCase().includes(searchAddress))
        );
      }
      
      // Update pagination state
      // The backend returns 'totalRequests' field, not 'totalCount'
      let totalCount = history.totalRequests;
      
      // Fallback to other possible field names if totalRequests is not available
      if (!totalCount) {
        totalCount = history.totalCount || history.total || 0;
      }
      
      // If still no total count, estimate based on current page
      if (!totalCount) {
        if (requests.length === pageSize) {
          // If we got a full page, there might be more data
          totalCount = (page * pageSize) + 1; // Estimate: at least one more page
          console.log('🔍 Estimated totalCount:', totalCount);
        } else {
          // If we got less than a full page, that's probably all the data
          totalCount = requests.length;
          console.log('🔍 Using requests.length as totalCount:', totalCount);
        }
      }
      
      console.log('🔍 Pagination Debug:', {
        totalCount,
        requestsLength: requests.length,
        page,
        pageSize,
        apiTotalRequests: history.totalRequests,
        apiReturnedRequests: history.returnedRequests,
        finalTotalCount: totalCount
      });
      
      setPagination({
        currentPage: page,
        pageSize: pageSize,
        totalCount: totalCount
      });
      
      setAllBridgeHistory(requests);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Error loading bridge history:', error);
      
      // Only show toast for unexpected errors, not for API unavailability
      if (error.message && !error.message.includes('Network error') && !error.message.includes('Unable to connect')) {
        addToast(`error_${Date.now()}`, {
          type: 'error',
          title: 'Loading Error',
          message: 'Failed to load bridge history',
          duration: 5000
        });
      } else {
        console.log('ℹ️ Bridge history API not available, skipping toast notification');
      }
    } finally {
      setIsLoadingHistory(false);
    }
  }, [analyticsFilter, addToast]);

  // Pagination control functions
  const goToPage = useCallback((page) => {
    loadAllBridgeHistory(analyticsFilter, page, pagination.pageSize);
  }, [analyticsFilter, pagination.pageSize, loadAllBridgeHistory]);

  // Function to fetch ALL completed transactions for accurate fee calculation
  const loadAllCompletedTransactionsForFees = useCallback(async () => {
    try {
      console.log('🔍 Fetching ALL completed transactions for fee calculation...');
      
      // Fetch all completed transactions without pagination limit
      const allCompletedRequests = await bridgeService.getBridgeRequests({
        status: 'completed',
        limit: 10000 // Large limit to get all completed transactions
      });
      
      console.log(`📊 Retrieved ${allCompletedRequests.requests?.length || 0} completed transactions for fee calculation`);
      return allCompletedRequests.requests || [];
    } catch (error) {
      console.error('❌ Error fetching all completed transactions for fees:', error);
      return [];
    }
  }, []);

  // Function to get actual counts for analytics dashboard
  const getActualAnalyticsCounts = useCallback(async () => {
    try {
      console.log('🔍 Fetching actual analytics counts...');
      
      // Get all transactions for accurate counts
      const allRequests = await bridgeService.getBridgeRequests({
        limit: 10000 // Large limit to get all transactions
      });
      
      const allTransactions = allRequests.requests || [];
      
      // Calculate total volume in USD and per-token statistics
      let totalVolumeUSD = 0;
      const tokenStats = {
        usdt: { total: 0, completed: 0, volume: 0, transactions: [] },
        usdx: { total: 0, completed: 0, volume: 0, transactions: [] },
        rwaNova: { total: 0, completed: 0, volume: 0, transactions: [] }
      };
      
      const rwaNovaBsc = (process.env.REACT_APP_RWA_NOVA_BSC_ADDRESS || '0x49A756e34A5A0a7FDBFCce73d10228ee2C85CA8b').toLowerCase();
      const rwaNovaXphere = (process.env.REACT_APP_RWA_NOVA_XPHERE_ADDRESS || '0x0DFfFad57Bc67701244B1bdA37E830eeadb0d69F').toLowerCase();
      const usdtAddress = process.env.REACT_APP_USDT_ADDRESS?.toLowerCase();
      const usdxAddress = process.env.REACT_APP_USDX_ADDRESS?.toLowerCase();
      
      allTransactions.forEach(transaction => {
        const tokenAddress = (transaction.token_address || transaction.tokenAddress || '').toLowerCase();
        const sourceNetwork = (transaction.source_network || transaction.sourceNetwork || '').toLowerCase();
        
        // Determine token type from address
        let tokenType = null;
        let tokenDecimals = 18; // Default
        
        if (tokenAddress === rwaNovaBsc || tokenAddress === rwaNovaXphere) {
          tokenType = 'rwaNova';
          tokenDecimals = parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18');
        } else if (tokenAddress === usdtAddress) {
          tokenType = 'usdt';
          tokenDecimals = parseInt(process.env.REACT_APP_USDT_DECIMALS || '6');
        } else if (tokenAddress === usdxAddress) {
          tokenType = 'usdx';
          tokenDecimals = parseInt(process.env.REACT_APP_USDX_DECIMALS || '6');
        } else {
          // Fallback: determine by network if address not available
          if (sourceNetwork.includes('bsc')) {
            tokenType = 'usdt';
            tokenDecimals = parseInt(process.env.REACT_APP_USDT_DECIMALS || '6');
          } else if (sourceNetwork.includes('xphere')) {
            tokenType = 'usdx';
            tokenDecimals = parseInt(process.env.REACT_APP_USDX_DECIMALS || '6');
          }
        }
        
        if (tokenType) {
          tokenStats[tokenType].total++;
          tokenStats[tokenType].transactions.push(transaction);
          
          if (transaction.status === 'completed') {
            tokenStats[tokenType].completed++;
            const rawAmount = parseFloat(transaction.amount) || 0;
            const amountUSD = rawAmount / Math.pow(10, tokenDecimals);
            tokenStats[tokenType].volume += amountUSD;
            totalVolumeUSD += amountUSD;
            
            // console.log(`💰 Volume calculation [${tokenType}]: ${sourceNetwork} -> ${amountUSD} USD (raw: ${rawAmount}, decimals: ${tokenDecimals})`);
          }
        }
      });
      
      const counts = {
        total: allTransactions.length,
        completed: allTransactions.filter(req => req.status === 'completed').length,
        active: allTransactions.filter(req => req.status === 'pending' || req.status === 'processing').length,
        totalVolumeUSD: totalVolumeUSD,
        successRate: allTransactions.length > 0 
          ? Math.round((allTransactions.filter(req => req.status === 'completed').length / allTransactions.length) * 100)
          : 0,
        tokenStats: tokenStats
      };
      
      console.log('📊 Actual analytics counts:', counts);
      return counts;
    } catch (error) {
      console.error('❌ Error fetching actual analytics counts:', error);
      // Fallback to paginated data
      let fallbackVolume = 0;
      const usdtDecimals = parseInt(process.env.REACT_APP_USDT_DECIMALS) || 18;
      const usdxDecimals = parseInt(process.env.REACT_APP_USDX_DECIMALS) || 6;
      
      const fallbackTokenStats = {
        usdt: { total: 0, completed: 0, volume: 0 },
        usdx: { total: 0, completed: 0, volume: 0 },
        rwaNova: { total: 0, completed: 0, volume: 0 }
      };
      
      const rwaNovaBsc = (process.env.REACT_APP_RWA_NOVA_BSC_ADDRESS || '0x49A756e34A5A0a7FDBFCce73d10228ee2C85CA8b').toLowerCase();
      const rwaNovaXphere = (process.env.REACT_APP_RWA_NOVA_XPHERE_ADDRESS || '0x0DFfFad57Bc67701244B1bdA37E830eeadb0d69F').toLowerCase();
      const usdtAddress = process.env.REACT_APP_USDT_ADDRESS?.toLowerCase();
      const usdxAddress = process.env.REACT_APP_USDX_ADDRESS?.toLowerCase();
      
      allBridgeHistory.forEach(transaction => {
        const tokenAddress = (transaction.token_address || transaction.tokenAddress || '').toLowerCase();
        const sourceNetwork = (transaction.source_network || transaction.sourceNetwork || '').toLowerCase();
        
        // Determine token type from address
        let tokenType = null;
        let tokenDecimals = 18;
        
        if (tokenAddress === rwaNovaBsc || tokenAddress === rwaNovaXphere) {
          tokenType = 'rwaNova';
          tokenDecimals = parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18');
        } else if (tokenAddress === usdtAddress) {
          tokenType = 'usdt';
          tokenDecimals = usdtDecimals;
        } else if (tokenAddress === usdxAddress) {
          tokenType = 'usdx';
          tokenDecimals = usdxDecimals;
        } else {
          // Fallback: determine by network
          if (sourceNetwork.includes('bsc')) {
            tokenType = 'usdt';
            tokenDecimals = usdtDecimals;
          } else if (sourceNetwork.includes('xphere')) {
            tokenType = 'usdx';
            tokenDecimals = usdxDecimals;
          }
        }
        
        if (tokenType) {
          fallbackTokenStats[tokenType].total++;
          
          if (transaction.status === 'completed') {
            fallbackTokenStats[tokenType].completed++;
            const rawAmount = parseFloat(transaction.amount) || 0;
            const amountUSD = rawAmount / Math.pow(10, tokenDecimals);
            fallbackTokenStats[tokenType].volume += amountUSD;
            fallbackVolume += amountUSD;
          }
        }
      });
      
      return {
        total: pagination.totalCount || allBridgeHistory.length,
        completed: allBridgeHistory.filter(req => req.status === 'completed').length,
        active: allBridgeHistory.filter(req => req.status === 'pending' || req.status === 'processing').length,
        totalVolumeUSD: fallbackVolume,
        successRate: allBridgeHistory.length > 0 
          ? Math.round((allBridgeHistory.filter(req => req.status === 'completed').length / allBridgeHistory.length) * 100)
          : 0,
        tokenStats: fallbackTokenStats
      };
    }
  }, [allBridgeHistory, pagination.totalCount]);

  // Function to fetch actual token balances from source contracts
  const fetchSourceContractBalances = useCallback(async () => {
    try {
      console.log('🔍 Fetching actual token balances from source contracts...');
      
      const balances = {
        usdtLocked: '0',
        usdxLocked: '0',
        rwaNovaBscLocked: '0',
        rwaNovaXphereLocked: '0'
      };

      // BSC Source Contract: Get USDT balance
      const bscSourceAddress = process.env.REACT_APP_BSC_SOURCE_BRIDGE;
      const bscUsdtAddress = process.env.REACT_APP_USDT_ADDRESS;
      
      if (bscSourceAddress && bscUsdtAddress) {
        try {
          // Create BSC public client
          const bscClient = createPublicClient({
            chain: {
              id: 56,
              name: 'BSC Mainnet',
              network: 'bsc',
              nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
              rpcUrls: {
                default: { http: ['https://api-bsc-mainnet-full.n.dwellir.com/c6fd7c04-24d6-4cb1-881b-c3c3a43ba755'] },
                public: { http: ['https://api-bsc-mainnet-full.n.dwellir.com/c6fd7c04-24d6-4cb1-881b-c3c3a43ba755'] }
              },
              blockExplorers: {
                default: { name: 'BSCScan', url: 'https://bscscan.com' }
              }
            },
            transport: http()
          });

          // Get USDT balance of BSC source contract
          const usdtBalance = await bscClient.readContract({
            address: bscUsdtAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [bscSourceAddress]
          });

          const usdtDecimals = parseInt(process.env.REACT_APP_USDT_DECIMALS) || 18;
          balances.usdtLocked = ethers.formatUnits(usdtBalance, usdtDecimals);
          
          console.log(`💰 BSC Source Contract USDT Balance: ${balances.usdtLocked} USDT`);
        } catch (error) {
          console.error('❌ Error fetching BSC source contract USDT balance:', error);
        }
      }

      // Xphere Source Contract: Get USDX balance
      const xphereSourceAddress = process.env.REACT_APP_XPHERE_SOURCE_BRIDGE;
      const xphereUsdxAddress = process.env.REACT_APP_USDX_ADDRESS;
      
      if (xphereSourceAddress && xphereUsdxAddress) {
        try {
          // Create Xphere public client
          const xphereClient = createPublicClient({
            chain: {
              id: 20250217,
              name: 'Xphere Mainnet',
              network: 'xphere',
              nativeCurrency: { name: 'XPT', symbol: 'XPT', decimals: 18 },
              rpcUrls: {
                default: { http: ['https://xp-mainnet.rpc.xplorium.xyz'] },
                public: { http: ['https://xp-mainnet.rpc.xplorium.xyz'] }
              },
              blockExplorers: {
                default: { name: 'Xphere Explorer', url: 'https://explorer.xplorium.xyz' }
              }
            },
            transport: http()
          });

          // Get USDX balance of Xphere source contract
          const usdxBalance = await xphereClient.readContract({
            address: xphereUsdxAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [xphereSourceAddress]
          });

          const usdxDecimals = parseInt(process.env.REACT_APP_USDX_DECIMALS) || 6;
          balances.usdxLocked = ethers.formatUnits(usdxBalance, usdxDecimals);
          
          console.log(`💰 Xphere Source Contract USDX Balance: ${balances.usdxLocked} USDX`);
        } catch (error) {
          console.error('❌ Error fetching Xphere source contract USDX balance:', error);
        }
      }

      // BSC Source Contract: Get RWA Nova balance
      const rwaNovaBscAddress = process.env.REACT_APP_RWA_NOVA_BSC_ADDRESS || '0x49A756e34A5A0a7FDBFCce73d10228ee2C85CA8b';
      const rwaNovaXphereAddress = process.env.REACT_APP_RWA_NOVA_XPHERE_ADDRESS || '0x0DFfFad57Bc67701244B1bdA37E830eeadb0d69F';
      const rwaNovaDecimals = parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18');
      
      console.log('🔍 Fetching RWA Nova balances...', {
        bscSourceAddress,
        rwaNovaBscAddress,
        xphereSourceAddress,
        rwaNovaXphereAddress
      });
      
      // Fetch BSC RWA Nova balance
      if (bscSourceAddress && rwaNovaBscAddress) {
        try {
          console.log('🔄 Fetching BSC RWA Nova balance...');
          const bscClient = createPublicClient({
            chain: {
              id: 56,
              name: 'BSC Mainnet',
              network: 'bsc',
              nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
              rpcUrls: {
                default: { http: ['https://api-bsc-mainnet-full.n.dwellir.com/c6fd7c04-24d6-4cb1-881b-c3c3a43ba755'] },
                public: { http: ['https://api-bsc-mainnet-full.n.dwellir.com/c6fd7c04-24d6-4cb1-881b-c3c3a43ba755'] }
              },
              blockExplorers: {
                default: { name: 'BSCScan', url: 'https://bscscan.com' }
              }
            },
            transport: http()
          });

          const rwaNovaBscBalance = await bscClient.readContract({
            address: rwaNovaBscAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [bscSourceAddress]
          });

          const rwaNovaBscLocked = ethers.formatUnits(rwaNovaBscBalance, rwaNovaDecimals);
          balances.rwaNovaBscLocked = rwaNovaBscLocked;
          console.log(`✅ BSC RWA Nova Locked: ${rwaNovaBscLocked}`);
        } catch (error) {
          console.error('❌ Error fetching BSC RWA Nova locked balance:', error);
        }
      } else {
        console.log('⚠️ Skipping BSC RWA Nova - missing addresses:', { bscSourceAddress, rwaNovaBscAddress });
      }
      
      // Fetch Xphere RWA Nova balance
      if (xphereSourceAddress && rwaNovaXphereAddress) {
        try {
          console.log('🔄 Fetching Xphere RWA Nova balance...');
          const xphereClient = createPublicClient({
            chain: {
              id: 20250217,
              name: 'Xphere Mainnet',
              network: 'xphere',
              nativeCurrency: { name: 'XPT', symbol: 'XPT', decimals: 18 },
              rpcUrls: {
                default: { http: ['https://xp-mainnet.rpc.xplorium.xyz'] },
                public: { http: ['https://xp-mainnet.rpc.xplorium.xyz'] }
              },
              blockExplorers: {
                default: { name: 'Xphere Explorer', url: 'https://explorer.xplorium.xyz' }
              }
            },
            transport: http()
          });

          const rwaNovaXphereBalance = await xphereClient.readContract({
            address: rwaNovaXphereAddress,
            abi: ERC20_ABI,
            functionName: 'balanceOf',
            args: [xphereSourceAddress]
          });

          const rwaNovaXphereLocked = ethers.formatUnits(rwaNovaXphereBalance, rwaNovaDecimals);
          balances.rwaNovaXphereLocked = rwaNovaXphereLocked;
          console.log(`✅ Xphere RWA Nova Locked: ${rwaNovaXphereLocked}`);
        } catch (error) {
          console.error('❌ Error fetching Xphere RWA Nova locked balance:', error);
        }
      } else {
        console.log('⚠️ Skipping Xphere RWA Nova - missing addresses:', { xphereSourceAddress, rwaNovaXphereAddress });
      }
      
      console.log(`💰 RWA Nova Locked Summary: BSC: ${balances.rwaNovaBscLocked}, XPhere: ${balances.rwaNovaXphereLocked}`);

      return balances;
    } catch (error) {
      console.error('❌ Error fetching source contract balances:', error);
      return { usdtLocked: '0', usdxLocked: '0', rwaNovaBscLocked: '0', rwaNovaXphereLocked: '0' };
    }
  }, [ERC20_ABI]);

  // Fallback function to calculate metrics from bridge history
  const calculateMetricsFromHistory = useCallback((history) => {
    const metrics = {
      totalUSDTLocked: '0',
      totalUSDXLocked: '0',
      totalUSDTReserved: '0',
      totalUSDXReserved: '0',
      totalFeesCollected: '0',
      bscBridgeFees: '0',
      xphereBridgeFees: '0',
      rwaNovaBscFees: '0',
      rwaNovaXphereFees: '0'
    };

    try {
      let usdtLocked = 0;
      let usdxLocked = 0;
      let bscFees = 0; // USDT fees on BSC
      let xphereFees = 0; // USDX fees on Xphere
      let rwaNovaBscFees = 0; // RWA Nova fees on BSC
      let rwaNovaXphereFees = 0; // RWA Nova fees on Xphere

      // Get RWA Nova addresses for comparison
      const rwaNovaBscAddress = (process.env.REACT_APP_RWA_NOVA_BSC_ADDRESS || '0x49A756e34A5A0a7FDBFCce73d10228ee2C85CA8b').toLowerCase();
      const rwaNovaXphereAddress = (process.env.REACT_APP_RWA_NOVA_XPHERE_ADDRESS || '0x0DFfFad57Bc67701244B1bdA37E830eeadb0d69F').toLowerCase();
      const rwaNovaDecimals = parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18');
      const usdtDecimals = parseInt(process.env.REACT_APP_USDT_DECIMALS || '6');
      const usdxDecimals = parseInt(process.env.REACT_APP_USDX_DECIMALS || '6');

      history.forEach(request => {
        // Convert raw amount to human-readable format using token decimals
        const rawAmount = parseFloat(request.amount) || 0;
        
        const sourceNetwork = (request.source_network || request.sourceNetwork || '').toLowerCase();
        const tokenAddress = (request.token_address || request.tokenAddress || '').toLowerCase();
        
        // Determine if this is RWA Nova transaction
        const isRwaNova = tokenAddress === rwaNovaBscAddress || tokenAddress === rwaNovaXphereAddress;
        
        // Determine correct decimals based on token type
        let tokenDecimals = 6; // Default fallback
        if (isRwaNova) {
          tokenDecimals = rwaNovaDecimals;
        } else if (sourceNetwork.includes('bsc')) {
          tokenDecimals = usdtDecimals;
        } else if (sourceNetwork.includes('xphere')) {
          tokenDecimals = usdxDecimals;
        }
        
        const amount = rawAmount / Math.pow(10, tokenDecimals); // Convert from raw to actual token amount
        
        // BSC network variations
        const isBSCSource = sourceNetwork === 'bsc' || sourceNetwork === 'bscmainnet';
        
        // Xphere network variations  
        const isXphereSource = sourceNetwork === 'xphere' || sourceNetwork === 'xpheremainnet';
        
        if (request.status === 'completed') {
          // Use fee amount directly from database instead of calculating
          const rawFeeAmount = parseFloat(request.feeAmount || '0');
          const feeAmount = rawFeeAmount / Math.pow(10, tokenDecimals); // Convert from raw to actual token amount
          
          // Calculate net amount (amount after fee deduction) - this is what's actually locked
          const rawNetAmount = parseFloat(request.netAmount || request.amount) || 0;
          const netAmount = rawNetAmount / Math.pow(10, tokenDecimals); // Convert from raw to actual token amount
          
          if (isRwaNova) {
            // RWA Nova fees
            if (isBSCSource) {
              rwaNovaBscFees += feeAmount;
            } else if (isXphereSource) {
              rwaNovaXphereFees += feeAmount;
            }
          } else {
            // USDT/USDX fees
            if (isBSCSource) {
              usdtLocked += netAmount; // Use net amount (after fee) for locked calculation
              bscFees += feeAmount;
            }
            
            if (isXphereSource) {
              usdxLocked += netAmount; // Use net amount (after fee) for locked calculation
              xphereFees += feeAmount;
            }
          }
        }
        
        // Note: Reserves are fetched directly from contracts, not calculated from history
        // The reserves represent current available tokens in the destination contracts
      });

      metrics.totalUSDTLocked = usdtLocked.toFixed(3);
      metrics.totalUSDXLocked = usdxLocked.toFixed(3);
      // Store fees as human-readable amounts (no conversion needed)
      metrics.bscBridgeFees = bscFees.toFixed(6); // Store as human-readable USDT amount
      metrics.xphereBridgeFees = xphereFees.toFixed(6); // Store as human-readable USDX amount
      metrics.rwaNovaBscFees = rwaNovaBscFees.toFixed(6); // Store as human-readable RWA Nova amount
      metrics.rwaNovaXphereFees = rwaNovaXphereFees.toFixed(6); // Store as human-readable RWA Nova amount
      metrics.totalFeesCollected = (bscFees + xphereFees + rwaNovaBscFees + rwaNovaXphereFees).toFixed(6); // Sum of all fees
      // Note: totalUSDTReserved and totalUSDXReserved are set to '0' here
      // They will be overridden by actual contract values in loadAnalyticsMetrics
      
      console.log('📊 Final calculated metrics from history:', {
        usdtLocked: usdtLocked,
        usdxLocked: usdxLocked,
        bscFees: bscFees,
        xphereFees: xphereFees,
        rwaNovaBscFees: rwaNovaBscFees,
        rwaNovaXphereFees: rwaNovaXphereFees,
        totalUSDTLocked: metrics.totalUSDTLocked,
        totalUSDXLocked: metrics.totalUSDXLocked,
        bscBridgeFees: metrics.bscBridgeFees,
        xphereBridgeFees: metrics.xphereBridgeFees,
        rwaNovaBscFees: metrics.rwaNovaBscFees,
        rwaNovaXphereFees: metrics.rwaNovaXphereFees,
        totalFeesCollected: metrics.totalFeesCollected,
        totalUSDTReserved: metrics.totalUSDTReserved,
        totalUSDXReserved: metrics.totalUSDXReserved
      });
    } catch (error) {
      console.log('❌ Error calculating fallback metrics:', error.message);
    }

    return metrics;
  }, []);

  // Load analytics metrics from contracts
  const loadAnalyticsMetrics = useCallback(async () => {
    try {
      setAnalyticsMetrics(prev => ({ ...prev, isLoading: true }));

      const metrics = {
        totalUSDTLocked: '0',
        totalUSDXLocked: '0',
        rwaNovaBscLocked: '0',
        rwaNovaXphereLocked: '0',
        totalUSDTReserved: '0',
        totalUSDXReserved: '0',
        rwaNovaBscReserved: '0',
        rwaNovaXphereReserved: '0',
        totalFeesCollected: '0',
        xphereBridgeFees: '0',
        bscBridgeFees: '0',
        rwaNovaBscFees: '0',
        rwaNovaXphereFees: '0'
      };

      console.log('🔍 Loading analytics metrics from contracts...');

      // Get actual token balances from source contracts (real locked amounts)
      console.log('🔄 Fetching actual locked amounts from source contracts...');
      const contractBalances = await fetchSourceContractBalances();
      metrics.totalUSDTLocked = contractBalances.usdtLocked;
      metrics.totalUSDXLocked = contractBalances.usdxLocked;
      metrics.rwaNovaBscLocked = contractBalances.rwaNovaBscLocked || '0';
      metrics.rwaNovaXphereLocked = contractBalances.rwaNovaXphereLocked || '0';
      
      console.log('📊 Actual locked amounts from contracts:', {
        totalUSDTLocked: metrics.totalUSDTLocked,
        totalUSDXLocked: metrics.totalUSDXLocked,
        rwaNovaBscLocked: metrics.rwaNovaBscLocked,
        rwaNovaXphereLocked: metrics.rwaNovaXphereLocked
      });

      // Calculate fees from ALL completed transactions (not just paginated results)
      console.log('🔄 Calculating fees from ALL completed transactions...');
      const allCompletedTransactions = await loadAllCompletedTransactionsForFees();
      if (allCompletedTransactions.length > 0) {
        const feeMetrics = calculateMetricsFromHistory(allCompletedTransactions);
        metrics.bscBridgeFees = feeMetrics.bscBridgeFees;
        metrics.xphereBridgeFees = feeMetrics.xphereBridgeFees;
        metrics.rwaNovaBscFees = feeMetrics.rwaNovaBscFees;
        metrics.rwaNovaXphereFees = feeMetrics.rwaNovaXphereFees;
        metrics.totalFeesCollected = feeMetrics.totalFeesCollected;
        
        console.log('📊 Total locked amounts and fees calculated from history:', {
          totalUSDTLocked: metrics.totalUSDTLocked,
          totalUSDXLocked: metrics.totalUSDXLocked,
          bscBridgeFees: metrics.bscBridgeFees,
          xphereBridgeFees: metrics.xphereBridgeFees,
          totalFeesCollected: metrics.totalFeesCollected
        });
      } else {
        console.log('⚠️ No bridge history available, locked amounts will remain 0');
      }

      // Fetch actual analytics counts for dashboard
      console.log('🔄 Fetching actual analytics counts...');
      const counts = await getActualAnalyticsCounts();
      setActualCounts(counts);

      // Fetch reserves from destination contracts
      try {
        console.log('🔍 Fetching token reserves from destination contracts...');
        
      const bscChainId = '56';
      const xphereChainId = '20250217';
      
        // Get BSC destination contract and fetch USDT reserves
        try {
          const bscDestContract = getDestinationContractReadOnly(bscChainId);
          const usdtAddress = CONTRACT_ADDRESSES[bscChainId]?.usdt;
          
          if (usdtAddress) {
            console.log('🔍 Fetching USDT reserves from BSC destination...');
            const usdtReserves = await getTokenReserve(bscDestContract, usdtAddress);
            
            // Get USDT decimals dynamically
            let usdtDecimals = parseInt(process.env.REACT_APP_USDT_DECIMALS); // fallback
            try {
              const usdtContract = new ethers.Contract(usdtAddress, ERC20_ABI, bscDestContract.runner);
              usdtDecimals = await usdtContract.decimals();
            } catch (error) {
              console.warn('Could not get USDT decimals, using fallback:', error.message);
            }
            
            const usdtReservesFormatted = ethers.formatUnits(usdtReserves, usdtDecimals);
            metrics.totalUSDTReserved = parseFloat(usdtReservesFormatted).toFixed(3);
            console.log(`✅ USDT Reserves: ${metrics.totalUSDTReserved}`);
        }
      } catch (error) {
          console.log('❌ Could not get USDT reserves from BSC destination:', error.message);
        }

        // Get Xphere destination contract and fetch USDX reserves
        try {
          const xphereDestContract = getDestinationContractReadOnly(xphereChainId);
          const usdxAddress = CONTRACT_ADDRESSES[xphereChainId]?.usdx;
          
          if (usdxAddress) {
            console.log('🔍 Fetching USDX reserves from Xphere destination...');
            const usdxReserves = await getTokenReserve(xphereDestContract, usdxAddress);
            
            // Get USDX decimals dynamically
            let usdxDecimals = parseInt(process.env.REACT_APP_USDX_DECIMALS); // fallback
            try {
              const usdxContract = new ethers.Contract(usdxAddress, ERC20_ABI, xphereDestContract.runner);
              usdxDecimals = await usdxContract.decimals();
            } catch (error) {
              console.warn('Could not get USDX decimals, using fallback:', error.message);
            }
            
            const usdxReservesFormatted = ethers.formatUnits(usdxReserves, usdxDecimals);
            metrics.totalUSDXReserved = parseFloat(usdxReservesFormatted).toFixed(3);
            console.log(`✅ USDX Reserves: ${metrics.totalUSDXReserved}`);
        }
      } catch (error) {
          console.log('❌ Could not get USDX reserves from Xphere destination:', error.message);
        }

        // Get RWA Nova reserves from both destination contracts
        try {
          const rwaNovaBscAddress = process.env.REACT_APP_RWA_NOVA_BSC_ADDRESS || '0x49A756e34A5A0a7FDBFCce73d10228ee2C85CA8b';
          const rwaNovaXphereAddress = process.env.REACT_APP_RWA_NOVA_XPHERE_ADDRESS || '0x0DFfFad57Bc67701244B1bdA37E830eeadb0d69F';
          const rwaNovaDecimals = parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18');
          
          let rwaNovaBscReserved = '0';
          let rwaNovaXphereReserved = '0';
          
          // Get RWA Nova reserves from BSC destination
          try {
            const bscDestContract = getDestinationContractReadOnly(bscChainId);
            console.log('🔍 Fetching RWA Nova reserves from BSC destination...');
            const rwaNovaBscReserves = await getTokenReserve(bscDestContract, rwaNovaBscAddress);
            rwaNovaBscReserved = ethers.formatUnits(rwaNovaBscReserves, rwaNovaDecimals);
            console.log(`✅ RWA Nova BSC Reserves: ${rwaNovaBscReserved}`);
          } catch (error) {
            console.log('❌ Could not get RWA Nova reserves from BSC destination:', error.message);
          }
          
          // Get RWA Nova reserves from XPhere destination
          try {
            const xphereDestContract = getDestinationContractReadOnly(xphereChainId);
            console.log('🔍 Fetching RWA Nova reserves from XPhere destination...');
            const rwaNovaXphereReserves = await getTokenReserve(xphereDestContract, rwaNovaXphereAddress);
            rwaNovaXphereReserved = ethers.formatUnits(rwaNovaXphereReserves, rwaNovaDecimals);
            console.log(`✅ RWA Nova XPhere Reserves: ${rwaNovaXphereReserved}`);
          } catch (error) {
            console.log('❌ Could not get RWA Nova reserves from XPhere destination:', error.message);
          }
          
          // Store RWA Nova reserves separately for each chain
          metrics.rwaNovaBscReserved = rwaNovaBscReserved;
          metrics.rwaNovaXphereReserved = rwaNovaXphereReserved;
          console.log(`✅ RWA Nova Reserves: BSC: ${rwaNovaBscReserved}, XPhere: ${rwaNovaXphereReserved}`);
        } catch (error) {
          console.log('❌ Error fetching RWA Nova reserves:', error.message);
        }

      } catch (error) {
        console.log('❌ Error fetching reserves from contracts:', error.message);
      }







      
      setAnalyticsMetrics(prev => ({ ...prev, ...metrics, isLoading: false }));
      
    } catch (error) {
      console.error('❌ Error loading analytics metrics:', error);
      setAnalyticsMetrics(prev => ({ ...prev, isLoading: false }));
    }
  }, [calculateMetricsFromHistory, loadAllCompletedTransactionsForFees, getActualAnalyticsCounts, fetchSourceContractBalances, getDestinationContractReadOnly, getTokenReserve, ERC20_ABI]);

  // Auto-refresh analytics when filters change
  useEffect(() => {
    if (analyticsFilter && Object.keys(analyticsFilter).length > 0) {
      loadAllBridgeHistory(analyticsFilter);
    }
  }, [analyticsFilter, loadAllBridgeHistory]);

  // Load analytics metrics when component mounts
  useEffect(() => {
      loadAnalyticsMetrics();
  }, [loadAnalyticsMetrics]);

  // Smart refresh: Check if there are new analytics data since last refresh
  const checkForNewAnalyticsData = useCallback(async (lastRefreshTime) => {
    try {
      console.log('🔍 Checking for new analytics data since:', new Date(lastRefreshTime).toISOString());
      
      // Get the latest transaction timestamp from our current data
      if (allBridgeHistory.length === 0) {
        console.log('🔍 No existing data, consider this as "new data"');
        return true; // No existing data, so refresh to get initial data
      }
      
      // Find the most recent transaction timestamp
      const latestTransaction = allBridgeHistory.reduce((latest, current) => {
        const currentTime = new Date(current.created_at || current.createdAt || 0).getTime();
        const latestTime = new Date(latest.created_at || latest.createdAt || 0).getTime();
        return currentTime > latestTime ? current : latest;
      });
      
      const latestTransactionTime = new Date(latestTransaction.created_at || latestTransaction.createdAt || 0).getTime();
      console.log('🔍 Latest transaction in current data:', new Date(latestTransactionTime).toISOString());
      
      // Check if there are any new transactions since our last refresh
      // We'll make a lightweight API call to check for newer transactions
      try {
        const response = await bridgeService.makeRequest('/api/bridge-requests?limit=1&sort=created_at:desc');
        
        if (response && response.ok) {
          const data = await response.json();
          
          if (data && data.length > 0) {
            const newestTransactionTime = new Date(data[0].created_at || data[0].createdAt || 0).getTime();
            console.log('🔍 Newest transaction from API:', new Date(newestTransactionTime).toISOString());
            
            // Check if the newest transaction is newer than our latest
            if (newestTransactionTime > latestTransactionTime) {
              console.log('✅ New data detected: API has newer transactions');
              return true;
            } else {
              console.log('✅ No new data: API has same or older transactions');
              return false;
            }
          } else {
            console.log('🔍 API returned no transactions, no new data');
            return false;
          }
        } else {
          console.log('❌ API check failed, assuming no new data to avoid unnecessary refreshes');
          return false;
        }
      } catch (apiError) {
        console.log('❌ API check error, assuming no new data:', apiError.message);
        return false;
      }
    } catch (error) {
      console.error('❌ Error checking for new analytics data:', error);
      return false; // Default to no new data on error
    }
  }, [allBridgeHistory]);


  // Helper function to format numbers with comma separators
  const formatNumberWithCommas = useCallback((num) => {
    if (!num && num !== 0) return '0';
    return Number(num).toLocaleString('en-US');
  }, []);

  // Helper function to format amount for display with commas
  const formatAmount = useCallback((amount, tokenType) => {
    if (!amount) return '0.000';
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '0.000';
    
    // The amount passed to toast is already in human-readable format (e.g., "0.1")
    // Format with 3 decimal places and add commas if >= 1000
    const formatted = numAmount.toFixed(3);
    return formatNumberWithCommas(formatted);
  }, [formatNumberWithCommas]);

  // Helper function to format token amounts with commas (for already formatted amounts)
  const formatTokenAmount = useCallback((amount) => {
    if (!amount || amount === '0') return '0.000';
    
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '0.000';
    
    // Format with 3 decimal places and add commas for thousands
    const formatted = numAmount.toFixed(3);
    return formatNumberWithCommas(formatted);
  }, [formatNumberWithCommas]);

  // Helper function to get token info from address
  const getTokenInfoFromAddress = useCallback((tokenAddress, network) => {
    if (!tokenAddress) return null;
    
    const addr = tokenAddress.toLowerCase();
    const rwaNovaBsc = (process.env.REACT_APP_RWA_NOVA_BSC_ADDRESS || '0x49A756e34A5A0a7FDBFCce73d10228ee2C85CA8b').toLowerCase();
    const rwaNovaXphere = (process.env.REACT_APP_RWA_NOVA_XPHERE_ADDRESS || '0x0DFfFad57Bc67701244B1bdA37E830eeadb0d69F').toLowerCase();
    const usdtAddress = process.env.REACT_APP_USDT_ADDRESS?.toLowerCase();
    const usdxAddress = process.env.REACT_APP_USDX_ADDRESS?.toLowerCase();
    
    if (addr === rwaNovaBsc || addr === rwaNovaXphere) {
      return {
        symbol: 'RWA Nova',
        name: process.env.REACT_APP_RWA_NOVA_NAME || 'RWA Nova',
        decimals: parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18')
      };
    } else if (addr === usdtAddress) {
      return {
        symbol: 'USDT',
        name: process.env.REACT_APP_USDT_NAME || 'Tether USD',
        decimals: parseInt(process.env.REACT_APP_USDT_DECIMALS || '6')
      };
    } else if (addr === usdxAddress) {
      return {
        symbol: 'USDX',
        name: process.env.REACT_APP_USDX_NAME || 'USDX',
        decimals: parseInt(process.env.REACT_APP_USDX_DECIMALS || '6')
      };
    }
    
    // Fallback: determine by network if address not found
    if (network === 'bscMainnet') {
      return {
        symbol: 'USDT',
        name: process.env.REACT_APP_USDT_NAME || 'Tether USD',
        decimals: parseInt(process.env.REACT_APP_USDT_DECIMALS || '6')
      };
    } else {
      return {
        symbol: 'USDX',
        name: process.env.REACT_APP_USDX_NAME || 'USDX',
        decimals: parseInt(process.env.REACT_APP_USDX_DECIMALS || '6')
      };
    }
  }, []);

  // Helper function to format bridge amount with receive amount (bridgeAmount -> receiveAmount)
  const formatBridgeAmountWithReceive = useCallback((request) => {
    if (!request) return '0.000';
    
    // Get the original bridge amount (what user sent)
    const rawBridgeAmount = parseFloat(request.amount || '0');
    
    // Get the receive amount (what user actually receives after fees)
    const rawReceiveAmount = parseFloat(request.netAmount || request.amount || '0');
    
    // Determine token symbols and decimals based on token address or network
    const sourceTokenInfo = getTokenInfoFromAddress(request.tokenAddress, request.sourceNetwork);
    const sourceToken = sourceTokenInfo?.symbol || (request.sourceNetwork === 'bscMainnet' ? 'USDT' : 'USDX');
    const sourceDecimals = sourceTokenInfo?.decimals || (sourceToken === 'USDT' ? 
      (parseInt(process.env.REACT_APP_USDT_DECIMALS) || 6) : 
      (parseInt(process.env.REACT_APP_USDX_DECIMALS) || 6));
    
    // For destination token, we need to check if there's a destination token address
    // Otherwise, infer from destination network or source token type
    let destTokenInfo = getTokenInfoFromAddress(request.destTokenAddress, request.destNetwork);
    
    // If destTokenInfo is not available, check if source token is RWA Nova
    // If so, destination should also be RWA Nova (same token, different chain)
    if (!destTokenInfo && sourceTokenInfo && sourceTokenInfo.symbol === 'RWA Nova') {
      // Source is RWA Nova, so destination should also be RWA Nova
      const rwaNovaBsc = (process.env.REACT_APP_RWA_NOVA_BSC_ADDRESS || '0x49A756e34A5A0a7FDBFCce73d10228ee2C85CA8b').toLowerCase();
      const rwaNovaXphere = (process.env.REACT_APP_RWA_NOVA_XPHERE_ADDRESS || '0x0DFfFad57Bc67701244B1bdA37E830eeadb0d69F').toLowerCase();
      
      // Determine destination RWA Nova address based on destination network
      const destRwaNovaAddress = request.destNetwork === 'bscMainnet' ? rwaNovaBsc : rwaNovaXphere;
      destTokenInfo = getTokenInfoFromAddress(destRwaNovaAddress, request.destNetwork);
    }
    
    const destToken = destTokenInfo?.symbol || (request.destNetwork === 'bscMainnet' ? 'USDT' : 'USDX');
    const destDecimals = destTokenInfo?.decimals || (destToken === 'USDT' ? 
      (parseInt(process.env.REACT_APP_USDT_DECIMALS) || 6) : 
      (parseInt(process.env.REACT_APP_USDX_DECIMALS) || 6));
    
    // Proper decimal conversion based on token type
    let bridgeAmount, receiveAmount;
    
    // Convert bridge amount from source token raw units to human-readable
    bridgeAmount = rawBridgeAmount / Math.pow(10, sourceDecimals);
    
    // For net_amount: it's stored in source token raw units, but represents destination token amount
    // We need to convert it to destination token format for display
    if (sourceDecimals !== destDecimals) {
      // Cross-chain conversion needed
      if (sourceDecimals > destDecimals) {
        // Converting from higher to lower decimals (e.g., 18 → 6)
        // net_amount is in 18 decimals, represents 6 decimal amount
        receiveAmount = rawReceiveAmount / Math.pow(10, sourceDecimals - destDecimals) / Math.pow(10, destDecimals);
      } else {
        // Converting from lower to higher decimals (e.g., 6 → 18)  
        // net_amount is in 6 decimals, represents 18 decimal amount
        receiveAmount = rawReceiveAmount * Math.pow(10, destDecimals - sourceDecimals) / Math.pow(10, destDecimals);
      }
    } else {
      // Same decimals, direct conversion
      receiveAmount = rawReceiveAmount / Math.pow(10, destDecimals);
    }
    
    // Debug logging for large amounts to help identify any future issues
    if (rawBridgeAmount > 1000000000 || rawReceiveAmount > 1000000000) {
      // console.log('🔍 Large amount conversion:', {
      //   requestId: request.requestId || request.id,
      //   sourceToken,
      //   destToken,
      //   rawBridgeAmount,
      //   rawReceiveAmount,
      //   bridgeAmount,
      //   receiveAmount
      // });
    }
    
    // Format amounts with commas and proper decimal places
    const formattedBridgeAmount = formatTokenAmount(bridgeAmount.toString());
    const formattedReceiveAmount = formatTokenAmount(receiveAmount.toString());
    
    // Return in "bridgeAmount -> receiveAmount" format
    return `${formattedBridgeAmount} ${sourceToken} → ${formattedReceiveAmount} ${destToken}`;
  }, [formatTokenAmount, getTokenInfoFromAddress]);

  // Helper function to format fee amounts with proper token decimal handling and commas
  const formatFeeAmount = useCallback((amount, tokenDecimals = 6) => {
    if (!amount || amount === '0') return '0.000';
    
    // Amount is already in human-readable format, no conversion needed
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return '0.000';
    
    // Format with appropriate decimal places based on token type
    const decimalPlaces = tokenDecimals === process.env.REACT_APP_USDT_DECIMALS ? 3 : 3; // Standard precision for USDT (6 decimals)
    const formatted = numAmount.toFixed(decimalPlaces);
    
    // Add commas for thousands separator
    return formatNumberWithCommas(formatted);
  }, [formatNumberWithCommas]);

  // Helper function to format total fee amount (sum of USDT and USDX) with commas
  const formatTotalFeeAmount = useCallback((bscAmount, xphereAmount, bscDecimals = 6, xphereDecimals = 6) => {
    const bscNum = parseFloat(bscAmount) || 0;
    const xphereNum = parseFloat(xphereAmount) || 0;
    
    // Amounts are already in human-readable format, no conversion needed
    // Since both USDT and USDX are dollar-pegged, we can sum them directly
    const totalUSD = bscNum + xphereNum;
    
    // Format with 3 decimal places and add commas for large numbers
    const formatted = totalUSD.toFixed(3);
    
    // Add commas for thousands separator
    return formatNumberWithCommas(formatted);
  }, [formatNumberWithCommas]);

  const startBridgeStatusTracking = useCallback((requestId) => {
    if (!requestId) return;
    
    console.log(`🔍 Starting bridge status tracking for request ${requestId}`);
    console.log(`⏰ Current time: ${new Date().toISOString()}`);
    
    // Start status polling immediately (no delay)
    console.log(`🚀 Starting status polling for request ${requestId} immediately`);
    bridgeService.startStatusPolling(requestId, (status) => {
      console.log(`📊 Bridge status update for request ${requestId}:`, status);
      
      // Update toast status instead of local bridgeRequests state
      updateToast(requestId, status.status);
      
      // Keep bridgeStatuses for compatibility
      setBridgeStatuses(prev => ({
        ...prev,
        [requestId]: status
      }));
      
      // If completed, failed, or skipped, refresh the analytics
      if (status.status === 'completed' || status.status === 'failed' || status.status === 'skipped') {
                  if (wagmiAccount) {
            // Refresh analytics
                      loadAllBridgeHistory(analyticsFilter || {});
          }
        }
      });
  }, [loadAllBridgeHistory, wagmiAccount, updateToast, analyticsFilter]);



  // Fetch bridge configuration from contract
  const fetchBridgeConfig = useCallback(async () => {
    if (!publicClient || !chainId) return;

    try {
      const sourceContractAddress = CONTRACT_ADDRESSES[chainId]?.sourceBridge;
      if (!sourceContractAddress) return;

      console.log('🔧 Fetching bridge configuration from contract...');
      
      // Fetch bridge fee and defaults
      const [bridgeFee, defaultMinAmount, defaultMaxAmount] = await Promise.all([
        publicClient.readContract({
          address: sourceContractAddress,
          abi: SOURCE_ABI,
          functionName: 'bridgeFee',
        }),
        publicClient.readContract({
          address: sourceContractAddress,
          abi: SOURCE_ABI,
          functionName: 'defaultMinAmount',
        }),
        publicClient.readContract({
          address: sourceContractAddress,
          abi: SOURCE_ABI,
          functionName: 'defaultMaxAmount',
        })
      ]);

      // BASIS_POINTS is a constant in the contract (10000), not a public function
      const basisPoints = 10000;
      
      // Convert fee from basis points to percentage
      const feePercentage = (Number(bridgeFee) / basisPoints) * 100;
      
      // Convert amounts from wei to human readable format using environment variable
      const tokenDecimals = parseInt(process.env.REACT_APP_USDT_DECIMALS);
      const minAmount = ethers.formatUnits(defaultMinAmount, tokenDecimals);
      const maxAmount = ethers.formatUnits(defaultMaxAmount, tokenDecimals);

      console.log('📊 Bridge configuration fetched:', {
        fee: `${feePercentage.toFixed(1)}%`,
        minAmount,
        maxAmount
      });

      // Set a reasonable daily limit (10x max amount as default) if token-specific info is unavailable
      const dailyLimit = (parseFloat(maxAmount) * 10).toFixed(0);

      setBridgeConfig(prev => ({
        fee: `${feePercentage.toFixed(1)}%`,
        minAmount: parseFloat(minAmount).toFixed(3),
        maxAmount: parseFloat(maxAmount).toFixed(0),
        dailyLimit: dailyLimit,
        processingTime: '~30-60 seconds'
      }));

      // If token is selected, fetch token-specific configuration
      if (token) {
        const selectedTokenInfo = supportedTokens.find(t => t.symbol === token);
        const tokenAddress = selectedTokenInfo?.address || CONTRACT_ADDRESSES[chainId]?.[token];
        if (tokenAddress) {
          console.log('🔧 Fetching token-specific configuration...');
          
          try {
            const [tokenInfo, remainingDaily] = await Promise.all([
              publicClient.readContract({
                address: sourceContractAddress,
                abi: SOURCE_ABI,
                functionName: 'getTokenInfo',
                args: [tokenAddress]
              }),
              publicClient.readContract({
                address: sourceContractAddress,
                abi: SOURCE_ABI,
                functionName: 'getRemainingDailyLimit',
                args: [tokenAddress]
              })
            ]);

            console.log('📊 Raw token info from contract:', tokenInfo);
            console.log('📊 Raw remaining daily limit:', remainingDaily);

            // Check if tokenInfo is iterable (array) or an object
            let minAmt, maxAmt, dailyLim, dailyUse;
            
            if (Array.isArray(tokenInfo)) {
              // tokenInfo is an array with 7 values: [isSupported, decimals, minAmount, maxAmount, dailyLimit, dailyUsage, totalBridged]
              const [, , tokenMinAmount, tokenMaxAmount, tokenDailyLimit, tokenDailyUsage] = tokenInfo;
              minAmt = tokenMinAmount;
              maxAmt = tokenMaxAmount;
              dailyLim = tokenDailyLimit;
              dailyUse = tokenDailyUsage || 0n;
            } else if (tokenInfo && typeof tokenInfo === 'object') {
              // If it's an object, access properties directly
              minAmt = tokenInfo.minAmount || tokenInfo[0];
              maxAmt = tokenInfo.maxAmount || tokenInfo[1];
              dailyLim = tokenInfo.dailyLimit || tokenInfo[2];
              dailyUse = tokenInfo.dailyUsage || tokenInfo[3] || 0n;
            } else {
              console.error('❌ Unexpected tokenInfo format:', tokenInfo);
              throw new Error('Invalid tokenInfo format from contract');
            }

            const remaining = remainingDaily;

            console.log('📊 Raw tokenInfo response:', tokenInfo);
            console.log('📊 TokenInfo type:', typeof tokenInfo);
            console.log('📊 Is array:', Array.isArray(tokenInfo));
            console.log('📊 TokenInfo keys:', Object.keys(tokenInfo || {}));
            console.log('📊 Processed token-specific configuration:', {
              minAmt, maxAmt, dailyLim, dailyUse, remaining
            });

            // Get token decimals from tokenInfo response
            let tokenDecimals = 6; // Default fallback
            if (Array.isArray(tokenInfo)) {
              const [, decimals] = tokenInfo;
              tokenDecimals = Number(decimals);
            } else if (tokenInfo && typeof tokenInfo === 'object') {
              tokenDecimals = Number(tokenInfo.decimals) || parseInt(process.env.REACT_APP_USDT_DECIMALS);
            }
            
            // Convert from wei to human readable format using actual token decimals
            const minAmountFormatted = ethers.formatUnits(minAmt, tokenDecimals);
            const maxAmountFormatted = ethers.formatUnits(maxAmt, tokenDecimals);
            const dailyLimitFormatted = ethers.formatUnits(dailyLim, tokenDecimals);
            const dailyUsedFormatted = dailyUse ? ethers.formatUnits(dailyUse, tokenDecimals) : '0';
            const remainingFormatted = ethers.formatUnits(remaining, tokenDecimals);

            setBridgeConfig(prev => ({
              ...prev,
              minAmount: parseFloat(minAmountFormatted).toFixed(3),
              maxAmount: parseFloat(maxAmountFormatted).toFixed(0),
              dailyLimit: parseFloat(dailyLimitFormatted).toFixed(0),
              dailyUsed: parseFloat(dailyUsedFormatted).toFixed(6),
              remainingDaily: parseFloat(remainingFormatted).toFixed(6)
            }));
          } catch (error) {
            console.warn('⚠️ Could not fetch token-specific configuration:', error);
            // Keep the default configuration if token-specific fetch fails
          }
        }
      }
    } catch (error) {
      console.error('❌ Failed to fetch bridge configuration:', error);
    }
  }, [publicClient, chainId, token, SOURCE_ABI]);

  // New function to handle approval first, then show confirmation dialog
  const handleInitiateBridge = useCallback(async () => {
    if (!amount || !recipient) {
      showMessage('error', 'Validation Error', 'Please fill all fields', 5000);
      return;
    }

    // Check wallet connection
    if (!wagmiAccount) {
      showMessage('error', 'Wallet Error', 'Please connect your wallet', 5000);
      return;
    }

    // Check bridge availability
    if (!bridgeAvailability.isAvailable) {
      showMessage('error', 'Bridge Unavailable', bridgeAvailability.reason || 'Bridge is not available for this transaction', 8000);
      return;
    }

    // Validate recipient address
    if (!ethers.isAddress(recipient)) {
      showMessage('error', 'Validation Error', 'Invalid recipient address', 5000);
      return;
    }

    // Check if source and destination are different
    if (sourceChain === destChain) {
      showMessage('error', 'Validation Error', 'Source and destination chains must be different', 5000);
      return;
    }

    setLoading(true);
    
    try {
      // Get contract addresses
      const requiredChainId = getChainIdFromNetwork(sourceChain);
      if (!requiredChainId) {
        showMessage('error', 'Invalid Source Chain', `${sourceChain} is not a valid network`, 5000);
        setLoading(false);
        return;
      }

      // Check if we're on the correct network
      if (chainId !== requiredChainId) {
        const targetChainName = supportedChains[sourceChain]?.name || sourceChain;
        showMessage('info', 'Switching Network', 
                    `Switching to ${targetChainName} for bridge transaction...`, 
                    5000);
        
        const switchSuccess = await switchToCorrectNetwork(requiredChainId);
        
        if (!switchSuccess) {
          setLoading(false);
          return;
        }
        
        await new Promise(resolve => setTimeout(resolve, 3000));
      }

      // Get the current chainId after potential network switch
      const currentChainId = chainId || requiredChainId;
      
      // Get token address from supportedTokens or CONTRACT_ADDRESSES
      const selectedTokenInfo = supportedTokens.find(t => t.symbol === token);
      let tokenAddress = selectedTokenInfo?.address || CONTRACT_ADDRESSES[currentChainId]?.[token];
      
      if (!tokenAddress) {
        showMessage('error', 'Token Error', `Token ${token} is not available on the current network`, 5000);
        setLoading(false);
        return;
      }
      
      const sourceContractAddress = CONTRACT_ADDRESSES[requiredChainId]?.sourceBridge;

      if (!sourceContractAddress) {
        showMessage('error', 'Contract Error', 'Source bridge contract not found', 5000);
        setLoading(false);
        return;
      }

      // Check token approval
      console.log('🔍 Checking token approval...');
      const isApproved = await checkTokenApproval(tokenAddress, sourceContractAddress, amount);
      
      if (!isApproved) {
        console.log('Token not approved, requesting approval...');
        showMessage('info', 'Approval Required', 'Please approve token spending in your wallet', 5000);
        
        try {
          await approveToken(tokenAddress, sourceContractAddress, amount);
          showMessage('success', 'Approval Successful', 'Token approved! Review transaction details to continue', 5000);
          
          // Wait a moment for approval to be processed
          await new Promise(resolve => setTimeout(resolve, 2000));
        } catch (approvalError) {
          console.error('Token approval failed:', approvalError);
          
          const isApprovalUserCancellation = approvalError.code === 'ACTION_REJECTED' ||
            (approvalError.message && (
              approvalError.message.includes('User rejected the request') ||
              approvalError.message.includes('User denied transaction') ||
              approvalError.message.includes('User rejected') ||
              approvalError.message.includes('UserRejectedRequestError')
            ));
          
          if (isApprovalUserCancellation) {
            showMessage('info', 'Approval Cancelled', 'Token approval was cancelled. Click the button again when ready.', 5000);
          } else if (approvalError.message.includes('insufficient funds')) {
            showMessage('error', 'Insufficient Funds', 'Insufficient ETH for approval transaction gas', 5000);
          } else {
            showMessage('error', 'Approval Failed', `Token approval failed: ${approvalError.message}`, 5000);
          }
          
          setLoading(false);
          return;
        }
      } else {
        console.log('✅ Token already approved');
      }

      // After approval (or if already approved), show confirmation dialog
      setLoading(false);
      setShowConfirmDialog(true);
      
    } catch (error) {
      console.error('Error in handleInitiateBridge:', error);
      showMessage('error', 'Error', error.message || 'Failed to prepare bridge transaction', 5000);
      setLoading(false);
    }
  }, [amount, recipient, wagmiAccount, chainId, token, sourceChain, destChain, bridgeAvailability, supportedChains, getChainIdFromNetwork, switchToCorrectNetwork, checkTokenApproval, approveToken, showMessage]);

  const initiateBridge = useCallback(async () => {
    if (!amount || !recipient) {
      showMessage('error', 'Validation Error', 'Please fill all fields', 5000);
      return;
    }

    // MANDATORY: Check wallet connection BEFORE network switching. 
    // If wallet is not connected, don't proceed further
    if (!wagmiAccount) {
      showMessage('error', 'Wallet Error', 'Please connect your wallet', 5000);
      return;
    }

    // Debug wallet connection 
    console.log('🔍 Wallet check:', { wagmiAccount, signer: signer ? 'Found' : 'Missing' });

    // NOTE: We'll do the detailed wallet checks AFTER the network switching logic
    // because network switching might happen when we are on the wrong chain

    // Check bridge availability before proceeding
    if (!bridgeAvailability.isAvailable) {
      showMessage('error', 'Bridge Unavailable', bridgeAvailability.reason || 'Bridge is not available for this transaction', 8000);
      return;
    }

    // Check if we're on the correct network (network checking BEFORE signer requirements)
    const requiredChainId = getChainIdFromNetwork(sourceChain);
    if (!requiredChainId) {
      showMessage('error', 'Invalid Source Chain', `${sourceChain} is not a valid network`, 5000);
      setLoading(false);
      return;
    }

    if (chainId !== requiredChainId) {
      console.log(`🔄 Network check: User on chain ${chainId}, required chain ${requiredChainId} for ${sourceChain}`);
      
      const targetChainName = supportedChains[sourceChain]?.name || sourceChain;
      
      // Show loading state while switching
      showMessage('info', 'Switching Network', 
                  `Switching to ${targetChainName} for bridge transaction...`, 
                  5000);
      
      // Automatically switch to the correct network
      const switchSuccess = await switchToCorrectNetwork(requiredChainId);
      
      if (!switchSuccess) {
        setLoading(false);
        return; // Exit if network switch failed
      }
      
      // Wait longer for network switch to complete (especially for Rabby wallet)
      await new Promise(resolve => setTimeout(resolve, 5000));
      
      // VERIFY: Double-check user is actually on the correct network now
      let currentNetworkAfterSwitch;
      
      // Try different methods to get the current chain
      try {
        if (provider && provider.getNetwork) {
          const network = await provider.getNetwork();
          currentNetworkAfterSwitch = network.chainId;
          console.log(`🔍 Network verification via provider: Chain ${currentNetworkAfterSwitch}`);
        } else {
          currentNetworkAfterSwitch = chainId;
          console.log(`🔍 Network verification via chainId hook: Chain ${currentNetworkAfterSwitch}`);
        }
      } catch (providerError) {
        console.log('Provider not available for network check, using chainId hook');
        currentNetworkAfterSwitch = chainId;
      }
      
      console.log(`🔍 Network verification: Current chain is ${currentNetworkAfterSwitch}, required is ${requiredChainId}`);
      
      // Double-check we're on the right network (including type checking for string vs number)
      const normalizedCurrent = typeof currentNetworkAfterSwitch === 'string' ? currentNetworkAfterSwitch.toString() : currentNetworkAfterSwitch;
      const normalizedRequired = typeof requiredChainId === 'string' ? requiredChainId.toString() : requiredChainId;
      
      if (normalizedCurrent !== normalizedRequired) {
        console.log(`⚠️ Network switch failed: Got ${normalizedCurrent}, expected ${normalizedRequired}...`);
        console.log(`🔄 This might be due to chainId hook taking longer to update. Attempting retry...`);
        
        // If still on wrong network after timeout, check if the switch actually happened.
        // If we are not on the required network yet, show manual switch message and don't proceed
        console.log(`⚠️ Manual network switch required for Rabby wallet`);
        console.log(`🔄 User still on chain ${normalizedCurrent}, need chain ${normalizedRequired}`);
        
        // Do not clear toasts yet - let the condition resolve manually.
        // Show an error message instead of continuing with bridge on wrong network
        showMessage('error', 'Network Switch Required', 
          `Please manually switch to ${targetChainName} in Rabby Wallet (chain ID: ${normalizedRequired}). Click the network selector in the top menu first.`, 
          15000);
        setLoading(false);
        return;
      }
      
      console.log(`✅ Successfully verified network switch to ${targetChainName}`);
    }

    // Validate amount
    const numAmount = parseFloat(amount);
    if (numAmount <= 0) {
      addToast(`error_${Date.now()}`, {
        type: 'error',
        title: 'Validation Error',
        message: 'Amount must be greater than 0',
        duration: 5000
      });
      return;
    }

    // Validate decimal places (maximum 2)
    const decimalPlaces = (amount.split('.')[1] || '').length;
    if (decimalPlaces > 2) {
      addToast(`error_${Date.now()}`, {
        type: 'error',
        title: 'Validation Error',
        message: 'Amount can have maximum 2 decimal places',
        duration: 5000
      });
      return;
    }

    // Validate against bridge configuration limits
    if (bridgeConfig.minAmount && numAmount < parseFloat(bridgeConfig.minAmount)) {
      addToast(`error_${Date.now()}`, {
        type: 'error',
        title: 'Validation Error',
        message: `Amount must be at least ${bridgeConfig.minAmount} tokens`,
        duration: 5000
      });
      return;
    }

    if (bridgeConfig.maxAmount && numAmount > parseFloat(bridgeConfig.maxAmount)) {
      addToast(`error_${Date.now()}`, {
        type: 'error',
        title: 'Validation Error',
        message: `Amount cannot exceed ${bridgeConfig.maxAmount} tokens`,
        duration: 5000
      });
      return;
    }

    // Validate recipient address
    if (!ethers.isAddress(recipient)) {
      addToast(`error_${Date.now()}`, {
        type: 'error',
        title: 'Validation Error',
        message: 'Invalid recipient address',
        duration: 5000
      });
      return;
    }

    // Check if source and destination are different
    if (sourceChain === destChain) {
      addToast(`error_${Date.now()}`, {
        type: 'error',
        title: 'Validation Error',
        message: 'Source and destination chains must be different',
        duration: 5000
      });
      return;
    }

    // Check daily limit if available
    if (bridgeConfig.dailyLimit && bridgeConfig.dailyUsed) {
      const remainingDaily = parseFloat(bridgeConfig.dailyLimit) - parseFloat(bridgeConfig.dailyUsed);
      if (numAmount > remainingDaily) {
        addToast(`error_${Date.now()}`, {
          type: 'error',
          title: 'Daily Limit Exceeded',
          message: `Amount exceeds remaining daily limit of ${remainingDaily.toFixed(6)} tokens`,
          duration: 5000
        });
        return;
      }
    }

    setLoading(true);
    
    // Request ID will be created after successful bridge transaction
    
    try {
      const destChainInfo = supportedChains[destChain];

      if (!destChainInfo) {
        throw new Error(`Destination chain ${destChain} not supported`);
      }

      // Get token address from supportedTokens or CONTRACT_ADDRESSES
      let selectedTokenInfo = supportedTokens.find(t => t.symbol === token);
      let tokenAddress = selectedTokenInfo?.address || CONTRACT_ADDRESSES[chainId]?.[token];

      // Check if current chain supports the selected token
      if (!tokenAddress) {
        // Find a chain that supports this token
        const supportedChain = Object.entries(CONTRACT_ADDRESSES).find(([chainIdKey, contracts]) => {
          const tokenInfo = supportedTokens.find(t => t.symbol === token);
          return tokenInfo?.address || (contracts && contracts[token]);
        });
        
        if (supportedChain) {
          const [supportedChainId] = supportedChain;
          const chainKey = Object.keys(supportedChains).find(key => 
            supportedChains[key].id.toString() === supportedChainId
          );
          
          if (chainKey) {
            const targetChainName = supportedChains[chainKey].name;
            const targetChainId = supportedChains[chainKey].id;
            console.log(`🔄 Token ${token} not available on current chain ${chainId}, switching to ${chainKey} (${targetChainName})...`);
            
            // Show loading state while switching
            showMessage('info', 'Switching Network',
                       `Switching to ${targetChainName} to continue bridge transaction...`,
                       5000);
            
            // Automatically switch to the correct network
            const switchSuccess = await switchToCorrectNetwork(targetChainId);
            
            if (!switchSuccess) {
              setLoading(false);
              return; // Exit if network switch failed
            }
            
            // Wait a moment for the network switch to complete
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            // Continue with the bridge transaction after successful network switch
            console.log(`✅ Successfully switched to ${targetChainName}, continuing bridge transaction...`);
            
            // Re-fetch token address after network switch (need to get updated chainId)
            const updatedChainId = await publicClient.getChainId();
            const updatedSupportedTokens = getSupportedTokens(updatedChainId);
            selectedTokenInfo = updatedSupportedTokens.find(t => t.symbol === token);
            tokenAddress = selectedTokenInfo?.address || CONTRACT_ADDRESSES[updatedChainId]?.[token];
            if (!tokenAddress) {
              throw new Error(`Token ${token} is not available on the target network`);
            }
          } else {
            throw new Error(`Token ${token} not available on any supported chain`);
          }
        } else {
          throw new Error(`Token ${token} not available on any supported chain`);
        }
      }
      
      if (!tokenAddress) {
        throw new Error(`Token ${token} is not available on the current network. Please switch to the correct network or select a different token.`);
      }

      // Final real-time check via ethers provider to determine correct chain
      let finalCurrentChain;
      try {
        if (provider && provider.getNetwork) {
          const network = await provider.getNetwork();
          finalCurrentChain = Number(network.chainId);
        } else {
          // Fallback to use window.ethereum.eth_chainId itself instead
          if (window.ethereum) {
            const chainIdHex = await window.ethereum.request({ method: 'eth_chainId' });
            finalCurrentChain = parseInt(chainIdHex, 16);
          } else {
            finalCurrentChain = chainId;
          }
        }
        console.log(`🔍 Real-time chain check via provider: ${finalCurrentChain}, required: ${requiredChainId}`);
        if (BigInt(finalCurrentChain) !== BigInt(requiredChainId)) {
          throw new Error(`Wallet is on chain ${finalCurrentChain}, but bridge requires ${requiredChainId}. Please switch to: ${supportedChains[sourceChain]?.name}`);
        }
      } catch (e) {
        throw new Error(`Chain verification failed: ${e.message}`);
      }

      const sourceContractAddress = CONTRACT_ADDRESSES[finalCurrentChain]?.sourceBridge;
      if (!sourceContractAddress) throw new Error(`Source contract not deployed on chain ${finalCurrentChain}`);

      console.log('🚀 Initiating bridge transaction:', {
        token: tokenAddress,
        amount,
        from: sourceChain,
        to: destChain,
        destinationChainId: destChainInfo.id,
        recipient
      });

      // Check if wallet is connected - add retry for provider/signer refresh after network switch
      if (!wagmiAccount) {
        throw new Error('Please connect your wallet first');
      }
      
      // Retry logic for provider/signer in case they are null after network switching
      let retries = 0;
      let finalProvider = provider;
      let finalSigner = signer;
      
      while (!finalProvider && retries < 3) {
        if (!finalProvider && publicClient) {
          try {
            finalProvider = new ethers.BrowserProvider(publicClient);
            console.log(`🔄 Provider recreated on retry:`, !!finalProvider);
          } catch (e) {
            console.warn('Provider recreate failed on retry:', e.message);
          }
        }
        
        if (!finalSigner && walletClient) {
          try {
            const newProv = new ethers.BrowserProvider(walletClient);
            finalSigner = newProv.getSigner();
            console.log(`🔄 Signer recreated on retry:`, !!finalSigner);
          } catch (e) {
            console.warn('Signer recreate failed on retry:', e.message);
            console.log('🔍 WalletClient available for retry:', !!walletClient);
            console.log('🔍 WalletClient type:', walletClient?.constructor?.name);
          }
        } else if (!finalSigner) {
          console.log('🔄 WalletClient not available for signer recreation, walletClient:', !!walletClient);
        }
        
        if (!finalProvider) {
          // Wait briefly and try again
          await new Promise(resolve => setTimeout(resolve, 100));
          retries++;
          console.log(`🔄 Provider refresh retry #${retries} - Provider: ${!!finalProvider}, Signer: ${!!finalSigner}`);
        }
      }
      
      if (!finalProvider) {
        throw new Error('Wallet provider not available after network switch. Try refreshing the page.');
      }
      
      // User finalProvider and finalSigner for the rest of the function

      // Use read-only contract for view function calls  
      console.log('🔍 Creating contract instance directly...');
      console.log('   Provider available:', !!finalProvider);
      console.log('   Provider type:', finalProvider?.constructor?.name);
      console.log('   Provider connection:', finalProvider?.connection);
      console.log('   Provider URL:', finalProvider?.connection?.url);
      
      // Create a fresh provider to ensure we're using the correct RPC
      const networkConfig = getNetworkConfig();
      let rpcUrl;
      
      if (chainId === '56' || chainId === 56) {
        // BSC Mainnet
        rpcUrl = networkConfig.bscMainnet?.rpcUrl || 'https://bsc-dataseed.binance.org';
      } else if (chainId === '20250217' || chainId === 20250217) {
        // Xphere Mainnet
        rpcUrl = networkConfig.xphereMainnet?.rpcUrl || 'https://xp-mainnet.rpc.xplorium.xyz';
      } else {
        throw new Error(`Unsupported chain ID: ${chainId}`);
      }
      
      const freshProvider = new ethers.JsonRpcProvider(rpcUrl);
      console.log('🔄 Created fresh provider:', freshProvider.connection?.url);
      
      const sourceContractReadOnly = new ethers.Contract(
        CONTRACT_ADDRESSES[chainId].sourceBridge, 
        SOURCE_ABI, 
        freshProvider
      );
      console.log('🔄 Created direct contract instance:', sourceContractReadOnly.target);
      console.log('   Direct contract provider:', !!sourceContractReadOnly.provider);
      console.log('   Fresh provider URL:', freshProvider.connection?.url);
      
      // Debug logging
      console.log('🔍 Debug Token Support Check:', {
        chainId,
        sourceContractAddress: sourceContractReadOnly.target,
        tokenAddress,
        token,
        providerUrl: sourceContractReadOnly.provider?.connection?.url || 'unknown'
      });
      
      // Additional debugging - check if we can call other functions
      try {
        console.log('🔄 Testing contract connectivity...');
        const owner = await sourceContractReadOnly.owner();
        console.log('📊 Contract owner:', owner);
      } catch (e) {
        console.log('❌ Error calling owner():', e.message);
      }

      // Debug the exact call we're making
      console.log('🔍 About to call supportedTokens with:');
      console.log('   Contract address:', sourceContractReadOnly.target);
      console.log('   Token address:', tokenAddress);
      console.log('   Provider:', sourceContractReadOnly.provider);
      console.log('   ABI includes supportedTokens:', SOURCE_ABI.some(abi => abi.name === 'supportedTokens'));

      // Test with a simple contract call first
      try {
        console.log('🔄 Testing simple contract call...');
        console.log('   Using publicClient for contract call');
        const testResult = await publicClient.readContract({
          address: sourceContractAddress,
          abi: SOURCE_ABI,
          functionName: 'getTokenInfo',
          args: [tokenAddress],
        });
        console.log('📊 Direct getTokenInfo result:', testResult);
        console.log('📊 Result type:', typeof testResult);
        console.log('📊 Result value:', testResult);
      } catch (e) {
        console.log('❌ Error in direct getTokenInfo call:', e.message);
        console.log('❌ Error details:', e);
        
        // If it's a network error, try to switch to the correct network
        if (e.message.includes('could not decode result data') || e.message.includes('BAD_DATA')) {
          console.log('🔄 Network error detected, attempting to switch to correct network...');
          const chainKey = getChainKeyById(chainId);
          if (chainKey) {
            try {
              await switchToChain(chainKey);
              console.log('✅ Network switched successfully');
            } catch (switchError) {
              console.log('❌ Failed to switch network:', switchError.message);
              showMessage('error', 'Network Switch Required',
                         `Please switch to ${chainKey} network in MetaMask`, 5000);
            }
          }
        }
      }
      
      // Check if token is supported with retry mechanism
      let isSupported = false;
      let retryCount = 0;
      const maxRetries = 3;
      
      while (!isSupported && retryCount < maxRetries) {
        try {
          console.log(`🔄 Checking token support (attempt ${retryCount + 1}/${maxRetries})...`);
          const tokenInfo = await publicClient.readContract({
            address: sourceContractAddress,
            abi: SOURCE_ABI,
            functionName: 'getTokenInfo',
            args: [tokenAddress],
          });
          
          // Parse the tokenInfo response - it can be an object or array
          if (Array.isArray(tokenInfo)) {
            // If it's an array: [isSupported, decimals, minAmount, maxAmount, dailyLimit, dailyUsage, totalBridged]
            const [isSupportedValue] = tokenInfo;
            isSupported = isSupportedValue;
          } else if (tokenInfo && typeof tokenInfo === 'object') {
            // If it's an object with named properties
            isSupported = tokenInfo.supported || tokenInfo.isSupported;
          } else {
            console.error('❌ Unexpected tokenInfo format:', tokenInfo);
            throw new Error('Invalid tokenInfo format from contract');
          }
          console.log('📊 Token support result:', isSupported);
          console.log('📊 Token info:', tokenInfo);
          
          if (!isSupported && retryCount < maxRetries - 1) {
            console.log('⚠️ Token support returned false, retrying...');
            // Wait a bit before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
            retryCount++;
          } else {
            break;
          }
        } catch (error) {
          console.log('❌ Error checking token support:', error.message);
          if (retryCount < maxRetries - 1) {
            retryCount++;
            await new Promise(resolve => setTimeout(resolve, 1000));
          } else {
            throw error;
          }
        }
      }
      
      if (!isSupported) {
        throw new Error(`Token ${token} is not supported by the bridge. Please contact admin to add it.`);
      }

      // Check if destination chain is supported
      console.log('🔍 Bridge Debug Info:', {
        sourceContractAddress,
        destChain,
        destChainId: destChainInfo.id,
        currentChainId: chainId
      });
      const isChainSupported = await publicClient.readContract({
        address: sourceContractAddress,
        abi: SOURCE_ABI,
        functionName: 'supportedChains',
        args: [destChainInfo.id],
      });
      console.log('🔍 Chain support result:', isChainSupported);
      if (!isChainSupported) {
        throw new Error(`Destination chain ${destChain} (ID: ${destChainInfo.id}) is not supported by the bridge at ${sourceContractAddress}.`);
      }

      // Check user balance
      const balance = await publicClient.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [wagmiAccount],
      });
      
      // Get token info to determine decimals and calculate bridge amount
      const tokenInfo = await publicClient.readContract({
        address: sourceContractAddress,
        abi: SOURCE_ABI,
        functionName: 'getTokenInfo',
        args: [tokenAddress],
      });
      
      // Parse the tokenInfo response - it can be an object or array
      let tokenDecimals, tokenMinAmount, tokenMaxAmount;
      if (Array.isArray(tokenInfo)) {
        const [, decimals, minAmount, maxAmount] = tokenInfo;
        tokenDecimals = decimals;
        tokenMinAmount = minAmount;
        tokenMaxAmount = maxAmount;
      } else if (tokenInfo && typeof tokenInfo === 'object') {
        tokenDecimals = tokenInfo.decimals;
        tokenMinAmount = tokenInfo.minAmount;
        tokenMaxAmount = tokenInfo.maxAmount;
      } else {
        throw new Error('Invalid tokenInfo format from contract');
      }
      
      const bridgeAmount = ethers.parseUnits(amount, tokenDecimals);

      // Check bridge fee - fee is deducted from the bridge amount, so we only need to check the full amount
      const bridgeFee = BigInt(await publicClient.readContract({
        address: sourceContractAddress,
        abi: SOURCE_ABI,
        functionName: 'bridgeFee',
        args: [],
      }));
      const BASIS_POINTS = 10000n; // From contract
      const feeAmount = (bridgeAmount * bridgeFee) / BASIS_POINTS;
      const netBridgeAmount = bridgeAmount - feeAmount; // Amount user will actually receive on destination
      
      // The user only needs to have the full bridgeAmount (which includes the fee)
      // The contract will deduct the fee and send the net amount to destination
      if (balance < bridgeAmount) {
        throw new Error(`Insufficient ${token} balance. Required: ${ethers.formatUnits(bridgeAmount, tokenDecimals)} ${token} (You will receive: ${ethers.formatUnits(netBridgeAmount, tokenDecimals)} ${token} on destination, Fee: ${ethers.formatUnits(feeAmount, tokenDecimals)} ${token})`);
      }

      // Check bridge limits using ENHANCED CONTRACT FUNCTIONS
      try {
        const remainingDailyLimit = await publicClient.readContract({
          address: sourceContractAddress,
          abi: SOURCE_ABI,
          functionName: 'getRemainingDailyLimit',
          args: [tokenAddress],
        });

        // Format limits based on token decimals from contract
        const formatLimit = (amount) => ethers.formatUnits(amount, tokenDecimals);

        if (bridgeAmount < tokenMinAmount) {
          throw new Error(`Amount too small. Minimum: ${formatLimit(tokenMinAmount)} ${token.toUpperCase()}`);
        }
        if (bridgeAmount > tokenMaxAmount) {
          throw new Error(`Amount too large. Maximum: ${formatLimit(tokenMaxAmount)} ${token.toUpperCase()}`);
        }
        if (bridgeAmount > remainingDailyLimit) {
          throw new Error(`Exceeds daily limit. Remaining: ${formatLimit(remainingDailyLimit)} ${token.toUpperCase()}`);
        }

        console.log('✅ Bridge limits validated:', {
          minAmount: formatLimit(tokenMinAmount),
          maxAmount: formatLimit(tokenMaxAmount),
          remainingDaily: formatLimit(remainingDailyLimit),
          tokenDecimals: tokenDecimals
        });
      } catch (limitError) {
        console.warn('Could not check bridge limits:', limitError.message);
        // Continue with bridge if limits can't be checked
      }

      // Check destination contract status for enhanced contracts
      try {
        console.log('🔍 Checking destination contract status...');
        console.log(`   Source chain: ${chainId} (${sourceChain})`);
        console.log(`   Destination chain: ${destChainInfo.id} (${destChain})`);
        console.log(`   Token: ${tokenAddress}`);
        
        // For cross-chain bridges, we need to check the source chain's destination contract
        // to get the correct token mapping, then check the actual destination chain for reserves
        const sourceChainId = chainId;
        const destStatus = await checkDestinationContractStatus(sourceChainId, tokenAddress);
        console.log('✅ Destination contract status:', destStatus);
        
        if (destStatus.status === 'error') {
          console.warn('⚠️ Destination contract check had issues:', destStatus.error);
          console.log('   Bridge will continue but destination validation is incomplete');
        }
      } catch (destError) {
        console.warn('Destination contract check failed:', destError.message);
        console.log('   Bridge will continue but destination validation is incomplete');
      }

      // Approval is already handled by handleInitiateBridge before confirmation dialog
      // Skip approval check here to avoid redundant wallet prompts
      console.log('✅ Proceeding with bridge transaction (approval already handled)...');

      // Initiate bridge transaction
      console.log('🚀 Calling initiateBridge...');
      console.log('🔍 Bridge parameters:', {
        sourceContractAddress,
          tokenAddress,
        bridgeAmount: bridgeAmount.toString(),
        destChainId: destChainInfo.id,
        recipient,
        abi: SOURCE_ABI.length
      });

      // Check for writeContract errors before attempting transaction, but skip cached user rejection errors
      if (writeContractError) {
        // Check if this is a cached user rejection that we should ignore for retry attempts
        const isUserRejection = writeContractError.message && (
          writeContractError.message.includes('User rejected the request') ||
          writeContractError.message.includes('User denied transaction signature') ||
          writeContractError.code === 'ACTION_REJECTED' ||
          writeContractError.name === 'UserRejectedRequestError'
        );
        
        if (isUserRejection) {
          console.log('⏩ Ignoring cached user rejection error, proceeding with fresh approval attempt');
        } else {
          console.warn('Persistent approval call writeContractError detected, likely due to previous cancelled request:', writeContractError);
          throw new Error(`WriteContract error: ${writeContractError.message}`);
        }
      }

      if (isWriteContractPending) {
        console.warn('Another transaction is pending (isWriteContractPending=true), please wait');
        throw new Error('Another transaction is pending, please wait');
      }
      
      // Force wallet network context refresh right before transaction using Wagmi
      try {
        // Use Wagmi's switchChain action for proper network switching
        await switchChain(wagmiConfig, { chainId: finalCurrentChain });
        
        // Wait a moment for the network switch to fully activate the provider
        await new Promise(resolve => setTimeout(resolve, 1000));
        console.log('🔄 Forced wallet to network context via Wagmi', finalCurrentChain);
      } catch (switchError) {
        // This is expected if already on the correct network
        console.warn('Network switch already succeeded or not needed:', switchError.message);
      }
      
      // Use Wagmi's writeContractAsync for bridge transaction
      const hash = await writeContractAsync({
        address: sourceContractAddress,
        abi: SOURCE_ABI,
        functionName: 'initiateBridge',
        args: [tokenAddress, bridgeAmount, destChainInfo.id, recipient],
      });

      console.log('✅ Bridge transaction submitted:', hash);
      
      if (!hash) {
        throw new Error('Bridge transaction hash is undefined - transaction may have been rejected or failed');
      }
      
      // Toast will only be shown after successful bridge completion

      // Wait for confirmation
      const receipt = await publicClient.waitForTransactionReceipt({ hash });
      console.log('✅ Bridge transaction confirmed:', receipt);
      
      // Track successful bridge transaction
      trackBridgeTransaction({
        sourceNetwork: sourceChain,
        destNetwork: destChain,
        tokenSymbol: token,
        amount: amount,
        txHash: hash,
        userAddress: wagmiAccount
      });
      
      // Toast will only be shown after successful bridge completion

      // Find the BridgeRequest event
      let requestId = 'Unknown';
      try {
        // Parse logs using Wagmi's decodeEventLog
        const { decodeEventLog } = await import('viem');
        
        for (const log of receipt.logs) {
          try {
            const decoded = decodeEventLog({
              abi: SOURCE_ABI,
              data: log.data,
              topics: log.topics,
            });
            
            if (decoded.eventName === 'BridgeRequest') {
              requestId = decoded.args.requestId.toString();
        console.log('🔍 Bridge request ID:', requestId);
              break;
            }
          } catch (parseError) {
            // Continue to next log if this one doesn't match
            continue;
          }
        }
      } catch (error) {
        console.warn('Could not parse bridge request event:', error);
      }

      // Replace temporary toast with real request ID toast
      if (requestId && requestId !== 'Unknown') {
        // Show toast only after successful bridge transaction
        const actualSourceChain = getChainKeyById(chainId);
        const actualDestChain = destChain;
        
        console.log('🔔 Creating toast after successful bridge transaction:', {
          sourceNetwork: actualSourceChain,
          destNetwork: actualDestChain,
          requestId,
          amount,
          tokenType: token
        });
        
        addToast(requestId, {
          sourceNetwork: actualSourceChain,
          destNetwork: actualDestChain,
          amount: amount,
          tokenType: token,
          tokenAddress: supportedTokens.find(t => t.symbol === token)?.address || CONTRACT_ADDRESSES[chainId]?.[token]
        });
        
        console.log(`⏳ Starting bridge status tracking for request ${requestId} in ${config.statusPolling.initialDelay / 1000} seconds...`);
        setSuccess(`Bridge request ${requestId} submitted successfully! Waiting for relayer to process...`);
        setTimeout(() => {
          // Update toast status to "processing" when relayer starts
          updateToast(requestId, 'processing');
          startBridgeStatusTracking(requestId);
          setSuccess(`Bridge request ${requestId} submitted successfully! Status tracking started.`);
        }, config.statusPolling.initialDelay);
        
        // Clear success message after 5 seconds
        setTimeout(() => setSuccess(null), 5000);
      } else {
        // If no request ID found, show error without removing any temporary toast
        throw new Error('Failed to get bridge request ID from transaction');
      }
      
      // Clear form
      setAmount('');
      setRecipient('');
      
      // Reload balances
      await loadTokenBalances();
      
      // Refresh bridge configuration to get updated daily usage and remaining
      console.log('🔄 Refreshing bridge configuration after successful transaction...');
      await fetchBridgeConfig();
      
      setSuccess(`🎉 Bridge transaction submitted! Request ID: ${requestId}. Your relayer will process this automatically with ZK proofs!`);
      
      // Clear any pending bridge request since we've completed the transaction
      localStorage.removeItem('pendingBridgeRequest');
      
      setTimeout(() => setSuccess(null), 15000);

    } catch (error) {
      console.error('❌ Bridge transaction failed:', error);
      
      // Track bridge transaction error
      trackError({
        errorType: 'bridge_transaction_failed',
        errorMessage: error.message,
        component: 'ZKBridgeApp',
        userAddress: wagmiAccount
      });
      
      // Handle user cancellation by closing toasts and exiting early
      const isUserCancellation = error.code === 'ACTION_REJECTED' ||
        (error.message && (
          error.message.includes('User rejected the request') ||
          error.message.includes('User denied transaction') ||
          error.message.includes('User rejected') ||
          error.message.includes('UserRejectedRequestError')
        ));
      
      if (isUserCancellation) {
        console.log('🚫 User cancelled bridge transaction via wallet extension, no toast displayed');
        clearAllToasts();
        setLoading(false);
        return; // Exit early without showing error toast
      }
      
      // Create error toast notification for other errors
      let errorMessage;
      if (error.message.includes('insufficient funds')) {
        errorMessage = 'Insufficient ETH for transaction gas fees';
      } else if (error.message.includes('Token not supported')) {
        errorMessage = error.message;
      } else if (error.code === 'UNKNOWN_ERROR') {
        errorMessage = 'Network error occurred. Please check your connection and try again.';
      } else if (error.message.includes('Token approval') && !error.message.includes('User rejected')) {
        errorMessage = error.message;
      } else if (error.message.includes('execution reverted')) {
        errorMessage = 'Transaction reverted. Please check contract conditions and try again.';
      } else {
        errorMessage = `Bridge transaction failed: ${error.message}`;
      }

      // Add error toast  
      const actualSourceChain = getChainKeyById(chainId);
      const actualDestChain = destChain;
      
      addToast(`error_${Date.now()}`, {
        type: 'error',
        title: 'Bridge Transaction Failed',
        message: errorMessage,
        duration: 10000, // 10 seconds
        sourceNetwork: actualSourceChain,
        destNetwork: actualDestChain,
        amount: amount,
        tokenType: token,
        txHash: null,
        status: 'failed'
      });
    } finally {
      setLoading(false);
    }
  }, [amount, recipient, wagmiAccount, chainId, token, sourceChain, destChain, loadTokenBalances, supportedChains, checkDestinationContractStatus, startBridgeStatusTracking, addToast, updateToast, clearAllToasts, SOURCE_ABI, ERC20_ABI, provider, signer, getChainKeyById, switchToChain, writeContractAsync, writeContractError, isWriteContractPending, publicClient, bridgeConfig.minAmount, bridgeConfig.maxAmount, bridgeConfig.dailyLimit, bridgeConfig.dailyUsed, getChainIdFromNetwork, switchToCorrectNetwork, fetchBridgeConfig, bridgeAvailability.isAvailable, bridgeAvailability.reason, showMessage, walletClient]);

  const addTokenToMetaMask = useCallback(async (tokenType) => {
    try {
      const contracts = CONTRACT_ADDRESSES[chainId];
      if (!contracts) {
        throw new Error('Contracts not deployed on this chain');
      }

      const tokenAddress = contracts[tokenType];
      if (!tokenAddress) {
        throw new Error(`${tokenType} not available on this chain`);
      }

      const symbol = await publicClient.readContract({
        address: tokenAddress,
        abi: ERC20_ABI,
        functionName: 'symbol',
      });
      
      // Use correct decimals from environment variables
      const decimals = (tokenType === 'usdt') ? parseInt(process.env.REACT_APP_USDT_DECIMALS || '6') : 
                      (tokenType === 'usdx') ? parseInt(process.env.REACT_APP_USDX_DECIMALS || '6') :
                      (tokenType === 'rwaNova') ? parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18') : 18;

      await window.ethereum.request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: tokenAddress,
            symbol: symbol,
            decimals: decimals,
          },
        },
      });
    } catch (error) {
      console.error('Failed to add token to MetaMask:', error);
    }
  }, [chainId, publicClient, ERC20_ABI]);



  const formatAddress = useCallback((address) => {
    if (!address) return '';
    return address; // Show full address
  }, []);

  const openInExplorer = useCallback((txHash, sourceChainId) => {
    if (!sourceChainId) {
      console.log('Transaction Hash:', txHash);
      setSuccess(`Transaction Hash: ${txHash}`);
      setTimeout(() => setSuccess(null), 3000);
      return;
    }
    
    const chainConfig = Object.values(supportedChains).find(chain => BigInt(chain.id) === BigInt(sourceChainId));
    if (chainConfig && chainConfig.explorer) {
      window.open(`${chainConfig.explorer}/tx/${txHash}`, '_blank');
    } else {
      console.log('Transaction Hash:', txHash);
      setSuccess(`Transaction Hash: ${txHash}`);
      setTimeout(() => setSuccess(null), 3000);
    }
  }, [supportedChains]);


  const clearSuccess = useCallback(() => {
    setSuccess(null);
  }, []);


  // Fetch bridge configuration when chain or token changes
  useEffect(() => {
    if (chainId && publicClient && token) {
      fetchBridgeConfig();
    }
  }, [chainId, publicClient, token, fetchBridgeConfig]);

  // Also fetch configuration when token is first selected
  useEffect(() => {
    if (chainId && publicClient && token) {
      console.log('🔄 Token selected, fetching token-specific configuration...');
      fetchBridgeConfig();
    }
  }, [token, chainId, publicClient, fetchBridgeConfig]);

  // Periodic refresh of bridge configuration to keep daily usage updated
  useEffect(() => {
    if (!chainId || !publicClient || !token) return;

    const refreshInterval = setInterval(() => {
      console.log('🔄 Periodic refresh of bridge configuration...');
      fetchBridgeConfig();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(refreshInterval);
  }, [chainId, publicClient, token, fetchBridgeConfig]);

  // Get current token balance for validation
  const currentTokenBalance = useMemo(() => {
    if (!token || !chainId) return '0';
    // Token balances are stored by symbol (usdt, usdx), not by address
    return tokenBalances[token] || '0';
  }, [token, chainId, tokenBalances]);

  // Validate amount against balance
  const validateAmount = useCallback((inputAmount) => {
    if (!inputAmount || inputAmount === '') {
      setAmountError('');
      return true;
    }

    const amountNum = parseFloat(inputAmount);
    const balanceNum = parseFloat(currentTokenBalance);

    if (isNaN(amountNum) || amountNum <= 0) {
      setAmountError('Amount must be greater than 0');
      return false;
    }

    if (amountNum > balanceNum) {
      setAmountError(`Insufficient balance. Available: ${formatNumberWithCommas(currentTokenBalance)} ${token}`);
      return false;
    }

    setAmountError('');
    return true;
  }, [currentTokenBalance, token, formatNumberWithCommas]);

  // Handle amount input change with validation and decimal limit
  const handleAmountChange = useCallback((e) => {
    let value = e.target.value;
    
    // Force maximum 2 decimal places
    if (value.includes('.')) {
      const [wholePart, decimalPart] = value.split('.');
      if (decimalPart && decimalPart.length > 2) {
        // Truncate to 2 decimal places
        value = wholePart + '.' + decimalPart.substring(0, 2);
      }
    }
    
    // Prevent multiple decimal points
    const decimalCount = (value.match(/\./g) || []).length;
    if (decimalCount > 1) {
      return; // Don't update if multiple decimal points
    }
    
    setAmount(value);
    validateAmount(value);
  }, [validateAmount]);

  // Clear amount error when token changes
  useEffect(() => {
    setAmountError('');
  }, [token]);

  // Set max amount to current balance (with 2 decimal limit, rounded down)
  const setMaxAmount = useCallback(() => {
    // Parse and format balance to 2 decimal places, rounding down
    const balanceNum = parseFloat(currentTokenBalance);
    const roundedDown = Math.floor(balanceNum * 100) / 100;
    const formattedBalance = roundedDown.toFixed(2);
    setAmount(formattedBalance);
    setAmountError('');
  }, [currentTokenBalance]);

  // Get current chain name for display (removed as it's no longer used)
  // const currentChainName = useMemo(() => {
  //   if (!chainId) return 'Not Connected';
  //   const chain = Object.values(supportedChains).find(c => BigInt(c.id) === BigInt(chainId));
  //   return chain ? chain.name : `Chain ${chainId}`;
  // }, [chainId, supportedChains]);

  // Check if current chain is supported
  const isCurrentChainSupported = useMemo(() => {
    return chainId && CONTRACT_ADDRESSES[chainId];
  }, [chainId]);

  // Check if amount is within valid limits
  const isAmountValid = useMemo(() => {
    if (!amount) return false;
    const numAmount = parseFloat(amount);
    
    if (numAmount <= 0) return false;
    if (bridgeConfig.minAmount && numAmount < parseFloat(bridgeConfig.minAmount)) return false;
    if (bridgeConfig.maxAmount && numAmount > parseFloat(bridgeConfig.maxAmount)) return false;
    
    // Check if amount exceeds balance
    const balanceNum = parseFloat(currentTokenBalance);
    if (numAmount > balanceNum) return false;
    
    // Check if there are any amount errors
    if (amountError) return false;
    
    return true;
  }, [amount, bridgeConfig.minAmount, bridgeConfig.maxAmount, currentTokenBalance, amountError]);

  // Calculate daily usage from bridge history
  const calculateDailyUsage = useCallback(() => {
    if (!allBridgeHistory.length) return '0';
    
    const today = new Date().toISOString().split('T')[0];
    const todayTransactions = allBridgeHistory.filter(tx => 
      tx.timestamp && new Date(tx.timestamp).toISOString().split('T')[0] === today
    );
    
    const totalToday = todayTransactions.reduce((sum, tx) => {
      return sum + (parseFloat(tx.amount) || 0);
    }, 0);
    
    return totalToday.toFixed(6);
  }, [allBridgeHistory]);

  // Update daily usage when bridge history changes
  useEffect(() => {
    const dailyUsed = calculateDailyUsage();
    setBridgeConfig(prev => ({
      ...prev,
      dailyUsed: dailyUsed
    }));
  }, [calculateDailyUsage]);

  // Handle source chain changes
  const handleSourceChainChange = useCallback((newSourceChain) => {
    setSourceChain(newSourceChain);
    
    // If destination is the same as new source, change it
    if (newSourceChain === destChain) {
      const availableDestinations = getAvailableDestinationChains(newSourceChain);
      if (availableDestinations.length > 0) {
        setDestChain(availableDestinations[0]);
      }
    }
    
    // Auto-set token based on new source chain (use first available token)
    // Don't force token change if user has manually selected a token that's still available
    const newChainId = getChainIdFromNetwork(newSourceChain);
    const newSupportedTokens = getSupportedTokens(newChainId);
    if (newSupportedTokens.length > 0) {
      // Check if current token is still available on new chain
      const currentTokenStillAvailable = newSupportedTokens.find(t => t.symbol === token);
      if (!currentTokenStillAvailable) {
        // Current token not available, set to first available token
        setToken(newSupportedTokens[0].symbol);
      }
    } else {
      setToken('');
    }
  }, [destChain, getAvailableDestinationChains, getChainToken]);

  // Handle destination chain changes
  const handleDestChainChange = useCallback((newDestChain) => {
    // Prevent setting same chain as source
    if (newDestChain === sourceChain) {
      return;
    }
    setDestChain(newDestChain);
  }, [sourceChain]);

  // Load bridge data when wagmiAccount changes
    // Load bridge history when wagmiAccount changes (legacy - now analytics loads without wagmiAccount)
  useEffect(() => {
    if (wagmiAccount) {
      console.log('👤 Account connected, loading user-specific bridge history...');
      loadAllBridgeHistory(analyticsFilter || {});
    }
    // Note: We no longer clear analytics data when disconnecting wallet
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wagmiAccount]); // Simplified dependencies to avoid infinite loops
  
  // Smart background refresh for analytics - only refresh when there are actual changes
  useEffect(() => {
    if (activeTab !== 'analytics') return; // Only run when analytics tab is active
    if (!smartRefreshEnabled) return; // Only run if user has enabled smart refresh
    
    // Check if there are any processing requests
    const hasProcessingRequests = allBridgeHistory.some(req => 
      req.status === 'pending' || req.status === 'processing'
    );
    
    if (hasProcessingRequests) {
      // More frequent refresh when there are active requests
      const interval = setInterval(() => {
        console.log('🔄 Background refresh: Processing requests detected, refreshing analytics');
        loadAllBridgeHistory(analyticsFilter || {});
      }, 10000); // 10 seconds when there are processing requests
      
      return () => clearInterval(interval);
    } else if (allBridgeHistory.length > 0) {
      // Smart refresh: check for changes before refreshing
      const interval = setInterval(async () => {
        try {
          console.log('🔍 Smart refresh: Checking for new data before refreshing...');
          
          // Get the timestamp of our last refresh
          const lastRefreshTime = localStorage.getItem('lastAnalyticsRefresh') || 0;
          const currentTime = Date.now();
          
          // Only check for changes every 2 minutes (120 seconds) to reduce API calls
          if (currentTime - lastRefreshTime < 120000) {
            console.log('⏰ Smart refresh: Skipping - too soon since last refresh');
            return;
          }
          
          // Check if there are new transactions since our last refresh
          const hasNewData = await checkForNewAnalyticsData(lastRefreshTime);
          
          if (hasNewData) {
            console.log('🔄 Smart refresh: New data detected, refreshing analytics');
            await loadAllBridgeHistory(analyticsFilter || {});
            localStorage.setItem('lastAnalyticsRefresh', currentTime);
          } else {
            console.log('✅ Smart refresh: No new data, skipping refresh');
          }
        } catch (error) {
          console.error('❌ Smart refresh error:', error);
          // Fallback: refresh anyway if check fails
          console.log('🔄 Smart refresh: Fallback refresh due to error');
          await loadAllBridgeHistory(analyticsFilter || {});
          localStorage.setItem('lastAnalyticsRefresh', Date.now());
        }
      }, 120000); // Check every 2 minutes instead of every 30 seconds
      
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, allBridgeHistory.length, smartRefreshEnabled]); // Added smartRefreshEnabled dependency
  
  // Restore pending bridge request from localStorage when component mounts or chain changes
  useEffect(() => {
    const savedRequest = localStorage.getItem('pendingBridgeRequest');
    if (savedRequest) {
      try {
        const parsed = JSON.parse(savedRequest);
        
        // If we have a pending request and the current chain supports the token, restore the form
        if (parsed && chainId) {
          const sourceContracts = CONTRACT_ADDRESSES[chainId];
          if (sourceContracts && sourceContracts[parsed.token]) {
            console.log('🔄 Restoring pending bridge request after chain switch:', parsed);
            setAmount(parsed.amount);
            setRecipient(parsed.recipient);
            setToken(parsed.token);
            setSourceChain(parsed.sourceChain);
            setDestChain(parsed.destChain);
            
        localStorage.removeItem('pendingBridgeRequest');
            
            // Show success message
            setSuccess('✅ Bridge form restored after chain switch! You can now proceed with the transaction.');
            setTimeout(() => setSuccess(null), 5000);
          }
        }
      } catch (error) {
        console.error('Failed to parse pending bridge request:', error);
        localStorage.removeItem('pendingBridgeRequest');
      }
    }
  }, [chainId]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      bridgeService.stopAllPolling();
    };
  }, []);

  // Clear local bridge state on mount to ensure clean start
  useEffect(() => {
    setBridgeRequests([]);
    setBridgeStatuses({});
  }, []);

  // Auto-remove completed toasts after 5 seconds
  useEffect(() => {
    const completedToasts = activeToasts.filter(toast => 
      toast.status === 'completed' || toast.status === 'failed' || toast.status === 'skipped'
    );
    
    if (completedToasts.length > 0) {
      const timer = setTimeout(() => {
        clearCompletedToasts();
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [activeToasts, clearCompletedToasts]);

  // Auto-load analytics data when Analytics tab is selected (no wallet required)
  useEffect(() => {
    if (activeTab === 'analytics') {
      console.log('📊 Analytics tab selected, loading data automatically...');
      loadAllBridgeHistory(analyticsFilter || {});
        loadAnalyticsMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]); // Only depend on activeTab to avoid infinite loops

  // Refresh analytics when filters change (separate effect to avoid loops)
  useEffect(() => {
    if (activeTab === 'analytics' && Object.keys(analyticsFilter || {}).length > 0) {
      console.log('🔍 Analytics filters changed, refreshing data...');
      loadAllBridgeHistory(analyticsFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analyticsFilter]); // Only depend on filter changes to avoid infinite loops

  // Check admin status when wallet connects or chain changes
  const checkAdminStatus = useCallback(async () => {
    if (!isConnected || !wagmiAccount || !chainId || !provider) {
      setIsAdmin(false);
      setIsNetworkSwitching(false);
      return;
    }

    try {
      // Don't set network switching state here - let AdminPanel control it
      // Only set it if we're not already in a network switching state
      if (!isNetworkSwitching) {
        setIsNetworkSwitching(true);
      }
      
      // Get the contract addresses for the current chain
      const contractAddresses = CONTRACT_ADDRESSES[chainId];
      if (!contractAddresses || !contractAddresses.sourceBridge) {
        console.log('⚠️ No source bridge contract found for chain:', chainId, contractAddresses);
        setIsAdmin(false);
        // Only reset network switching if we set it in this function
        if (!isNetworkSwitching) {
          setIsNetworkSwitching(false);
        }
        return;
      }
      
      console.log('🔍 Checking admin status for contract:', contractAddresses.sourceBridge);

      // Create contract instance to check ownership
      const BRIDGE_SOURCE_ABI = [
        "function owner() view returns (address)"
      ];
      
      const bridgeSourceContract = new ethers.Contract(
        contractAddresses.sourceBridge, 
        BRIDGE_SOURCE_ABI, 
        provider
      );
      
      // Add better error handling for the owner() call
      let owner;
      try {
        owner = await bridgeSourceContract.owner();
        
        // Check if we got a valid owner address
        if (!owner || owner === '0x0000000000000000000000000000000000000000' || owner.startsWith('0x00')) {
          console.log('⚠️ Contract owner is undefined or zero address');
          setIsAdmin(false);
          return;
        }
      } catch (ownerError) {
        // If the owner() call fails (e.g., contract not deployed or doesn't exist)
        if (ownerError.message.includes('could not decode result data') || 
            ownerError.message.includes('execution reverted') ||
            ownerError.message.includes('BAD_DATA')) {
          console.log('⚠️ Contract does not have owner() function or not deployed:', contractAddresses.sourceBridge);
          setIsAdmin(false);
          return;
        }
        throw ownerError; // Re-throw if it's not our expected error
      }
      
      const isAdminWallet = owner.toLowerCase() === wagmiAccount.toLowerCase();
      setIsAdmin(isAdminWallet);
      
      // Only reset network switching if we set it in this function
      if (!isNetworkSwitching) {
        setIsNetworkSwitching(false);
      }
      
      console.log('🔐 Admin status checked:', { 
        isAdmin: isAdminWallet, 
        currentAccount: wagmiAccount, 
        contractOwner: owner 
      });
    } catch (error) {
      console.error('❌ Error checking admin status:', error);
      setIsAdmin(false);
      // Only reset network switching if we set it in this function
      if (!isNetworkSwitching) {
        setIsNetworkSwitching(false);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, wagmiAccount, chainId, provider]); // Removed isNetworkSwitching to prevent infinite loop

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  // Switch away from admin tab if user is no longer an admin
  // Add a longer delay to prevent switching during network transitions
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (activeTab === 'admin' && (!isConnected || !isAdmin) && !isNetworkSwitching) {
        setActiveTab('bridge');
      }
    }, 3000); // 3 second delay to allow network switching to complete
    
    return () => clearTimeout(timeoutId);
  }, [activeTab, isConnected, isAdmin, isNetworkSwitching]);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
    console.log(`💾 Saved active tab to localStorage: ${activeTab}`);
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Network Notifications */}
      
      {/* Header */}
      <header className="bg-black/20 backdrop-blur-lg border-b border-white/10 relative z-40">  
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center">
                <img src="/logo.png" alt="XP Bridge Logo" className="w-10 h-10 rounded-xl" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">XP Bridge</h1>
                <p className="text-xs text-gray-400">Decentralized cross chain bridge utilizing ZK cryptography</p>
              </div>
            </div>
            
            
            <div className="flex items-center space-x-4">
              {!isConnected ? (
                <WagmiWalletStatus />
              ) : (
                <div className="flex items-center space-x-4">
                  <div className="px-6 py-2 min-w-[400px]">
                    <div className="flex items-center justify-between gap-8">
                      <div className="flex items-center space-x-2">
                        <div className={`w-2 h-2 rounded-full ${isCorrectNetwork ? 'bg-green-400' : 'bg-yellow-400'} ${isAutoConnecting ? 'animate-pulse' : ''}`}></div>
                        <span className="text-white text-sm font-mono">{wagmiAccount ? formatAddress(wagmiAccount) : 'Unknown'}</span>
                      </div>
                      {/* Network Switch Button on same line */}
                      {currentNetwork && (
                        <div className="flex items-center space-x-2 bg-white/5 border border-white/20 rounded-lg px-3 py-1">
                          <button
                            onClick={() => {
                              // Switch to BSC
                              switchToChain('bscMainnet');
                            }}
                            className={`px-2 py-1 rounded transition-colors ${getChainKeyById(chainId) === 'bscMainnet' ? 'bg-yellow-500 text-white font-medium' : 'text-gray-400 hover:text-yellow-400 hover:bg-yellow-500/10'}`}
                          >
                            BSC
                          </button>
                          {getChainKeyById(chainId) === 'bscMainnet' ? (
                            <ArrowRight size={12} className="text-yellow-400" />
                          ) : (
                            <ArrowRight size={12} className="text-red-400 rotate-180" />
                          )}
                          <button
                            onClick={() => {
                              // Switch to Xphere
                              switchToChain('xphereMainnet');
                            }}
                            className={`px-2 py-1 rounded transition-colors ${getChainKeyById(chainId) === 'xphereMainnet' ? 'bg-red-500 text-white font-medium' : 'text-gray-400 hover:text-red-400 hover:bg-red-500/10'}`}
                          >
                            XP
                          </button>
                        </div>
                      )}
                    </div>
                    {isAutoConnecting && (
                      <div className="text-xs text-blue-400 mt-1 flex items-center space-x-1">
                        <Clock className="animate-spin" size={12} />
                        <span>Auto-connecting...</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handleDisconnectWallet}
                    className="text-gray-400 hover:text-white text-sm"
                  >
                    Disconnect
                  </button>
                </div>
              )}

              {/* Help Button */}
              <div className="relative group">
                <button className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors">
                  <HelpCircle size={24} />
                  <span className="text-sm font-medium">Help</span>
                </button>
                <div className="absolute right-0 top-full mt-2 w-80 bg-gray-900 border border-white/20 rounded-lg shadow-xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <p className="text-sm text-gray-300 leading-relaxed">
                    If you have any questions or comments please send us a detailed email to{' '}
                    <a href="mailto:info@xpbridge.app" className="text-blue-400 hover:text-blue-300 underline">
                      info@xpbridge.app
                    </a>
                    {' '}and a team member will reply as soon as possible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Toast Notifications for Active Bridge Requests */}
      <div className="fixed top-20 right-4 z-50 space-y-3 max-w-sm">
        {activeToasts.map((toast) => (
          <div
            key={toast.id}
            className={`backdrop-blur-lg border rounded-lg p-4 shadow-lg transition-all duration-300 ${
              toast.status === 'completed' ? 'bg-green-500/20 border-green-500/50' :
              toast.status === 'failed' ? 'bg-red-500/20 border-red-500/50' :
              toast.status === 'skipped' ? 'bg-yellow-500/20 border-yellow-500/50' :

              'bg-white/10 border-blue-500/30'
            }`}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="flex items-center space-x-1">
                    <img 
                      src={toast.sourceNetwork === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                      alt={toast.sourceNetwork === 'bscMainnet' ? 'BSC' : 'Xphere'} 
                      className="w-4 h-4" 
                    />
                    <span className="text-xs text-gray-300">{toast.sourceNetwork === 'bscMainnet' ? 'BSC' : 'XP'}</span>
                    <ArrowRight className="w-3 h-3 text-gray-400" />
                    <img 
                      src={toast.destNetwork === 'xphereMainnet' ? '/xpt.svg' : '/bsc.svg'} 
                      alt={toast.destNetwork === 'xphereMainnet' ? 'Xphere' : 'BSC'} 
                      className="w-4 h-4" 
                    />
                    <span className="text-xs text-gray-300">{toast.destNetwork === 'xphereMainnet' ? 'XP' : 'BSC'}</span>
                  </div>
                  <span className="text-xs text-gray-400">#{toast.requestId}</span>
                </div>
                
                <div className="text-sm text-white font-medium mb-1">
                  {(() => {
                    const formatted = formatAmount(toast.amount, toast.tokenType);
                    console.log('🔔 Toast amount formatting:', { 
                      raw: toast.amount, 
                      tokenType: toast.tokenType, 
                      formatted 
                    });
                    
                    // Get token symbol based on tokenType
                    let tokenSymbol = 'USDX'; // default
                    if (toast.tokenType === 'usdt') {
                      tokenSymbol = 'USDT';
                    } else if (toast.tokenType === 'rwaNova' || toast.tokenType === 'rwanova') {
                      tokenSymbol = process.env.REACT_APP_RWA_NOVA_NAME || 'RWA Nova';
                    } else if (toast.tokenType === 'usdx') {
                      tokenSymbol = 'USDX';
                    }
                    
                    return `${formatted} ${tokenSymbol}`;
                  })()}
                </div>
                
                <div className="flex items-center space-x-2">
                  {toast.status === 'pending' && (
                    <div className="flex items-center space-x-1 text-blue-400">
                      <Clock className="w-3 h-3 animate-spin" />
                      <span className="text-xs">Preparing...</span>
                    </div>
                  )}
                  {toast.status === 'processing' && (
                    <div className="flex items-center space-x-1 text-orange-400">
                      <Clock className="w-3 h-3 animate-spin" />
                      <span className="text-xs">Processing...</span>
                    </div>
                  )}
                  {toast.status === 'submitted' && (
                    <div className="flex items-center space-x-1 text-yellow-400">
                      <Clock className="w-3 h-3 animate-pulse" />
                      <span className="text-xs">Submitted</span>
                    </div>
                  )}
                  {toast.status === 'confirmed' && (
                    <div className="flex items-center space-x-1 text-blue-400">
                      <Clock className="w-3 h-3 animate-pulse" />
                      <span className="text-xs">Confirmed</span>
                    </div>
                  )}
                  {toast.status === 'completed' && (
                    <div className="flex items-center space-x-1 text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      <span className="text-xs">Completed</span>
                    </div>
                  )}
                  {toast.status === 'failed' && (
                    <div className="flex items-center space-x-1 text-red-400">
                      <AlertCircle className="w-3 h-3" />
                      <span className="text-xs">Failed</span>
                    </div>
                  )}
                  {toast.status === 'skipped' && (
                    <div className="flex items-center space-x-1 text-yellow-400">
                      <AlertCircle className="w-3 h-3" />
                      <span className="text-xs">Skipped</span>
                    </div>
                  )}

                </div>
              </div>
              
              <button
                onClick={() => removeToast(toast.id)}
                className="text-gray-400 hover:text-white ml-2 transition-colors"
              >
                ×
              </button>
            </div>
            
            {/* Auto-remove completed toasts after 5 seconds */}
            {toast.status === 'completed' && (
              <div className="mt-2">
                <div className="w-full bg-green-500/20 rounded-full h-1">
                  <div className="bg-green-500 h-1 rounded-full transition-all duration-5000" style={{ width: '100%' }}></div>
                </div>
              </div>
            )}
          </div>
        ))}
        
        {/* Clear completed toasts button */}
        {activeToasts.some(toast => toast.status === 'completed' || toast.status === 'failed' || toast.status === 'skipped') && (
          <button
            onClick={clearCompletedToasts}
            className="w-full bg-white/10 hover:bg-white/20 text-white text-xs py-2 rounded-lg transition-colors"
          >
            Clear Completed
          </button>
        )}
      </div>


      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">


        {/* Success Display */}
        {success && (
          <div className="mb-6 bg-green-500/10 border border-green-500/20 rounded-lg p-4 flex items-center space-x-2">
            <CheckCircle className="text-green-400" size={20} />
            <span className="text-green-200">{success}</span>
            <button onClick={clearSuccess} className="ml-auto text-green-400 hover:text-green-300">×</button>
          </div>
        )}

        {/* Chain Warning */}
        {wagmiAccount && !isCurrentChainSupported && (
          <div className="mb-6 bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <AlertCircle className="text-yellow-400" size={20} />
              <span className="text-yellow-200 font-medium">Unsupported Chain</span>
            </div>
            <p className="text-yellow-100 text-sm mb-3">
              Please switch to one of the supported networks to use the bridge:
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(supportedChains).map(([key, chain]) => (
                <button
                  key={key}
                  onClick={() => switchToChain(key)}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-3 py-1 rounded text-sm transition-colors"
                >
                  Switch to {chain.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-1 bg-white/10 rounded-xl p-1 relative z-30">
            {[
              { id: 'bridge', label: 'Bridge', icon: ArrowRightLeft },
              { id: 'analytics', label: 'Analytics', icon: Activity },
              ...(isConnected && isAdmin ? [{ id: 'admin', label: 'Admin', icon: Shield }] : []),
              { id: 'settings', label: 'Settings', icon: Settings }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                    activeTab === tab.id
                      ? 'bg-white text-purple-600 shadow-lg'
                      : 'text-white/70 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Bridge Tab */}
        {activeTab === 'bridge' && (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 max-w-4xl mx-auto">
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                  <ArrowRightLeft size={24} />
                  <span>XP Bridge</span>
                </h2>




                {/* Bridge Route Indicator */}
                {sourceChain && destChain && sourceChain !== destChain && (
                  <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <h3 className="text-white font-semibold mb-2">🌉 Bridge Route</h3>
                    <div className="flex items-center justify-center space-x-4 text-lg">
                      <div className="flex items-center space-x-2">
                        <div className={`w-8 h-8 ${supportedChains[sourceChain]?.color} rounded-lg flex items-center justify-center`}>
                          <img 
                            src={sourceChain === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                            alt={supportedChains[sourceChain]?.name} 
                            className="w-6 h-6" 
                          />
                        </div>
                        <span className="text-white font-medium">
                          {token ? (supportedTokens.find(t => t.symbol === token)?.name || token.toUpperCase()) : getChainToken(sourceChain)?.toUpperCase()} on {supportedChains[sourceChain]?.name}
                        </span>
                      </div>
                      
                      <ArrowRight className="text-blue-400" size={24} />
                      
                      <div className="flex items-center space-x-2">
                        <div className={`w-8 h-8 ${supportedChains[destChain]?.color} rounded-lg flex items-center justify-center`}>
                          <img 
                            src={destChain === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                            alt={supportedChains[destChain]?.name} 
                            className="w-6 h-6" 
                          />
                        </div>
                        <span className="text-white font-medium">
                          {token ? (supportedTokens.find(t => t.symbol === token)?.name || token.toUpperCase()) : getChainToken(destChain)?.toUpperCase()} on {supportedChains[destChain]?.name}
                        </span>
                      </div>
                    </div>
                    <p className="text-center text-gray-300 text-sm mt-2">
                      {token ? (
                        `${supportedTokens.find(t => t.symbol === token)?.name || token.toUpperCase()} will be locked on ${supportedChains[sourceChain]?.name}, ${supportedTokens.find(t => t.symbol === token)?.name || token.toUpperCase()} will be ${destChain === 'bscMainnet' ? 'released' : 'released'} on ${supportedChains[destChain]?.name}`
                      ) : (
                        sourceChain === 'bscMainnet' ? 'USDT will be locked on BSC, USDX will be minted on Xphere' : 
                        'USDX will be locked on Xphere, USDT will be released on BSC'
                      )}
                    </p>
                  </div>
                )}









                {/* Chain Selection */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      From Chain {isConnected && <span className="text-green-400 text-xs">(Auto-selected)</span>}
                    </label>
                    <select 
                      value={sourceChain}
                      onChange={(e) => handleSourceChainChange(e.target.value)}
                      disabled={isConnected}
                      className={`w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500 ${isConnected ? 'opacity-60 cursor-not-allowed' : ''}`}
                    >
                      {Object.entries(supportedChains).map(([key, chain]) => (
                        <option key={key} value={key} className="bg-slate-800">{chain.name}</option>
                      ))}
                    </select>
                    {isConnected && (
                      <p className="text-xs text-gray-400 mt-1">
                        Source chain is automatically set based on your connected network
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">To Chain</label>
                    <select 
                      value={destChain}
                      onChange={(e) => handleDestChainChange(e.target.value)}
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      {getAvailableDestinationChains(sourceChain).map((key) => {
                        const chain = supportedChains[key];
                        return (
                          <option key={key} value={key} className="bg-slate-800">
                            {chain.name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>

                {/* Token and Amount */}
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Token</label>
                    <div className="flex gap-2">
                      {sourceChain && supportedTokens.length > 0 ? (
                        <select
                          value={token || ''}
                          onChange={(e) => setToken(e.target.value)}
                          className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          {supportedTokens.map((tokenOption) => (
                            <option key={tokenOption.symbol} value={tokenOption.symbol} className="bg-slate-800">
                              {tokenOption.name} ({tokenOption.symbol.toUpperCase()})
                              {tokenBalances[tokenOption.symbol] ? ` - Balance: ${formatNumberWithCommas(parseFloat(tokenBalances[tokenOption.symbol]).toFixed(6))}` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className="flex-1 bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-gray-400">
                          {sourceChain ? 'No tokens available' : 'Select a source chain first'}
                        </div>
                      )}
                      {token && (
                        <button
                          onClick={() => addTokenToMetaMask(token)}
                          disabled={!isCurrentChainSupported}
                          className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                          title="Add to MetaMask"
                        >
                          + MM
                        </button>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      {token && (
                        <>
                          <p className="text-xs text-gray-400">
                            {supportedTokens.find(t => t.symbol === token)?.type || 'token'} • {supportedTokens.find(t => t.symbol === token)?.name || token}
                          </p>
                          <div className="flex items-center space-x-2">
                            {(() => {
                              const requiredChainId = getChainIdFromNetwork(sourceChain);
                              const isCorrectNetwork = chainId === requiredChainId;
                              return (
                                <>
                                  <div className={`w-2 h-2 rounded-full ${isCorrectNetwork ? 'bg-green-400' : 'bg-red-400'}`}></div>
                                  <span className={`text-xs ${isCorrectNetwork ? 'text-green-400' : 'text-red-400'}`}>
                                    {isCorrectNetwork ? 'Correct Network' : 'Wrong Network'}
                                  </span>
                                </>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-300">Amount</label>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-400">
                          Balance: {formatNumberWithCommas(currentTokenBalance)} {token}
                        </span>
                        <button
                          type="button"
                          onClick={setMaxAmount}
                          className="px-2 py-1 text-xs bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors"
                        >
                          Max
                        </button>
                      </div>
                    </div>
                    <input
                      type="number"
                      value={amount}
                      onChange={handleAmountChange}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                      max="999999999"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]{0,2}"
                      title="Enter amount with maximum 2 decimal places"
                      className={`w-full bg-white/10 border rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield] ${
                        amountError 
                          ? 'border-red-500 focus:ring-red-500' 
                          : 'border-white/20 focus:ring-purple-500'
                      }`}
                    />
                    {amountError && (
                      <p className="mt-1 text-sm text-red-400">{amountError}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">
                      Maximum 2 decimal places allowed (e.g., 123.45)
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Recipient Address</label>
                    <input
                      type="text"
                      value={recipient}
                      onChange={(e) => setRecipient(e.target.value)}
                      placeholder="0x..."
                      className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={() => setRecipient(wagmiAccount)}
                      disabled={!wagmiAccount}
                      className="mt-2 text-sm text-blue-400 hover:text-blue-300 disabled:opacity-50"
                    >
                      Use my address
                    </button>
                  </div>
                </div>

                {/* Network Warning */}
                {token && (() => {
                  const requiredChainId = getChainIdFromNetwork(sourceChain);
                  const isCorrectNetwork = chainId === requiredChainId;
                  if (!isCorrectNetwork) {
                    return (
                      <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="text-red-400" size={20} />
                          <div>
                            <h4 className="text-red-400 font-semibold">Wrong Network</h4>
                            <p className="text-red-300 text-sm">
                              You're connected to {supportedChains[Object.keys(supportedChains).find(key => supportedChains[key].id === chainId)]?.name || `Chain ${chainId}`}, 
                              but need to be on {supportedChains[sourceChain]?.name} to bridge {token.toUpperCase()}.
                            </p>
                            <p className="text-red-300 text-xs mt-1">
                              The app will automatically switch networks when you click the bridge button.
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Bridge Button */}
                <button
                  onClick={handleInitiateBridge}
                  disabled={!wagmiAccount || !amount || !recipient || loading || !ethers || !isCurrentChainSupported || sourceChain === destChain || !isAmountValid || !bridgeAvailability.isAvailable || bridgeAvailability.isChecking}
                  className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 ${
                    isAmountValid && wagmiAccount && amount && recipient && !loading && isCurrentChainSupported && sourceChain !== destChain && bridgeAvailability.isAvailable && !bridgeAvailability.isChecking
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                      : 'bg-gray-600 text-gray-300'
                  }`}
                >
                  {loading ? (
                    <Clock className="animate-spin" size={20} />
                  ) : bridgeAvailability.isChecking ? (
                    <>
                      <Clock className="animate-spin" size={20} />
                      <span>Checking Bridge Availability...</span>
                    </>
                  ) : !bridgeAvailability.isAvailable ? (
                    <>
                      <AlertCircle size={20} />
                      <span>Bridge Unavailable</span>
                    </>
                  ) : (
                    <>
                      <span>🎯 Initiate XP Bridge Transaction</span>
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>

                {sourceChain === destChain && (
                  <p className="text-yellow-400 text-sm mt-2 text-center">
                    Please select different source and destination chains
                  </p>
                )}

                {amount && bridgeConfig.minAmount && parseFloat(amount) < parseFloat(bridgeConfig.minAmount) && (
                  <p className="text-red-400 text-sm mt-2 text-center">
                    Amount must be at least {bridgeConfig.minAmount} tokens
                  </p>
                )}

                {amount && bridgeConfig.maxAmount && parseFloat(amount) > parseFloat(bridgeConfig.maxAmount) && (
                  <p className="text-red-400 text-sm mt-2 text-center">
                    Amount cannot exceed {bridgeConfig.maxAmount} tokens
                  </p>
                )}

                {amount && bridgeConfig.dailyLimit && bridgeConfig.dailyUsed && 
                 parseFloat(amount) > (parseFloat(bridgeConfig.dailyLimit) - parseFloat(bridgeConfig.dailyUsed)) && (
                  <p className="text-red-400 text-sm mt-2 text-center">
                    Amount exceeds remaining daily limit of {(parseFloat(bridgeConfig.dailyLimit) - parseFloat(bridgeConfig.dailyUsed)).toFixed(6)} tokens
                  </p>
                )}

                {/* Bridge Availability Warnings */}
                {bridgeAvailability.isChecking && (
                  <p className="text-blue-400 text-sm mt-2 text-center flex items-center justify-center space-x-2">
                    <Clock className="animate-spin" size={16} />
                    <span>Checking bridge reserves and limits...</span>
                  </p>
                )}

                {!bridgeAvailability.isAvailable && !bridgeAvailability.isChecking && bridgeAvailability.reason && (
                  <div className="mt-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-400 text-sm text-center font-medium">
                      🚫 Bridge Unavailable
                    </p>
                    <p className="text-red-300 text-xs text-center mt-1">
                      {bridgeAvailability.reason}
                    </p>
                    {bridgeAvailability.details && (
                      <details className="mt-2 text-xs text-gray-400">
                        <summary className="cursor-pointer hover:text-gray-300">View Details</summary>
                        <pre className="mt-1 p-2 bg-gray-800/50 rounded text-xs overflow-auto">
                          {JSON.stringify(bridgeAvailability.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                )}

                {bridgeAvailability.isAvailable && !bridgeAvailability.isChecking && bridgeAvailability.lastChecked && (
                  <p className="text-green-400 text-sm mt-2 text-center flex items-center justify-center space-x-2">
                    <CheckCircle size={16} />
                    <span>Bridge is available for this transaction</span>
                  </p>
                )}

                {/* Bridge Validation Status */}
                {amount && token && (
                  <div className="mt-4 p-3 bg-gray-500/10 border border-gray-500/20 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-300 mb-2">Bridge Validation</h4>
                    <div className="space-y-1 text-xs">
                      {amount && (
                        <div className="flex justify-between">
                          <span>Bridge Amount:</span>
                          <span className="text-white">{formatNumberWithCommas(parseFloat(amount).toFixed(6))} {token.toUpperCase()}</span>
                        </div>
                      )}

                      {amount && bridgeConfig.fee && (
                        <div className="flex justify-between">
                          <span>Bridge Fee:</span>
                          <span className="text-orange-400">{bridgeConfig.fee}</span>
                        </div>
                      )}

                      {amount && bridgeConfig.fee && (
                        <div className="flex justify-between">
                          <span>You Will Receive:</span>
                          <span className="text-green-400">
                            {(() => {
                              const bridgeAmount = parseFloat(amount);
                              const feePercent = parseFloat(bridgeConfig.fee.replace('%', ''));
                              const receiveAmount = bridgeAmount * (1 - feePercent / 100);
                              const destToken = token.toUpperCase();
                              return `${formatNumberWithCommas(receiveAmount.toFixed(6))} ${destToken}`;
                            })()}
                          </span>
                        </div>
                      )}

                      {bridgeConfig.minAmount && (
                        <div className="flex justify-between">
                          <span>Min Amount:</span>
                          <span className={parseFloat(amount) >= parseFloat(bridgeConfig.minAmount) ? "text-green-400" : "text-red-400"}>
                            {formatNumberWithCommas(parseFloat(bridgeConfig.minAmount).toFixed(3))} tokens
                          </span>
                        </div>
                      )}
                      
                      {bridgeConfig.maxAmount && (
                        <div className="flex justify-between">
                          <span>Max Amount:</span>
                          <span className={parseFloat(amount) <= parseFloat(bridgeConfig.maxAmount) ? "text-green-400" : "text-red-400"}>
                            {formatNumberWithCommas(parseFloat(bridgeConfig.maxAmount).toFixed(0))} tokens
                          </span>
                        </div>
                      )}
                      
                      {bridgeConfig.dailyLimit && (
                        <div className="flex justify-between">
                          <span>Daily Limit:</span>
                          <span className="text-blue-400">{formatNumberWithCommas(parseFloat(bridgeConfig.dailyLimit).toFixed(0))} tokens</span>
              </div>
                      )}
                      
                      {bridgeConfig.dailyUsed && (
                        <div className="flex justify-between">
                          <span>Daily Used:</span>
                          <span className="text-orange-400">{formatNumberWithCommas(parseFloat(bridgeConfig.dailyUsed).toFixed(6))} tokens</span>
            </div>
                      )}
                      
                      {bridgeConfig.remainingDaily ? (
                        <div className="flex justify-between">
                          <span>Remaining:</span>
                          <span className="text-green-400">{formatNumberWithCommas(parseFloat(bridgeConfig.remainingDaily).toFixed(6))} tokens</span>
                        </div>
                      ) : bridgeConfig.dailyLimit && bridgeConfig.dailyUsed ? (
                        <div className="flex justify-between">
                          <span>Remaining:</span>
                          <span className="text-green-400">
                            {formatNumberWithCommas((parseFloat(bridgeConfig.dailyLimit) - parseFloat(bridgeConfig.dailyUsed)).toFixed(6))} tokens
                          </span>
                        </div>
                      ) : null}
                      
                      {bridgeConfig.fee && (
                        <div className="flex justify-between">
                          <span>Bridge Fee:</span>
                          <span className="text-yellow-400">{bridgeConfig.fee}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* No Wallet Required Notice */}
            {!wagmiAccount && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-300">
                  <Activity size={20} />
                  <span className="font-medium">📊 Public Analytics Dashboard</span>
                </div>
                <p className="text-blue-200 text-sm mt-2">
                  View real-time bridge statistics and transaction history without connecting a wallet. 
                  Connect your wallet to access the bridge functionality.
                </p>
              </div>
            )}


            {/* Bridge Analytics */}
          <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                <Activity size={24} />
                  <span>Bridge Analytics</span>
              </h2>
                <p className="text-gray-400 mt-1">Complete analytics of all ZK bridge transactions across the network</p>
                
                {/* Analytics Summary */}
                <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-400">
                      ${formatNumberWithCommas((actualCounts.totalVolumeUSD || 0).toFixed(2))}
                    </div>
                    <div className="text-xs text-gray-400">Total Volume (USD)</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-white">{formatNumberWithCommas(actualCounts.total || pagination.totalCount || allBridgeHistory.length)}</div>
                    <div className="text-xs text-gray-400">Total Transactions</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-purple-400">
                      {formatNumberWithCommas(actualCounts.completed || allBridgeHistory.filter(req => req.status === 'completed').length)}
                    </div>
                    <div className="text-xs text-gray-400">Completed</div>
                  </div>
                  <div className="bg-white/5 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-400">
                      {actualCounts.successRate || (allBridgeHistory.length > 0 
                        ? Math.round((allBridgeHistory.filter(req => req.status === 'completed').length / allBridgeHistory.length) * 100)
                        : 0)}%
                    </div>
                    <div className="text-xs text-gray-400">Success Rate</div>
                  </div>
                </div>

                {/* Token-Specific Statistics */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                    <Activity size={20} />
                    <span>Token Statistics</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* USDT Statistics */}
                    <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-xl p-4 border border-blue-500/20">
                      <div className="flex items-center justify-center mb-3">
                        <h4 className="text-md font-semibold text-blue-300">USDT</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Transactions:</span>
                          <span className="text-white font-semibold">{actualCounts.tokenStats?.usdt?.total || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Completed:</span>
                          <span className="text-green-400 font-semibold">{actualCounts.tokenStats?.usdt?.completed || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Volume:</span>
                          <span className="text-blue-400 font-semibold">${formatNumberWithCommas((actualCounts.tokenStats?.usdt?.volume || 0).toFixed(2))}</span>
                        </div>
                        {actualCounts.tokenStats?.usdt?.total > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Success Rate:</span>
                            <span className="text-purple-400 font-semibold">
                              {Math.round((actualCounts.tokenStats.usdt.completed / actualCounts.tokenStats.usdt.total) * 100)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* USDX Statistics */}
                    <div className="bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-xl p-4 border border-red-500/20">
                      <div className="flex items-center justify-center mb-3">
                        <h4 className="text-md font-semibold text-red-300">USDX</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Transactions:</span>
                          <span className="text-white font-semibold">{actualCounts.tokenStats?.usdx?.total || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Completed:</span>
                          <span className="text-green-400 font-semibold">{actualCounts.tokenStats?.usdx?.completed || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Volume:</span>
                          <span className="text-red-400 font-semibold">${formatNumberWithCommas((actualCounts.tokenStats?.usdx?.volume || 0).toFixed(2))}</span>
                        </div>
                        {actualCounts.tokenStats?.usdx?.total > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Success Rate:</span>
                            <span className="text-purple-400 font-semibold">
                              {Math.round((actualCounts.tokenStats.usdx.completed / actualCounts.tokenStats.usdx.total) * 100)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RWA Nova Statistics */}
                    <div className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 rounded-xl p-4 border border-yellow-500/20">
                      <div className="flex items-center justify-center mb-3">
                        <h4 className="text-md font-semibold text-yellow-300">RWA Nova</h4>
                      </div>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Transactions:</span>
                          <span className="text-white font-semibold">{actualCounts.tokenStats?.rwaNova?.total || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Completed:</span>
                          <span className="text-green-400 font-semibold">{actualCounts.tokenStats?.rwaNova?.completed || 0}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-400">Volume:</span>
                          <span className="text-yellow-400 font-semibold">${formatNumberWithCommas((actualCounts.tokenStats?.rwaNova?.volume || 0).toFixed(2))}</span>
                        </div>
                        {actualCounts.tokenStats?.rwaNova?.total > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-400">Success Rate:</span>
                            <span className="text-purple-400 font-semibold">
                              {Math.round((actualCounts.tokenStats.rwaNova.completed / actualCounts.tokenStats.rwaNova.total) * 100)}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bridge Liquidity & Economic Metrics */}
                <div className="mt-6">
                  <h3 className="text-lg font-semibold text-white mb-3 flex items-center space-x-2">
                    <Zap size={20} />
                    <span>Bridge Liquidity & Economic Metrics</span>
                    <button
                      onClick={loadAnalyticsMetrics}
                      disabled={analyticsMetrics.isLoading}
                      className="ml-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-2 py-1 rounded text-xs transition-colors flex items-center space-x-1"
                    >
                      <RefreshCw className={`w-3 h-3 ${analyticsMetrics.isLoading ? 'animate-spin' : ''}`} />
                      <span>Refresh</span>
                    </button>
                  </h3>
                  
                  {/* Cross-chain analytics info */}
                  <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div className="flex items-center space-x-2 text-blue-300 text-sm">
                      <Activity size={16} />
                      <span>🌐 Cross-Chain Analytics: Data fetched from all networks regardless of your current connection</span>
                    </div>
                  </div>
                  
                  {analyticsMetrics.isLoading ? (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {[...Array(5)].map((_, i) => (
                        <div key={i} className="bg-white/5 rounded-lg p-3 text-center animate-pulse">
                          <div className="h-8 bg-white/20 rounded mb-2"></div>
                          <div className="h-4 bg-white/20 rounded"></div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* BSC Mainnet Block */}
                        <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-xl p-4 border border-blue-500/20">
                          <div className="flex items-center justify-center mb-4">
                            <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center mr-2">
                              <img src="/bsc.svg" alt="BSC" className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-semibold text-blue-300">BSC Mainnet</h3>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            {/* USDT Locked */}
                            <div className="bg-blue-500/20 rounded-lg p-3 text-center border border-blue-500/30">
                              <div className="text-xl font-bold text-blue-400">
                                {formatTokenAmount(analyticsMetrics.totalUSDTLocked || '0')} USDT
                              </div>
                              <div className="text-xs text-gray-300">USDT Total Locked</div>
                            </div>
                            
                            {/* USDT Reserved */}
                            <div className="bg-blue-500/20 rounded-lg p-3 text-center border border-blue-500/30">
                              <div className="text-xl font-bold text-blue-400">
                                {formatTokenAmount(analyticsMetrics.totalUSDTReserved || '0')} USDT
                              </div>
                              <div className="text-xs text-gray-300">USDT Reserved</div>
                            </div>
                            
                            {/* RWA Nova Locked */}
                            <div className="bg-blue-500/20 rounded-lg p-3 text-center border border-blue-500/30">
                              <div className="text-xl font-bold text-blue-400">
                                {formatTokenAmount(analyticsMetrics.rwaNovaBscLocked || '0')} RWA Nova
                              </div>
                              <div className="text-xs text-gray-300">RWA Nova Total Locked</div>
                            </div>
                            
                            {/* RWA Nova Reserved */}
                            <div className="bg-blue-500/20 rounded-lg p-3 text-center border border-blue-500/30">
                              <div className="text-xl font-bold text-blue-400">
                                {formatTokenAmount(analyticsMetrics.rwaNovaBscReserved || '0')} RWA Nova
                              </div>
                              <div className="text-xs text-gray-300">RWA Nova Reserved</div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Xphere Mainnet Block */}
                        <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-xl p-4 border border-green-500/20">
                          <div className="flex items-center justify-center mb-4">
                            <div className="w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center mr-2">
                              <img src="/xpt.svg" alt="Xphere" className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-semibold text-green-300">Xphere Mainnet</h3>
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3">
                            {/* USDX Locked */}
                            <div className="bg-green-500/20 rounded-lg p-3 text-center border border-green-500/30">
                              <div className="text-xl font-bold text-green-400">
                                {formatTokenAmount(analyticsMetrics.totalUSDXLocked || '0')} USDX
                              </div>
                              <div className="text-xs text-gray-300">USDX Total Locked</div>
                            </div>
                            
                            {/* USDX Reserved */}
                            <div className="bg-green-500/20 rounded-lg p-3 text-center border border-green-500/30">
                              <div className="text-xl font-bold text-green-400">
                                {formatTokenAmount(analyticsMetrics.totalUSDXReserved || '0')} USDX
                              </div>
                              <div className="text-xs text-gray-300">USDX Reserved</div>
                            </div>
                            
                            {/* RWA Nova Locked */}
                            <div className="bg-green-500/20 rounded-lg p-3 text-center border border-green-500/30">
                              <div className="text-xl font-bold text-green-400">
                                {formatTokenAmount(analyticsMetrics.rwaNovaXphereLocked || '0')} RWA Nova
                              </div>
                              <div className="text-xs text-gray-300">RWA Nova Total Locked</div>
                            </div>
                            
                            {/* RWA Nova Reserved */}
                            <div className="bg-green-500/20 rounded-lg p-3 text-center border border-green-500/30">
                              <div className="text-xl font-bold text-green-400">
                                {formatTokenAmount(analyticsMetrics.rwaNovaXphereReserved || '0')} RWA Nova
                              </div>
                              <div className="text-xs text-gray-300">RWA Nova Reserved</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Bridge Fees - Separate Tokens */}
                      <div className="mt-4 flex justify-center">
                        <div className="flex flex-wrap gap-4 justify-center">
                          {/* BSC USDT Bridge Fees */}
                          <div className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 rounded-lg p-4 text-center border border-yellow-500/30 min-w-48">
                            <div className="flex items-center justify-center mb-2">
                              <img src="/bsc.svg" alt="BSC" className="w-5 h-5 mr-2" />
                              <div className="text-sm text-gray-300">BSC Bridge Fees</div>
                            </div>
                            <div className="text-2xl font-bold text-yellow-400">
                              {formatFeeAmount(analyticsMetrics.bscBridgeFees || '0', parseInt(process.env.REACT_APP_USDT_DECIMALS || '6'))} USDT
                            </div>
                            <div className="text-xs text-yellow-300 mt-1">USDT Fees</div>
                          </div>
                          
                          {/* Xphere USDX Bridge Fees */}
                          <div className="bg-gradient-to-br from-red-500/20 to-red-600/20 rounded-lg p-4 text-center border border-red-500/30 min-w-48">
                            <div className="flex items-center justify-center mb-2">
                              <img src="/xpt.svg" alt="Xphere" className="w-5 h-5 mr-2" />
                              <div className="text-sm text-gray-300">Xphere Bridge Fees</div>
                            </div>
                            <div className="text-2xl font-bold text-red-400">
                              {formatFeeAmount(analyticsMetrics.xphereBridgeFees || '0', parseInt(process.env.REACT_APP_USDX_DECIMALS || '6'))} USDX
                            </div>
                            <div className="text-xs text-red-300 mt-1">USDX Fees</div>
                          </div>
                          
                          {/* BSC RWA Nova Bridge Fees */}
                          <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-lg p-4 text-center border border-blue-500/30 min-w-48">
                            <div className="flex items-center justify-center mb-2">
                              <img src="/bsc.svg" alt="BSC" className="w-5 h-5 mr-2" />
                              <div className="text-sm text-gray-300">BSC RWA Nova Fees</div>
                            </div>
                            <div className="text-2xl font-bold text-blue-400">
                              {formatFeeAmount(analyticsMetrics.rwaNovaBscFees || '0', parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18'))} RWA
                            </div>
                            <div className="text-xs text-blue-300 mt-1">RWA Nova Fees</div>
                          </div>
                          
                          {/* Xphere RWA Nova Bridge Fees */}
                          <div className="bg-gradient-to-br from-indigo-500/20 to-indigo-600/20 rounded-lg p-4 text-center border border-indigo-500/30 min-w-48">
                            <div className="flex items-center justify-center mb-2">
                              <img src="/xpt.svg" alt="Xphere" className="w-5 h-5 mr-2" />
                              <div className="text-sm text-gray-300">Xphere RWA Nova Fees</div>
                            </div>
                            <div className="text-2xl font-bold text-indigo-400">
                              {formatFeeAmount(analyticsMetrics.rwaNovaXphereFees || '0', parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18'))} RWA
                            </div>
                            <div className="text-xs text-indigo-300 mt-1">RWA Nova Fees</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  

                </div>
                
                {/* Analytics Filters */}
                <div className="mt-4 flex flex-wrap gap-3">
                  <select 
                    value={analyticsFilter.status || ''}
                    onChange={(e) => setAnalyticsFilter(prev => ({ ...prev, status: e.target.value }))}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                    <option value="skipped">Skipped</option>
                  </select>
                  
                  <select 
                    value={analyticsFilter.network || ''}
                    onChange={(e) => setAnalyticsFilter(prev => ({ ...prev, network: e.target.value }))}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">All Networks</option>
                    <option value="bscMainnet">BSC Mainnet</option>
                    <option value="xphereMainnet">Xphere Mainnet</option>
                  </select>
                  
                  <input
                    type="text"
                    placeholder="Search by user address..."
                    value={analyticsFilter.userAddress || ''}
                    onChange={(e) => setAnalyticsFilter(prev => ({ ...prev, userAddress: e.target.value }))}
                    className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 placeholder-gray-400"
                  />
                  
                  <button
                    onClick={() => setAnalyticsFilter({})}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm transition-colors"
                  >
                    Clear Filters
                  </button>
                  
                  <div>
                    <button
                      onClick={() => loadAllBridgeHistory(analyticsFilter || {})}
                      disabled={isLoadingHistory}
                      className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm transition-colors flex items-center space-x-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoadingHistory ? 'animate-spin' : ''}`} />
                      <span>Refresh</span>
                    </button>
                  </div>
                  
                  {/* Smart Refresh Toggle */}
                  <div className="flex items-center space-x-2 bg-white/5 rounded-lg px-3 py-2 border border-white/20">
                    <label className="text-sm text-gray-300 flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={smartRefreshEnabled}
                        onChange={(e) => {
                          const enabled = e.target.checked;
                          setSmartRefreshEnabled(enabled);
                          localStorage.setItem('smartRefreshEnabled', JSON.stringify(enabled));
                          console.log(`🔄 Smart refresh ${enabled ? 'enabled' : 'disabled'}`);
                        }}
                        className="w-4 h-4 text-purple-600 bg-white/10 border-white/20 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span>Smart Refresh</span>
                    </label>
                    <div className="text-xs text-gray-400">
                      (Auto-refresh when new data detected)
                    </div>
                  </div>
                  
                  {/* Smart Refresh Status */}
                  {smartRefreshEnabled && (
                    <div className="flex items-center space-x-2">
                      <div className="text-xs text-green-400 bg-green-500/10 rounded-lg px-2 py-1 border border-green-500/20">
                        🔄 Smart refresh active
                      </div>
                      <button
                        onClick={async () => {
                          console.log('🔍 Manual smart refresh check triggered');
                          const lastRefreshTime = localStorage.getItem('lastAnalyticsRefresh') || 0;
                          const hasNewData = await checkForNewAnalyticsData(lastRefreshTime);
                          if (hasNewData) {
                            console.log('🔄 Manual check: New data detected, refreshing...');
                            await loadAllBridgeHistory(analyticsFilter || {});
                            localStorage.setItem('lastAnalyticsRefresh', Date.now());
                          } else {
                            console.log('✅ Manual check: No new data found');
                          }
                        }}
                        className="text-xs text-blue-400 bg-blue-500/10 rounded-lg px-2 py-1 border border-blue-500/20 hover:bg-blue-500/20 transition-colors"
                        title="Manually check for new data"
                      >
                        Check Now
                      </button>
                    </div>
                  )}
                  
                  {/* Last Updated Timestamp */}
                  {lastRefresh && (
                    <div className="mt-3 text-xs text-gray-400">
                      Last updated: {lastRefresh.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              {isLoadingHistory ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <RefreshCw className="text-gray-400 animate-spin" size={32} />
                  </div>
                  <p className="text-gray-400 text-lg">Loading bridge analytics...</p>
                </div>
              ) : allBridgeHistory.length === 0 && bridgeRequests.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Activity className="text-gray-400" size={32} />
                  </div>
                  <p className="text-gray-400 text-lg">No bridge transactions found</p>
                  <p className="text-gray-500 text-sm mt-1">
                    {Object.keys(analyticsFilter).length > 0 
                      ? 'Try adjusting your filters or refreshing the data'
                      : 'Bridge transactions from all users will appear here'
                    }
                  </p>
                </div>
              ) : (
                <>
                <div className="divide-y divide-white/10">
                                      {/* Show local pending requests first */}
                  {bridgeRequests.map((request, index) => {
                    // Skip if this request is already in allBridgeHistory (completed)
                    if (allBridgeHistory.some(apiRequest => apiRequest.requestId === request.id)) {
                      return null;
                    }
                    
                    const statusInfo = bridgeService.formatBridgeStatus(request.status);
                    const timeSince = bridgeService.getTimeSince(request.timestamp);
                    
                    return (
                      <div key={`local-${request.id}-${index}`} className="p-6 hover:bg-white/5 transition-colors bg-blue-500/10 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center space-x-2">
                              <div className={`w-8 h-8 ${supportedChains[request.sourceNetwork]?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                                <img 
                                  src={request.sourceNetwork === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                                  alt={request.sourceNetwork || 'Unknown'} 
                                  className="w-6 h-6" 
                                />
                              </div>
                              <ArrowRight className="text-gray-400" size={16} />
                              <div className={`w-8 h-8 ${supportedChains[request.destNetwork]?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                                <img 
                                  src={request.destNetwork === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                                  alt={request.destNetwork || 'Unknown'} 
                                  className="w-6 h-6" 
                                />
                              </div>
                            </div>
                            
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="text-white font-semibold">{formatBridgeAmountWithReceive(request)}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${statusInfo.color}`}>
                                  <span>{statusInfo.icon}</span>
                                  <span>{statusInfo.label}</span>
                                </span>
                                <span className="text-blue-400 text-xs">(Local)</span>
                              </div>
                              <p className="text-gray-400 text-sm">{timeSince}</p>
                              <p className="text-gray-500 text-xs">To: {bridgeService.formatAddress(request.userAddress)}</p>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-gray-400 text-sm">Request ID: {request.id}</p>
                            {request.txHash && (
                              <button 
                                onClick={() => {
                                  const chainId = getChainIdFromNetwork(request.sourceNetwork);
                                  openInExplorer(request.txHash, chainId);
                                }}
                                className="text-blue-400 hover:text-blue-300 text-xs mt-1 transition-colors flex items-center space-x-1"
                              >
                                <span>View Transaction</span>
                                <ExternalLink size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Show all users' bridge analytics data */}
                  {allBridgeHistory.map((request, index) => {
                    try {
                      const statusInfo = bridgeService.formatBridgeStatus(request.status);
                      
                      // Get token decimals from request or determine from token address
                      let tokenDecimals = request.tokenDecimals;
                      
                      // If tokenDecimals is not available, determine from token address
                      if (!tokenDecimals && request.tokenAddress) {
                        // Check against known token addresses
                        const knownTokens = {
                          // Dynamic token addresses from environment
                          [process.env.REACT_APP_USDT_ADDRESS?.toLowerCase()]: process.env.REACT_APP_USDT_DECIMALS, // USDT has 6 decimals
                          [process.env.REACT_APP_USDX_ADDRESS?.toLowerCase()]: process.env.REACT_APP_USDX_DECIMALS, // USDX
                          [(process.env.REACT_APP_RWA_NOVA_BSC_ADDRESS || '0x49A756e34A5A0a7FDBFCce73d10228ee2C85CA8b').toLowerCase()]: process.env.REACT_APP_RWA_NOVA_DECIMALS || '18', // RWA Nova BSC
                          [(process.env.REACT_APP_RWA_NOVA_XPHERE_ADDRESS || '0x0DFfFad57Bc67701244B1bdA37E830eeadb0d69F').toLowerCase()]: process.env.REACT_APP_RWA_NOVA_DECIMALS || '18', // RWA Nova XPhere
                        };
                        
                        tokenDecimals = knownTokens[request.tokenAddress.toLowerCase()] || 18; // Default to 18 for unknown tokens
                      }
                      
                      // Fallback to environment variable if still not available
                      tokenDecimals = tokenDecimals || parseInt(process.env.REACT_APP_USDT_DECIMALS || '6');
                      
                      // Debug: Log the request data to see what we're working with (only for debugging)
                      // if (process.env.NODE_ENV === 'development') {
                      //   console.log('🔍 Bridge history request data:', {
                      //     id: request.requestId || request.id,
                      //     amount: request.amount,
                      //     tokenDecimals: tokenDecimals,
                      //     tokenAddress: request.tokenAddress,
                      //     sourceNetwork: request.sourceNetwork,
                      //     destNetwork: request.destNetwork
                      //   });
                      // }
                      
                      const timeSince = bridgeService.getTimeSince(request.createdAt);
                      const processingTime = request.processedAt ? 
                        bridgeService.getProcessingTime(request.createdAt, request.processedAt) : null;
                      
                      return (
                        <div key={`api-${request.requestId}-${request.txHash}-${index}`} className="p-6 hover:bg-white/5 transition-colors">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                              <div className="flex items-center space-x-2">
                                <div className={`w-8 h-8 ${supportedChains[request.sourceNetwork]?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                                  <img 
                                    src={request.sourceNetwork === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                                    alt={request.sourceNetwork || 'Unknown'} 
                                    className="w-6 h-6" 
                                  />
                                </div>
                                <ArrowRight className="text-gray-400" size={16} />
                                <div className={`w-8 h-8 ${supportedChains[request.destNetwork]?.color || 'bg-gray-500'} rounded-lg flex items-center justify-center`}>
                                  <img 
                                    src={request.destNetwork === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                                    alt={request.destNetwork || 'Unknown'} 
                                    className="w-6 h-6" 
                                  />
                                </div>
                              </div>
                              
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="text-white font-semibold">{formatBridgeAmountWithReceive(request)}</span>
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${statusInfo.color}`}>
                                    <span>{statusInfo.icon}</span>
                                    <span>{statusInfo.label}</span>
                                  </span>
                                </div>
                                <p className="text-gray-400 text-sm">{timeSince}</p>
                                <p className="text-gray-500 text-xs">User: {bridgeService.formatAddress(request.userAddress || request.recipientAddress || 'Unknown')}</p>
                                <p className="text-gray-500 text-xs">Network: {request.sourceNetwork || 'Unknown'} → {request.destNetwork || 'Unknown'}</p>
                                {processingTime && request.status === 'completed' && (
                                  <p className="text-green-400 text-xs">Processing time: {processingTime}</p>
                                )}
                              </div>
                            </div>
                            
                            <div className="text-right">
                              <p className="text-gray-400 text-sm">Request ID: {request.requestId}</p>
                              {request.txHash && (
                                <button 
                                  onClick={() => {
                                    const chainId = getChainIdFromNetwork(request.sourceNetwork);
                                    openInExplorer(request.txHash, chainId);
                                  }}
                                  className="text-blue-400 hover:text-blue-300 text-xs mt-1 transition-colors flex items-center space-x-1"
                                >
                                  <span>View Transaction</span>
                                  <ExternalLink size={12} />
                                </button>
                              )}
                              {request.gasUsed && (
                                <p className="text-gray-500 text-xs">Gas: {request.gasUsed}</p>
                              )}
                              {request.proofHash && (
                                <button 
                                  onClick={() => {
                                    // For proof hash, use destination network (where the proof was submitted)
                                    const chainId = getChainIdFromNetwork(request.destNetwork || 'xphereMainnet');
                                    openInExplorer(request.proofHash, chainId);
                                  }}
                                  className="text-green-500 hover:text-green-400 text-xs transition-colors flex items-center space-x-1"
                                >
                                  <span>Proof: {request.proofHash.substring(0, 8)}...</span>
                                  <ExternalLink size={12} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    } catch (error) {
                      console.error('Error rendering bridge request:', error, 'request:', request);
                      return (
                        <div key={`error-${index}`} className="p-6 bg-red-500/10 border border-red-500/20 rounded-lg">
                          <p className="text-red-400 text-sm">Error rendering request {request.requestId}: {error.message}</p>
                          <pre className="text-xs text-red-300 mt-2 overflow-auto">{JSON.stringify(request, null, 2)}</pre>
                        </div>
                      );
                    }
                  })}
                </div>
                
                {/* Pagination Controls */}
                {pagination.totalCount > 0 && (
                  <div className="p-4 border-t border-white/10 bg-white/5 flex flex-wrap items-center justify-between gap-4">
                    <div className="text-gray-400 text-sm">
                      Page {pagination.currentPage} of {Math.ceil(pagination.totalCount / pagination.pageSize)} ({pagination.totalCount} total)
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => goToPage(pagination.currentPage - 1)}
                        disabled={pagination.currentPage <= 1}
                        className="px-3 py-1 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded transition-colors"
                      >
                        Previous
                      </button>
                      
                      {/* Page Numbers */}
                      <div className="flex items-center space-x-1">
                        {(() => {
                          const totalPages = Math.ceil(pagination.totalCount / pagination.pageSize);
                          const currentPage = pagination.currentPage;
                          const pages = [];
                          
                          // Show first page
                          if (totalPages > 0) {
                            pages.push(
                              <button
                                key={1}
                                onClick={() => goToPage(1)}
                                className={`px-2 py-1 text-sm rounded transition-colors ${
                                  currentPage === 1 
                                    ? 'bg-purple-600 text-white' 
                                    : 'bg-white/10 hover:bg-white/20 text-white'
                                }`}
                              >
                                1
                              </button>
                            );
                          }
                          
                          // Show ellipsis if current page is far from start
                          if (currentPage > 3) {
                            pages.push(<span key="start-ellipsis" className="text-gray-400 px-1">...</span>);
                          }
                          
                          // Show pages around current page
                          for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                            if (i !== 1 && i !== totalPages) {
                              pages.push(
                                <button
                                  key={i}
                                  onClick={() => goToPage(i)}
                                  className={`px-2 py-1 text-sm rounded transition-colors ${
                                    currentPage === i 
                                      ? 'bg-purple-600 text-white' 
                                      : 'bg-white/10 hover:bg-white/20 text-white'
                                  }`}
                                >
                                  {i}
                                </button>
                              );
                            }
                          }
                          
                          // Show ellipsis if current page is far from end
                          if (currentPage < totalPages - 2) {
                            pages.push(<span key="end-ellipsis" className="text-gray-400 px-1">...</span>);
                          }
                          
                          // Show last page (if more than 1 page)
                          if (totalPages > 1) {
                            pages.push(
                              <button
                                key={totalPages}
                                onClick={() => goToPage(totalPages)}
                                className={`px-2 py-1 text-sm rounded transition-colors ${
                                  currentPage === totalPages 
                                    ? 'bg-purple-600 text-white' 
                                    : 'bg-white/10 hover:bg-white/20 text-white'
                                }`}
                              >
                                {totalPages}
                              </button>
                            );
                          }
                          
                          return pages;
                        })()}
                      </div>
                      
                      <button
                        onClick={() => goToPage(pagination.currentPage + 1)}
                        disabled={pagination.currentPage >= Math.ceil(pagination.totalCount / pagination.pageSize)}
                        className="px-3 py-1 bg-white/10 hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm rounded transition-colors"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
                </>
              )}
            </div>

            {/* Recent Bridge Requests (Local State) */}
            {bridgeRequests.length > 0 && (
              <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
                <div className="p-6 border-b border-white/10">
                  <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                    <Clock size={24} />
                    <span>Recent Bridge Requests</span>
                  </h2>
                  <p className="text-gray-400 mt-1">Your recent bridge transactions from this session</p>
                </div>

                <div className="divide-y divide-white/10">
                  {bridgeRequests.map((request) => {
                    const statusInfo = bridgeService.formatBridgeStatus(request.status);
                    const bridgeStatus = bridgeStatuses[request.id];
                    
                    return (
                  <div key={`${request.id}-${request.txHash}`} className="p-6 hover:bg-white/5 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <div className={`w-8 h-8 ${supportedChains[request.from]?.color} rounded-lg flex items-center justify-center text-white font-semibold text-xs`}>
                            {supportedChains[request.from]?.name.slice(0, 3).toUpperCase()}
                          </div>
                          <ArrowRight className="text-gray-400" size={16} />
                          <div className={`w-8 h-8 ${supportedChains[request.to]?.color} rounded-lg flex items-center justify-center text-white font-semibold text-xs`}>
                            {supportedChains[request.to]?.name.slice(0, 3).toUpperCase()}
                          </div>
                        </div>
                        
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="text-white font-semibold">{formatBridgeAmountWithReceive(request)}</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${statusInfo.color}`}>
                                  <span>{statusInfo.icon}</span>
                                  <span>{statusInfo.label}</span>
                            </span>
                                {bridgeStatus && bridgeStatus.status === 'processing' && (
                                  <span className="text-yellow-400 text-xs">🔄 Processing...</span>
                                )}
                          </div>
                          <p className="text-gray-400 text-sm">
                            {request.timestamp ? new Date(request.timestamp).toLocaleString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              hour12: true,
                              timeZoneName: 'short'
                            }) : 'Unknown time'}
                          </p>
                          <p className="text-gray-500 text-xs">To: {formatAddress(request.recipient)}</p>
                              {bridgeStatus && (
                                <div className="mt-1 text-xs">
                                  {bridgeStatus.status === 'completed' && (
                                    <p className="text-green-400">✅ Completed at {new Date(bridgeStatus.processedAt).toLocaleString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                      hour12: true,
                                      timeZoneName: 'short'
                                    })}</p>
                                  )}
                                  {bridgeStatus.status === 'failed' && (
                                    <p className="text-red-400">❌ Failed: {bridgeStatus.errorMessage}</p>
                                  )}
                                  {bridgeStatus.gasUsed && (
                                    <p className="text-gray-400">Gas used: {bridgeStatus.gasUsed}</p>
                                  )}
                                </div>
                              )}
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <p className="text-gray-400 text-sm">Request ID: {request.id}</p>
                        {request.txHash && (
                          <button 
                                onClick={() => {
                                  const chainId = getChainIdFromNetwork(request.sourceChainId);
                                  openInExplorer(request.txHash, chainId);
                                }}
                            className="text-blue-400 hover:text-blue-300 text-xs mt-1 transition-colors flex items-center space-x-1"
                          >
                            <span>View Transaction</span>
                            <ExternalLink size={12} />
                          </button>
                        )}
                        {request.blockNumber && (
                          <p className="text-gray-500 text-xs">Block: {request.blockNumber}</p>
                        )}
                            {bridgeStatus && bridgeStatus.proofHash && (
                              <button 
                                onClick={() => {
                                  // For proof hash, use destination network (where the proof was submitted)
                                  const chainId = getChainIdFromNetwork('xphereMainnet');
                                  openInExplorer(bridgeStatus.proofHash, chainId);
                                }}
                                className="text-green-500 hover:text-green-400 text-xs transition-colors flex items-center space-x-1"
                              >
                                <span>Proof: {bridgeStatus.proofHash.substring(0, 8)}...</span>
                                <ExternalLink size={12} />
                              </button>
                            )}
                      </div>
                    </div>
                  </div>
                    );
                  })}
            </div>
              </div>
            )}
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="max-w-4xl mx-auto space-y-6">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-2">
                <Settings size={24} />
                <span>Bridge Settings</span>
              </h2>

              <div className="space-y-6">
                {!chainId && (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-4">
                    <p className="text-blue-400 text-sm">
                      💡 Connect your wallet to see which chains you're connected to and switch between them.
                    </p>
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Supported Chains</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {Object.entries(supportedChains).map(([key, chain]) => (
                      <div key={key} className="bg-white/10 rounded-lg p-4 border border-white/10">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 ${chain.color} rounded-lg flex items-center justify-center`}>
                              <img 
                                src={key === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                                alt={chain.name} 
                                className="w-8 h-8" 
                              />
                            </div>
                            <div>
                              <p className="text-white font-medium">{chain.name}</p>
                              <p className="text-gray-400 text-sm">Chain ID: {chain.id}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`px-2 py-1 rounded text-xs ${chainId && BigInt(chainId) === BigInt(chain.id) ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                              {chainId && BigInt(chainId) === BigInt(chain.id) ? 'Connected' : 'Available'}
                            </div>
                            {chainId && BigInt(chainId) !== BigInt(chain.id) && (
                              <button
                                onClick={() => switchToChain(key)}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs transition-colors"
                              >
                                Switch
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                  <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Token Contracts</h3>
                  <div className="space-y-6">
                    {Object.entries(supportedChains).map(([chainKey, chain]) => {
                      const contracts = CONTRACT_ADDRESSES[chain.id];
                      const chainTokens = [];
                      
                      // Add chain-specific tokens
                      if (chainKey === 'bscMainnet') {
                        if (contracts?.usdt) {
                          chainTokens.push({
                            name: process.env.REACT_APP_USDT_NAME || 'Tether USD',
                            symbol: 'usdt',
                            address: contracts.usdt,
                            type: 'stablecoin',
                            decimals: parseInt(process.env.REACT_APP_USDT_DECIMALS || '6')
                          });
                        }
                      } else if (chainKey === 'xphereMainnet') {
                        if (contracts?.usdx) {
                          chainTokens.push({
                            name: process.env.REACT_APP_USDX_NAME || 'USDX',
                            symbol: 'usdx',
                            address: contracts.usdx,
                            type: 'stablecoin',
                            decimals: parseInt(process.env.REACT_APP_USDX_DECIMALS || '6')
                          });
                        }
                      }
                      
                      // Add RWA Nova (available on both chains)
                      if (contracts?.rwaNova) {
                        chainTokens.push({
                          name: process.env.REACT_APP_RWA_NOVA_NAME || 'RWA Nova',
                          symbol: 'rwaNova',
                          address: contracts.rwaNova,
                          type: 'token',
                          decimals: parseInt(process.env.REACT_APP_RWA_NOVA_DECIMALS || '18')
                        });
                      }

                        return (
                        <div key={chainKey} className="bg-white/10 rounded-lg p-4 border border-white/10">
                          <div className="flex items-center space-x-3 mb-4">
                            <div className={`w-8 h-8 ${chain.color} rounded-lg flex items-center justify-center`}>
                              <img 
                                src={chainKey === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                                alt={chain.name} 
                                className="w-6 h-6" 
                              />
                            </div>
                            <h4 className="text-white font-medium">{chain.name}</h4>
                            {chainId && BigInt(chainId) === BigInt(chain.id) && (
                              <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs">Connected</span>
                            )}
                          </div>
                          <div className="space-y-3">
                            {chainTokens.map((tokenItem) => (
                              <div key={tokenItem.symbol} className="bg-white/5 rounded-lg p-3 border border-white/5">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-white font-medium">{tokenItem.name}</p>
                                <p className="text-gray-400 text-sm">{tokenItem.symbol}</p>
                                    <p className="text-gray-500 text-xs font-mono">{tokenItem.address}</p>
                                <p className="text-gray-500 text-xs">{tokenItem.type} • {tokenItem.decimals} decimals</p>
                              </div>
                              <div className="text-right">
                                    {chainId && BigInt(chainId) === BigInt(chain.id) ? (
                                      <>
                                <p className="text-white font-mono">
                                  {tokenBalances[tokenItem.symbol] ? formatNumberWithCommas(parseFloat(tokenBalances[tokenItem.symbol]).toFixed(6)) : '0.000000'}
                                </p>
                                <p className="text-gray-400 text-sm">Balance</p>
                                <button
                                  onClick={() => addTokenToMetaMask(tokenItem.symbol)}
                                  className="text-blue-400 hover:text-blue-300 text-xs mt-1 transition-colors"
                                >
                                  Add to MetaMask
                                </button>
                                      </>
                                    ) : (
                                      <p className="text-gray-400 text-sm">Switch to see balance</p>
                                    )}
                              </div>
                                </div>
                              </div>
                            ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Bridge Configuration</h3>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/10 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-white">Bridge Fee</span>
                      <span className="text-gray-400">{bridgeConfig.fee}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white">Min Bridge Amount</span>
                      <span className="text-gray-400">{formatNumberWithCommas(parseFloat(bridgeConfig.minAmount).toFixed(3))} tokens</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white">Max Bridge Amount</span>
                      <span className="text-gray-400">{formatNumberWithCommas(parseFloat(bridgeConfig.maxAmount).toFixed(0))} tokens</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white">Processing Time</span>
                      <span className="text-gray-400">{bridgeConfig.processingTime}</span>
                    </div>
                  </div>
                </div>

                  <div>
                  <h3 className="text-lg font-semibold text-white mb-3">Contract Addresses</h3>
                  <div className="space-y-6">
                    {Object.entries(supportedChains).map(([chainKey, chain]) => {
                      const contractAddresses = chainKey === 'bscMainnet' ? {
                        source: CONTRACT_ADDRESSES[56].sourceBridge,
                        destination: CONTRACT_ADDRESSES[56].destBridge,
                        token: CONTRACT_ADDRESSES[56].usdt
                      } : {
                        source: CONTRACT_ADDRESSES[20250217].sourceBridge,
                        destination: CONTRACT_ADDRESSES[20250217].destBridge,
                        token: CONTRACT_ADDRESSES[20250217].usdx
                      };

                      return (
                        <div key={chainKey} className="bg-white/10 rounded-lg p-4 border border-white/10">
                          <div className="flex items-center space-x-3 mb-4">
                            <div className={`w-8 h-8 ${chain.color} rounded-lg flex items-center justify-center`}>
                              <img 
                                src={chainKey === 'bscMainnet' ? '/bsc.svg' : '/xpt.svg'} 
                                alt={chain.name} 
                                className="w-6 h-6" 
                              />
                            </div>
                            <h4 className="text-white font-medium">{chain.name}</h4>
                            {chainId && BigInt(chainId) === BigInt(chain.id) && (
                              <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded text-xs">Connected</span>
                            )}
                          </div>
                          <div className="space-y-3">
                      <div>
                        <span className="text-gray-300 text-sm">Source Bridge:</span>
                        <p className="text-white font-mono break-all text-xs mt-1">
                                {contractAddresses.source}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-300 text-sm">Destination Bridge:</span>
                        <p className="text-white font-mono break-all text-xs mt-1">
                                {contractAddresses.destination}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-300 text-sm">Available Tokens:</span>
                        <div className="mt-1 space-y-1">
                          {chainKey === 'bscMainnet' && contractAddresses.usdt && (
                            <div className="text-white text-xs">
                              • {process.env.REACT_APP_USDT_NAME || 'Tether USD'} (usdt) - {contractAddresses.usdt}
                            </div>
                          )}
                          {chainKey === 'xphereMainnet' && contractAddresses.usdx && (
                            <div className="text-white text-xs">
                              • {process.env.REACT_APP_USDX_NAME || 'USDX'} (usdx) - {contractAddresses.usdx}
                            </div>
                          )}
                          {contractAddresses.rwaNova && (
                            <div className="text-white text-xs">
                              • {process.env.REACT_APP_RWA_NOVA_NAME || 'RWA Nova'} (rwaNova) - {contractAddresses.rwaNova}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-white mb-3">How It Works</h3>
                  <div className="bg-white/10 rounded-lg p-4 border border-white/10 space-y-3 text-sm text-gray-300">
                    <div className="flex items-start space-x-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">1</span>
                      <div>
                        <p className="font-medium text-white">Lock Tokens</p>
                        <p>Your tokens are locked in the source chain smart contract</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">2</span>
                      <div>
                        <p className="font-medium text-white">Generate ZK Proof</p>
                        <p>Relayer generates a zero-knowledge proof of the transaction</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold">3</span>
                      <div>
                        <p className="font-medium text-white">Verify & Release</p>
                        <p>Destination contract verifies the proof and releases tokens</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
              <h3 className="text-lg font-semibold text-white mb-4">🎯 XP Bridge Features & Security</h3>
              <div className="space-y-4 text-gray-300">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <p className="font-medium text-white">Real Zero-Knowledge Proofs</p>
                    <p className="text-sm">Transaction privacy through zk-SNARKs - your data stays private</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <p className="font-medium text-white">Multi-Chain Architecture</p>
                    <p className="text-sm">Bridge between available chains such as BSC and Xphere</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <p className="font-medium text-white">Nullifier Protection</p>
                    <p className="text-sm">Prevents double-spending with unique transaction identifiers</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <p className="font-medium text-white">Automated Processing</p>
                    <p className="text-sm">Working template method generates proofs in ~1 second</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <p className="font-medium text-white">Automated Relayer Processing</p>
                    <p className="text-sm">High-performance backend automation for seamless bridging</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-400 mt-1 flex-shrink-0" size={16} />
                  <div>
                    <p className="font-medium text-white">Production Ready</p>
                    <p className="text-sm">Enterprise-grade reliability and performance for production use</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Admin Tab */}
        {activeTab === 'admin' && (
          <AdminPanel 
            provider={provider}
            signer={signer}
            account={wagmiAccount}
            chainId={chainId}
            onNetworkSwitch={() => setIsNetworkSwitching(true)}
            onNetworkSwitchComplete={() => setIsNetworkSwitching(false)}
          />
        )}
      </div>

      {/* Confirmation Dialog - Full Screen Overlay */}
      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl border border-white/20 max-w-2xl w-full max-h-[90vh] flex flex-col">
            {/* Dialog Header - Fixed */}
            <div className="p-4 border-b border-white/10 flex-shrink-0">
              <h2 className="text-xl font-bold text-white flex items-center space-x-2">
                <AlertCircle className="text-yellow-400" size={24} />
                <span>Confirm Bridge Transaction</span>
              </h2>
              <p className="text-gray-400 text-xs mt-1">Please review the details carefully before proceeding</p>
            </div>

            {/* Dialog Content - Scrollable */}
            <div className="overflow-y-auto flex-1 p-4 space-y-4">
              {/* Transaction Details */}
              <div className="bg-white/5 rounded-xl p-3 space-y-2">
                <h3 className="text-base font-semibold text-white mb-2">Transaction Details</h3>
                
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">From Network:</span>
                  <span className="text-white font-semibold">
                    {sourceChain === 'bscMainnet' ? '🟡 BSC Mainnet' : '🟣 Xphere Mainnet'}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">To Network:</span>
                  <span className="text-white font-semibold">
                    {destChain === 'bscMainnet' ? '🟡 BSC Mainnet' : '🟣 Xphere Mainnet'}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Bridge Amount:</span>
                  <span className="text-white font-semibold">
                    {formatNumberWithCommas(parseFloat(amount).toFixed(6))} {token.toUpperCase()}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Bridge Fee ({bridgeConfig?.fee || '0.5%'}):</span>
                  <span className="text-orange-400 font-semibold">
                    {(() => {
                      const bridgeAmount = parseFloat(amount);
                      const feePercent = bridgeConfig?.fee ? parseFloat(bridgeConfig.fee.replace('%', '')) : 0.5;
                      const feeAmount = bridgeAmount * (feePercent / 100);
                      return formatNumberWithCommas(feeAmount.toFixed(6));
                    })()} {token.toUpperCase()}
                  </span>
                </div>

                <div className="border-t border-white/10 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 font-semibold text-sm">You Will Receive:</span>
                    <span className="text-green-400 font-bold text-base">
                      {(() => {
                        const bridgeAmount = parseFloat(amount);
                        const feePercent = bridgeConfig?.fee ? parseFloat(bridgeConfig.fee.replace('%', '')) : 0.5;
                        const receiveAmount = bridgeAmount * (1 - feePercent / 100);
                        const destToken = token.toUpperCase();
                        return `${formatNumberWithCommas(receiveAmount.toFixed(6))} ${destToken}`;
                      })()}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-400">Recipient Address:</span>
                  <span className="text-white font-mono text-xs">
                    {recipient.slice(0, 6)}...{recipient.slice(-4)}
                  </span>
                </div>
              </div>

              {/* Important Warnings */}
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                <h3 className="text-base font-semibold text-yellow-400 flex items-center space-x-2 mb-2">
                  <AlertCircle size={18} />
                  <span>Important Information</span>
                </h3>
                
                <ul className="space-y-1.5 text-xs text-gray-300">
                  <li className="flex items-start space-x-2">
                    <span className="text-yellow-400 mt-0.5">⚠️</span>
                    <span>Bridge transactions are <strong className="text-white">irreversible</strong>. Verify all details carefully.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-yellow-400 mt-0.5">⚠️</span>
                    <span>Recipient address must be correct. Wrong address = <strong className="text-white">permanent loss</strong>.</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-yellow-400 mt-0.5">⚠️</span>
                    <span>Processing takes <strong className="text-white">2-5 minutes</strong>. Don't refresh during processing.</span>
                  </li>
                </ul>
              </div>

              {/* Confirmation Checkbox */}
              <div className="bg-white/5 rounded-xl p-3">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    id="confirmCheckbox"
                    checked={confirmCheckboxChecked}
                    onChange={(e) => setConfirmCheckboxChecked(e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-gray-500 text-purple-600 focus:ring-purple-500 focus:ring-offset-0 cursor-pointer"
                  />
                  <span className="text-gray-300 text-xs">
                    I have reviewed all transaction details and understand that this transaction is irreversible. I confirm the recipient address is correct.
                  </span>
                </label>
              </div>
            </div>

            {/* Dialog Actions - Fixed */}
            <div className="p-4 border-t border-white/10 flex space-x-3 flex-shrink-0">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmCheckboxChecked(false); // Reset checkbox when cancelled
                }}
                className="flex-1 py-2.5 rounded-xl font-semibold bg-gray-600 hover:bg-gray-700 text-white transition-colors text-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setConfirmCheckboxChecked(false); // Reset checkbox for next time
                  initiateBridge();
                }}
                disabled={!confirmCheckboxChecked}
                className={`flex-1 py-2.5 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 text-sm ${
                  confirmCheckboxChecked
                    ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white cursor-pointer'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed opacity-50'
                }`}
              >
                <span>Confirm & Proceed</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-black/20 backdrop-blur-lg border-t border-white/10 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-6 h-6 rounded-lg flex items-center justify-center">
                  <img src="/logo.png" alt="XP Bridge Logo" className="w-6 h-6 rounded-lg" />
                </div>
                <span className="text-white font-semibold">XP Bridge</span>
              </div>
              <span className="text-gray-400 text-sm">Decentralized cross chain bridge utilizing ZK cryptography</span>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <span className="flex items-center space-x-1">
                <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                <span>Live XP Bridge</span>
              </span>
              
              {/* Help Button */}
              <div className="relative group">
                <button className="flex items-center space-x-1 text-gray-400 hover:text-white transition-colors">
                  <HelpCircle size={20} />
                  <span className="text-sm font-medium">Help</span>
                </button>
                <div className="absolute right-0 bottom-full mb-2 w-80 bg-gray-900 border border-white/20 rounded-lg shadow-xl p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <p className="text-sm text-gray-300 leading-relaxed">
                    If you have any questions or comments please send us a detailed email to{' '}
                    <a href="mailto:info@xpbridge.app" className="text-blue-400 hover:text-blue-300 underline">
                      info@xpbridge.app
                    </a>
                    {' '}and a team member will reply as soon as possible.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ZKBridgeApp;